// DLC_01_POWER_PROJECT — frontend types (mirror backend serializers).
// v2 access/correspondence model: per-owner access titles (OwnerAccess) + universal
// proceedings (Proceeding/ProceedingItem) + per-project permissions + DLC_02 register.

export type DeadlineStatus = "none" | "overdue" | "soon" | "approaching" | "ok";

export type ParcelStatus =
  | "not_started" | "in_progress" | "action_required" | "application_sent"
  | "negotiator_sent" | "ok" | "problem";

// models.ParcelAccessMode — the single three-way "what does this parcel need?" control.
export type ParcelAccessMode = "consent" | "notification" | "not_required";

export type MilestoneState =
  | "not_started" | "in_progress" | "submitted" | "done" | "applicable" | "na";

// models.TopicStatus — dyrektorskie oznaczenie tematu w portfelu (REALIZACJA / PÓŁKA /
// KOŃCZENIE / ZAMKNIĘTY). Edytuje wyłącznie CEO / dyrektor techniczny; reszta view-only.
export type TopicStatus = "in_progress" | "shelved" | "finishing" | "closed";

// ── access titles (OwnerAccess) ──────────────────────────────────────────────────
// Mirrors models.OwnerAccessStatus.
export type OwnerAccessStatus =
  | "not_started" | "in_progress" | "secured" | "refused" | "not_required" | "problem";

// ── proceedings (universal correspondence thread) ────────────────────────────────
export type ProceedingScope = "owner" | "office" | "technical" | "notification" | "letter"; // models.ProceedingScope
export type ProceedingStatus = "open" | "closed"; // models.ProceedingStatus
export type ProceedingOutcome = "none" | "positive" | "negative" | "partial"; // models.ProceedingOutcome
export type ProceedingItemKind = "application" | "response" | "event"; // models.ProceedingItemKind
// "withdrawn" = pismo pakietu wycofane, zanim wyszło (wniosek anulowany) — ślad w wątku sprawy.
// "completed" = lustro zakończonego pakietu z modułu Wnioski (odpowiedź urzędu wpłynęła).
export type ApplicationStatus = "" | "draft" | "approved" | "submitted" | "completed" | "withdrawn"; // models.ApplicationStatus (blank for non-APPLICATION)
export type ItemChannel = "" | "paper" | "edoreczenia" | "mail" | "other"; // models.ItemChannel
export type ItemOutcome = "none" | "positive" | "negative" | "partial"; // models.ItemOutcome
export type ProceedingEventType = "" | "telefon" | "wyjazd" | "mail" | "inne"; // models.EventType
export type LetterDirection = "" | "outgoing" | "incoming"; // models.LetterDirection

// ── orders ────────────────────────────────────────────────────────────────────
// orders.OrderType — STANDARD + the two WPW kinds (terenowe / kompleksowe).
export type OrderType = "standard" | "wpw_terenowe" | "wpw_kompleksowe";

// ── DLC_02 correspondence register ───────────────────────────────────────────────
export type RegisterDirection = "outgoing" | "incoming"; // dlc_02.RegisterDirection
export type RegisterChannel = "paper" | "edoreczenia" | "mail" | "other"; // dlc_02.RegisterChannel
export type DispatchStatus =
  | "queued" | "prepared" | "sent" | "delivered" | "returned" | "undeliverable"
  | "received" | "filed" | "acknowledged"
  // Wychodzące: autor wycofał pismo z kolejki wysyłki, zanim wyszło (stan końcowy).
  | "cancelled"; // dlc_02.DispatchStatus

// Stan przesyłki papierowej w firmowym dzienniku korespondencji (DLC_02). Autor pisma nie ma
// wglądu w sam dziennik (wpis wychodzący nie jest na niego dekretowany), więc to jedyne miejsce,
// w którym widzi, czy przesyłka czeka w kolejce, czy już wyszła — bez tego zakładał zlecenie
// wydruku „na wszelki wypadek".
export interface PaperDispatchBrief {
  id: string;
  status: DispatchStatus;
  status_display: string;
  date: string | null;
  tracking_number: string;
  counterparty_name: string;
  handled_by_name: string | null;
  // Author of the dziennik entry (= the pismo's submitter) — gates the "open in DLC_02" link,
  // which leads to the authored-only "Moje pisma do wysłania" tab.
  registered_by: string | null;
}

// Przesyłka pisma w kolejce e-Doręczeń (DLC_09). Do akceptacji (CEO / dyrektor techniczny)
// pismo jest tylko złożone — bez daty wysłania; `awaiting_approval` = nie pisz „wysłano".
export interface EdeliveryBrief {
  id: string;
  status: "queued" | "approved" | "rejected" | "sending" | "sent" | "delivered" | "failed" | "cancelled" | "withdrawn";
  status_display: string;
  approved_at: string | null;
  sent_at: string | null;
  awaiting_approval: boolean;
}

export interface Progress {
  parcels_total: number;
  parcels_ok: number;
  parcels_problem: number;
  // Parcels actually USED by the project (current route runs through them or an object
  // sits on them) — reserve parcels ordered in wypisy don't measure progress. Summaries
  // show ok_used/used with the grand total only in parentheses.
  parcels_used: number;
  parcels_ok_used: number;
  // USED parcels that actually need an access title / notification (access_mode !=
  // NOT_REQUIRED) and how many of those are agreed — drives the workspace parcels-tab badge.
  parcels_required: number;
  parcels_ok_required: number;
  parcels_pct: number; // ok_used / used
  milestones_total: number;
  milestones_done: number;
  milestones_pct: number;
  open_agreements: number; // open proceedings (any scope, not CLOSED)
}

// ── karta specyfiki („trudny klient") ─────────────────────────────────────────

/** Flaga „szczególnej opieki" obowiązująca projekt (suma poziomów
 *  klient/oddział/pododdział). Badge/portfel dostają tylko aktywne;
 *  endpoint care/ (zakładka „Opieka") niesie też wygaszone + metadane. */
export interface CareFlagBrief {
  id: string;
  label: string;
  reason: string;
  scope: "client" | "branch" | "sub_branch";
  scope_name: string;
  is_active?: boolean;
  created_by_name?: string | null;
  created_at?: string;
}

/** Ustalenie oddziału/pododdziału (pełna lista dla zakładki „Opieka"). */
export interface CareArrangementBrief {
  id: string;
  kind: "warning" | "required_step";
  kind_display: string;
  text: string;
  step_label: string;
  /** SLA kroku: faza 1 = nasza akcja, faza 2 = odpowiedź strony. null = bez terminu. */
  submit_sla_days: number | null;
  resolve_sla_days: number | null;
  is_active: boolean;
  scope: "branch" | "sub_branch";
  scope_name: string;
}

/** Wiersz rejestru „szczególnych oddziałów" (GET power-projects/care-overview/). */
export interface CareOverviewRow {
  scope: "client" | "branch" | "sub_branch";
  id: string;
  name: string;
  /** Ścieżka od klienta w dół: [klient] / [klient, oddział] / [klient, oddział, pododdział]. */
  path: string[];
  client_id: string;
  flags: { id: string; label: string; reason: string }[];
  required_steps: { id: string; label: string; text: string }[];
  warnings: { id: string; label: string; text: string }[];
  notes_count: number;
  last_note: {
    text: string;
    author_name: string | null;
    created_at: string;
    project_id: string | null;
    project_name: string | null;
  } | null;
  projects: { project_id: string; project_name: string }[];
}

/** Wpis dziennika obserwacji (odczyt w workspace; pełny CRUD w module Klienci). */
export interface CareNoteBrief {
  id: string;
  text: string;
  author: string | null;
  author_name: string | null;
  scope_label: string;
  project: string | null;
  project_name: string | null;
  created_at: string;
}

/** GET connections/<id>/care/ — karta specyfiki dla workspace'u. */
export interface CareSummary {
  flags: CareFlagBrief[];
  warnings: { id: string; text: string; scope: "branch" | "sub_branch"; scope_name: string }[];
  arrangements: CareArrangementBrief[];
  notes: CareNoteBrief[];
  can_manage: boolean;
  scope: { client: string | null; branch: string | null; sub_branch: string | null };
}

export interface Milestone {
  id: string;
  // Stable board identifier: the enum `type` for built-in milestones, the UUID for
  // manually-added (custom) ones. Use this — not `type` — to key cards / set / delete.
  key: string;
  type: string; // "" for custom milestones
  type_display: string; // display name (enum label for built-ins, `label` for customs)
  is_custom: boolean;
  label: string; // manual name (custom milestones only)
  state: MilestoneState;
  state_display: string;
  detail: string;
  due_date: string | null;
  // Data FAKTYCZNEGO zakończenia etapu (nie data kliknięcia „gotowe") — wpisywana ręcznie
  // przy zamykaniu kroku wstecz; to ona idzie do raportu z postępu prac dla operatora.
  completed_at: string | null;
  // Ile notatek / plików wisi na kamieniu — karta na tablicy i kafelek na grafie
  // procesu pokazują tę samą liczbę, żeby było widać, gdzie są szczegóły.
  note_count: number;
  file_count: number;
  // Osobne pola wgrywania, gdy jeden krok wydaje dwa rodzaje artefaktu (MDCP + FOTO:
  // mapa vs zdjęcia). Puste = jedno, domyślne pole. Klucz "" = pole domyślne.
  file_slots?: { key: string; label: string }[];
  // Kamień z ustalenia „karty specyfiki" oddziału klienta — care_source to etykieta
  // poziomu (np. nazwa pododdziału), do podpisu „z ustaleń oddziału".
  care_arrangement?: string | null;
  care_source?: string | null;
}

// A single note in a milestone's details thread (newest first).
export interface MilestoneNote {
  id: string;
  text: string;
  author_name: string | null;
  // Osoby oznaczone @ w notatce — dostały powiadomienie z linkiem do tego kamienia.
  mentions: { id: string; full_name: string }[];
  created_at: string;
}

// A single note in a parcel's thread (newest first). `owner` set = the note is about
// one co-owner: the thread renders it distinctly, with who/share/access-title state.
// `owner_removed` marks a note whose owner was later deleted (only the name survives).
export interface ParcelNote {
  id: string;
  parcel: string;
  owner: string | null;
  owner_label: string;
  owner_share: string;
  owner_status: string;
  owner_status_display: string;
  owner_removed: boolean;
  text: string;
  author_name: string | null;
  can_delete: boolean;
  created_at: string;
}

export interface ManagedFileMini {
  id: string;
  original_filename: string;
  url: string | null;
  /** Serializer plików podaje rozmiar, ale nie każdy widok go przepisuje. */
  size_bytes?: number;
}

// Read-only client card exposed on the connection (metadata tab). When the
// project only has a free-text client name, `name` is the sole field present.
export interface ClientInfo {
  id?: string;
  name: string;
  short_name?: string;
  nip?: string;
  address?: string | null;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  website?: string;
}

export interface ClientBranchInfo {
  id: string;
  name: string;
  street?: string;
  city: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
}

// Mirrors ZEShortTypeProjectSerializer.get_source_tender_info — the ZE tender the
// project was created from (gate for copying its map variants into the project).
export interface SourceTenderInfo {
  id: string;
  title: string;
  reference_number: string;
  operator_name: string;
  variant_count: number;
}

export interface PowerProject {
  id: string;
  project: string;
  project_name: string;
  project_short_name: string;
  /** Los umowy przedsięwzięcia: "active" | "terminating" | "terminated" — znacznik w nagłówku. */
  project_contract_status?: "active" | "terminating" | "terminated";
  project_contract_terminated_on?: string | null;
  /** Koordynatorzy przedsięwzięcia (enterprises.Project.coordinators) — id userów. */
  project_coordinators: string[];
  /** Ich imiona i nazwiska — do nagłówka workspace'u. */
  project_coordinator_names: string[];
  /** Wartość przedsięwzięcia (PLN, netto wg Project.total_value). Backend przysyła ją
   *  TYLKO użytkownikowi z prawem do kwot tego przedsięwzięcia — inaczej null. */
  project_value: string | null;
  can_view_project_value: boolean;
  srid: number;
  /** Operator (OSD) projektu: "energa" | "tauron" | "inny" — decyduje o doborze
   *  szablonów, wnioskodawcy wypisów i sposobie opisywania prac. */
  operator: string;
  operator_display: string;
  source_tender: string | null;
  source_tender_info: SourceTenderInfo | null;
  contract_number: string;
  st_number: string;
  wp_number: string;
  contract_signed_at: string | null;
  contract_deadline: string | null;
  deadline_status: DeadlineStatus;
  contract_contact_name: string;
  contract_contact_phone: string;
  contract_contact_email: string;
  st_contact_name: string;
  st_contact_phone: string;
  st_contact_email: string;
  designer: string | null;
  designer_name: string | null;
  lead_designer: string | null;
  lead_designer_detail: Designer | null;
  lead_designer_name: string | null;
  coordinator: string | null;
  coordinator_detail: Designer | null;
  coordinator_name: string | null;
  /** Raporty okresowe do ZE: "auto" = z napięcia trasy, pozostałe wymuszają ręcznie. */
  reporting_mode: ReportingMode;
  /** Puste = domyślne 2 miesiące. */
  reporting_interval_months: number | null;
  /** Puste = data zawarcia umowy + 6 tygodni. */
  reporting_start_override: string | null;
  expected_completion_date: string | null;
  power_of_attorney: boolean;
  power_of_attorney_file: ManagedFileMini | null;
  friday_task: string | null;
  notes: string;
  extra_metadata: { label: string; value: string }[];
  kpi_interval_days: number | null;
  // Ścieżka listowna WPW: próg dni tego projektu (null = globalny, ustawia CEO) + obowiązujący.
  wpw_mail_auto_days?: number | null;
  wpw_mail_auto_days_effective?: number;
  // Wniosek regulacyjny (OMI) — dane stałe zadania
  connection_group: "" | "III" | "IV" | "V";
  omi_responsible_name: string;
  td_order_date: string | null;
  psp_zadania: string;
  branch_name: string | null;
  sub_branch_name: string | null;
  client_info: ClientInfo | null;
  branch_info: ClientBranchInfo | null;
  sub_branch_info: ClientBranchInfo | null;
  /** Aktywne flagi „szczególnej opieki" (karta specyfiki) — badge w nagłówku. */
  care_flags?: CareFlagBrief[];
  milestones: Milestone[];
  // Wody Polskie na karcie kamienia: ile zdarzeń wodnoprawnych czeka na zlecenie
  // podwykonawcy (0 = zlecone / nie ma czego zlecać) i czy detekcja jest liczona na
  // starszej wersji trasy. Pola opcjonalne — starszy backend ich nie przysyła.
  wody_pending_count?: number;
  wody_analysis_stale?: boolean;
  // This user's saved card order on the milestones board; null = shared default
  // (only users who actually dragged a card have a saved layout).
  milestone_layout: string[] | null;
  progress: Progress;
  topic_status: TopicStatus;
}

export interface MetadataPreset {
  id: string;
  name: string;
  columns: string[];
}

// Wpis wspólnej „książki telefonicznej" ZE — osoba prowadząca sprawy w projektach
// (kontakt z umowy lub z ST/WP). Budowana z danych już wpisanych w projektach + ręcznie;
// podpowiadana przy wypełnianiu metadanych kolejnych projektów.
export interface ProjectContact {
  id: string;
  name: string;
  phone: string;
  email: string;
  position: string;
  notes: string;
  is_active: boolean;
  created_by: string | null;
  created_by_name: string | null;
  project_count: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectContactInput {
  name: string;
  phone?: string;
  email?: string;
  position?: string;
  notes?: string;
  is_active?: boolean;
}

// Projekt ZE, w którym kontakt występuje (akcja contacts/{id}/projects/).
export interface ProjectContactProject {
  connection_id: string;
  // Id przedsięwzięcia (hub) — /power-projects/:id przyjmuje JE, nie id connection.
  project_id: string | null;
  project_name: string;
  roles: string[]; // "umowa" | "ST/WP"
}

// Kategoria wpisu w słowniku podpisów: projektant uprawniony (zewnętrzny) lub
// opracowujący (koordynator z ramienia ELEKTIM).
export type DesignerRole = "licensed_designer" | "coordinator";

export const DESIGNER_ROLE_LABELS: Record<DesignerRole, string> = {
  licensed_designer: "Projektant uprawniony",
  coordinator: "Opracowujący (ELEKTIM)",
};

// Wpis wspólnego słownika osób z podpisem na formatkach (globalny). Dwie kategorie:
// projektant uprawniony (wybierany na projekcie jako „Projektant uprawniony główny")
// i opracowujący (jako „Opracowujący"). Źródło zmiennych do formatek.
export interface Designer {
  id: string;
  role: DesignerRole;
  first_name: string;
  last_name: string;
  title: string;
  license_number: string;
  full_name: string;
  display_name: string;
  // Adres korespondencyjny — nagłówek oświadczeń projektanta w TOM II.
  street: string;
  building_no: string;
  postal_code: string;
  city: string;
  full_address: string;
  signature_file: ManagedFileMini | null;
  is_active: boolean;
  // Konto w systemie tej osoby — łączy wpis słownika z rejestrem pełnomocnictw
  // (rejestr trzyma pełnomocnictwa na kontach). Puste u osób spoza ELEKTIM.
  user: string | null;
  user_name: string;
  created_at: string;
  updated_at: string;
}

export interface DesignerInput {
  role?: DesignerRole;
  first_name: string;
  last_name: string;
  title?: string;
  license_number?: string;
  street?: string;
  building_no?: string;
  postal_code?: string;
  city?: string;
  is_active?: boolean;
  user?: string | null;
}

export interface PortfolioRow {
  id: string;
  project_id: string;
  project_name: string;
  client_name: string | null;
  contract_number: string;
  contract_deadline: string | null;
  deadline_status: DeadlineStatus;
  branch_name: string | null;
  sub_branch_name: string | null;
  /** Aktywne flagi „szczególnej opieki" — znacznik ⚠ przy nazwie w portfelu. */
  care_flags?: CareFlagBrief[];
  /** Temat Wód Polskich: "none" = brak, inaczej tryb realizacji (filtr „Wody Polskie"). */
  wody_status?: "none" | "internal" | "contractor";
  coordinator_name: string | null;
  /** Koordynatorzy przedsięwzięcia (enterprises.Project.coordinators) — id userów. */
  coordinator_ids: string[];
  progress: Progress;
  topic_status: TopicStatus;
  /** Los umowy przedsięwzięcia — wypiera status tematu (patrz TopicStatusControl). */
  project_contract_status?: "active" | "terminating" | "terminated";
  project_contract_terminated_on?: string | null;
  /** Wartość przedsięwzięcia (PLN). null gdy nie wpisana ALBO gdy user nie ma prawa do
   *  kwot tego przedsięwzięcia — te dwa przypadki rozróżnia `can_view_project_value`. */
  project_value: string | null;
  can_view_project_value: boolean;
  /** Kolumna „Otrzymano": kiedy projekt trafił do obecnie prowadzącego — podwykonawcy
   *  (przekazanie) albo koordynatora. `exact=false` = koordynator był przypisany już
   *  przed startem rejestru przypisań, data to „najpóźniej". null = nikt nie prowadzi. */
  received?: {
    date: string;
    by: "contractor" | "coordinator";
    name: string;
    exact: boolean;
  } | null;
  // ── Metadane profilu ZE (bez „kolumn dodatkowych") — źródło filtrów zaawansowanych.
  srid: number;
  st_number: string;
  wp_number: string;
  contract_signed_at: string | null;
  contract_contact_name: string;
  contract_contact_phone: string;
  contract_contact_email: string;
  st_contact_name: string;
  st_contact_phone: string;
  st_contact_email: string;
  power_of_attorney: boolean;
  notes: string;
  kpi_interval_days: number | null;
  operator: string;
  operator_display: string;
  designer_name: string | null;
  lead_designer_name: string | null;
  /** „Opracowujący" ze słownika Designer — osobne pole od `coordinator_name` (prowadzący). */
  ze_coordinator_name: string | null;
  source_tender: string | null;
  source_tender_title: string | null;
  process_template_name: string | null;
  connection_group: "" | "III" | "IV" | "V";
  omi_responsible_name: string;
  td_order_date: string | null;
  psp_zadania: string;
  reporting_mode: ReportingMode;
  reporting_interval_months: number | null;
  reporting_start_override: string | null;
}

/** Kolumna „Kamień" w portfelu — katalog kroków do wyboru i stan wybranego kroku
 *  per projekt (klucz mapy `rows` = id połączenia ZE, czyli PortfolioRow.id). */
export interface MilestoneColumnOption {
  key: string;
  label: string;
  /** W ilu projektach ZE ten krok w ogóle występuje. */
  count: number;
}

export interface MilestoneColumnCell {
  label: string;
  state: MilestoneState;
  detail: string;
  due_date: string | null;
  completed_at: string | null;
}

export interface MilestoneColumn {
  /** Krok, którego dotyczy `rows` ("" = zapytanie o sam katalog). */
  key: string;
  options: MilestoneColumnOption[];
  rows: Record<string, MilestoneColumnCell>;
}

/** Kolumna „Najbliższy raport" w portfelu: termin najbliższego raportu okresowego do
 *  operatora per projekt (klucz `rows` = PortfolioRow.id). Liczone tym samym kodem,
 *  co zakładka „Raporty" w projekcie. */
export type ReportColumnState = "overdue" | "due_soon" | "planned" | "unscheduled" | "none";

export interface ReportColumnCell {
  state: ReportColumnState;
  /** Czy projekt w ogóle raportuje (SN / wymuszone ręcznie). */
  required: boolean;
  /** Czy przypomnienia realnie działają (raportuje, ma datę startu, temat nie zamknięty). */
  active: boolean;
  voltage: string | null;
  /** Zdanie po polsku: dlaczego raporty są (nie)wymagane. */
  reason: string;
  next_due_date: string | null;
  /** Dni do najbliższego terminu (ujemne = zaległy); null, gdy terminu nie ma. */
  days_left: number | null;
  sequence_no: number | null;
  overdue_count: number;
  interval_months: number;
  /** Tylko przy oknie terminu (`due_from`/`due_to`/`period`): WSZYSTKIE otwarte terminy
   *  projektu wpadające w okno — nie tylko najbliższy. */
  window_slots?: ReportWindowSlot[];
}

export interface ReportWindowSlot {
  sequence_no: number;
  due_date: string;
  days_left: number;
  state: "overdue" | "due_soon" | "planned";
}

/** Skrót okna terminu: miesiąc kalendarzowy zamiast progu „N dni od dziś". */
export type ReportColumnPeriod = "current_month" | "next_month";

export interface ReportColumnParams {
  status?: ReportColumnState[];
  within_days?: number;
  /** true = tylko projekty z obowiązkiem raportowania (stan ≠ none). */
  required?: boolean;
  /** Okno terminu (ISO, włącznie) — projekty z JAKIMKOLWIEK otwartym terminem w oknie. */
  due_from?: string;
  due_to?: string;
  period?: ReportColumnPeriod;
}

export interface ReportColumn {
  within_days: number;
  status: ReportColumnState[];
  required: boolean | null;
  period: ReportColumnPeriod | null;
  due_from: string | null;
  due_to: string | null;
  /** Liczba projektów per stan (przed jakimkolwiek zawężeniem). */
  summary: Record<ReportColumnState, number>;
  /** Liczba wierszy po zawężeniu. */
  count: number;
  rows: Record<string, ReportColumnCell>;
}

/** Szukanie po danych POWIĄZANYCH z projektem (zakładki karty projektu): działki, KW,
 *  wnioski WNU/pakiety WPW, zamówienia, pliki, kamienie, warstwy DXF. Nagłówka projektu
 *  (nazwa, klient, umowa, prowadzący, oddział, EPSG, metadane) tu NIE ma — ten dopasowuje
 *  przeglądarka na wierszu z /portfolio/. */
export type DeepSearchKind =
  | "parcel" | "kw" | "application" | "order"
  | "wypis_order" | "mz_order" | "mpzp_order"
  | "file" | "milestone" | "dxf_layer";

export interface DeepSearchHit {
  kind: DeepSearchKind;
  /** Etykieta rodzaju po polsku, gotowa do pokazania („Działka", „Warstwa DXF"). */
  label: string;
  /** Dopasowana wartość (numer działki, numer KW, nazwa pliku…). */
  value: string;
  /** Doprecyzowanie („obr. Ciepłe", „A1 (bieżąca)", status zamówienia). */
  detail: string;
}

export interface DeepSearchRow {
  /** Do HITS_PER_CONNECTION trafień; reszta zliczona w `total`. */
  hits: DeepSearchHit[];
  total: number;
}

export interface DeepSearchResult {
  query: string;
  rows: Record<string, DeepSearchRow>;
  counts: Record<DeepSearchKind, number>;
  /** Fraza trafiła w sufit wierszy jednej z tabel — wynik jest niepełny. */
  truncated: boolean;
  min_query_len: number;
}

export interface GeoJsonGeometry {
  type: string;
  coordinates: unknown;
}

export interface MapObject {
  id: string;
  semantic_type: string;
  attrs: Record<string, unknown>;
  parcel: string | null;
  geometry: GeoJsonGeometry | null;
}

export interface RouteVersion {
  id: string;
  variant: string;
  number: number;
  label: string;
  is_current: boolean;
  length_m: number | null;
  // Długość korytarza: metry terenu, gdzie linie biegnące równolegle w jednym pasie
  // liczą się raz. Z niej idzie zamówienie MDCP. null dla wersji sprzed tej zmiany.
  corridor_length_m: number | null;
  // Powierzchnia pasa trasy [ha]: 35 m w każdą stronę od osi (70 m), nakładające się
  // odcinki liczone raz. Ilość, gdy geodeta rozlicza MDCP za hektar. null dla starych wersji.
  corridor_area_ha: number | null;
  source: string;
  dxf_file: ManagedFileMini | null;
  base_map_file: ManagedFileMini | null;
  gis_project_id: string | null;
  layer_preset: string | null;
  layer_preset_name: string | null;
  upload_comment: string;
  created_by_name: string | null;
  // Who last edited this version in place; null when untouched since upload.
  updated_by_name: string | null;
  unknown_layers: string[];
  dxf_layers: DxfLayerStat[];
  geometry: GeoJsonGeometry | null;
  // Demolition ("likwidacja"/"demontaż") lines — drawn BLUE apart from the red route.
  demolition_geometry: GeoJsonGeometry | null;
  // Per-type line overlay: FeatureCollection with properties.semantic_type
  // (ZEElementType.code) — styling like the tender drawing. Null for old versions.
  typed_lines: {
    type: "FeatureCollection";
    features: { type: "Feature"; geometry: GeoJsonGeometry; properties: { semantic_type: string } }[];
  } | null;
  objects: MapObject[];
  parcels_recomputed_at: string | null;
  created_at: string;
  updated_at: string;
}

// Answer of DELETE route-versions/{id}/ — what the delete did to the variant.
export interface RouteVersionDeleteResult {
  deleted: string; // "A2"
  variant: string; // "A"
  // The deleted version was the variant's last one, so the variant is gone too.
  variant_deleted: boolean;
  // The variant's current version after the delete ("A1"); null when the variant went.
  current_version: string | null;
  // The connection's authoritative route changed (parcel statuses were recomputed).
  route_changed: boolean;
}

export interface MdcpPointPhoto {
  id: string;
  original_name: string;
  file: { id: string; original_filename: string; url: string | null } | null;
  uploaded_by_name: string | null;
  created_at: string;
  /** Odbiór zamawiającego: zdjęcie odrzucone zostaje widoczne (także u geodety), ale
   *  przestaje być materiałem zamówienia — powód mówi, co trzeba powtórzyć. */
  is_rejected: boolean;
  rejected_at: string | null;
  rejected_by_name: string | null;
  rejection_reason: string;
}

export interface MdcpPhotoPoint {
  id: string;
  connection: string;
  number: number;
  azimuth_deg: number;
  note: string;
  is_done: boolean;
  order_link: string | null;
  /** Punkt wystawiony w opublikowanym postępowaniu ofertowym — zamrożony tak samo jak zlecony. */
  rfq_link: string | null;
  geometry: GeoJsonGeometry | null;
  // Survey-accurate PUWG 2000 coordinates in the project zone (X = northing, Y = easting).
  coords_2000: { x: number; y: number; srid: number } | null;
  photos: MdcpPointPhoto[];
  /** Zdjęcia, które przeszły odbiór (odrzucone NIE liczą się — punkt jest znów „bez zdjęcia"). */
  photos_count: number;
  rejected_photos_count: number;
  created_at: string;
}

// Bufor (obrys) zamówienia MDCP: poligon zakresu dla geodety. Zasiewany jako bufor
// `buffer_width_m` m wokół trasy, edytowalny (przeciąganie wierzchołków). Geometria w 4326.
// Projekt ma ich WIELE (lista rozwijana na mapie): `is_active` to obrys, który idzie do
// kolejnego zamówienia, a `order_numbers` mówi, które zamówienia stoją na tym obrysie.
export interface MdcpBuffer {
  id: string;
  name: string;
  /** Etykieta na liście: nazwa własna albo „Bufor <szerokość> m". */
  display_name: string;
  is_active: boolean;
  buffer_width_m: number;
  is_customized: boolean;
  geometry: GeoJsonGeometry | null;
  srid: number | null;
  area_m2: number | null;
  order_numbers: string[];
  created_at?: string;
  updated_at?: string;
}

// Odpowiedź endpointów listy obrysów: cała lista + wskazanie wybranego.
export interface MdcpBufferList {
  buffers: MdcpBuffer[];
  buffer: MdcpBuffer | null;
}

// Zamrożony obrys MDCP w zamówieniu (widok tylko-do-odczytu).
export interface MdcpBufferSnapshot {
  geometry: GeoJsonGeometry;
  width_m: number | null;
  srid: number | null;
}

// Line placement read per drawn segment of the route (backend line_placement.py):
// cable in the ground (kablowa/doziemna) vs overhead (napowietrzna). "unknown" when the
// slug says nothing about it; "mixed" only as the route headline.
export type LinePlacement = "underground" | "overhead" | "unknown";
export type LinePlacementHeadline = LinePlacement | "mixed";
// Status bucket of a segment in the placement summary.
export type LineStatusBucket = "construction" | "demolition" | "existing";

export interface PlacedTypedLines {
  type: "FeatureCollection";
  features: {
    type: "Feature";
    geometry: GeoJsonGeometry;
    properties: { semantic_type: string; placement: LinePlacement; status: LineStatusBucket };
  }[];
}

export interface LinePlacementSummary {
  headline: LinePlacementHeadline;
  // Sorted construction → demolition → existing, underground → overhead → unknown;
  // metres measured in the project's PUWG zone.
  rows: { status: LineStatusBucket; placement: LinePlacement; length_m: number }[];
}

// View-only route context for contractor-facing maps (surveyor's order, contractor
// project map). typed_lines/line_placement are null for versions parsed before per-type
// classification — the map then draws the flat geometry.
export interface RouteMapContext {
  geometry: GeoJsonGeometry | null;
  demolition_geometry: GeoJsonGeometry | null;
  length_m: number | null;
  typed_lines: PlacedTypedLines | null;
  line_placement: LinePlacementSummary | null;
}

// Payload of /power-projects/mdcp/order/:id/points/ — points + view-only map context
// (current route and the parcels under it; parcel numbers only, no owner data).
export interface MdcpOrderMap {
  points: MdcpPhotoPoint[];
  route: RouteMapContext | null;
  parcels: { id: string; teryt: string; short_number: string; geometry: GeoJsonGeometry | null }[];
  buffer: MdcpBufferSnapshot | null;
  order: {
    id: string;
    order_number: string;
    status: string;
    status_display: string;
    /** Odebrane / zamknięte / anulowane — materiału zamówienia nikt już nie ocenia. */
    is_finished: boolean;
  } | null;
  /** Czy TEN użytkownik może odrzucać zdjęcia (zamawiający + zamówienie niezakończone). */
  can_review_photos: boolean;
}

export interface MdcpOrderRow {
  id: string;
  order: string;
  order_number: string;
  order_status: string;
  order_status_display: string;
  contractor_name: string | null;
  deadline: string | null;
  kind: string;
  is_paid: boolean;
  points_snapshot: { number: number; azimuth_deg: number; note: string; lat: number | null; lng: number | null }[];
  points_count: number;
  // Ile sztuk „100 mb" siedzi realnie w zamówieniu u geodety; null = zamówienie
  // rozlicza się inaczej (np. stawka jednostkowa) i nie ma z czym porównywać.
  ordered_length_units: number | null;
  // Hektary w zamówieniu (pozycje MDCP za ha); null, gdy geodeta nie rozlicza za hektar.
  ordered_area_units: number | null;
  // Zamrożony obrys tego zamówienia (GeoJSON 4326) — mapa podpisuje nim obrysy
  // per zamówienie. null dla zamówień sprzed obrysów (projekt bez trasy).
  buffer_geometry: GeoJsonGeometry | null;
  // Który obrys z listy projektu poszedł w to zamówienie (null dla zamówień sprzed
  // wielu buforów — wtedy zostaje sam zamrożony buffer_geometry).
  buffer: string | null;
  buffer_name: string;
  created_at: string;
}

// Ile „szt. 100 mb" wychodzi z trasy: z sumy narysowanych linii i z korytarza (metry
// terenu, gdzie nitki biegnące razem liczą się raz). Puste, gdy projekt nie ma trasy.
export interface MdcpRouteLengthCheck {
  length_m?: number | null;
  corridor_length_m?: number | null;
  units_from_length?: number | null;
  expected_units?: number | null;
  // Rozliczenie za hektar: pas `area_half_width_m` (35 m) w każdą stronę od trasy,
  // nakładające się odcinki raz; expected_area_units = rozpoczęte hektary.
  corridor_area_ha?: number | null;
  expected_area_units?: number | null;
  area_half_width_m?: number | null;
}

// ── postępowanie ofertowe dla etapu (PT / kosztorys / MDCP) ────────────────────
// Druga droga wyłonienia wykonawcy: zamiast wskazać firmę, wystawiamy etap w przetargu.
// Zamówienie i tak powstaje w panelu etapu („Zleć zwycięzcy"), więc wszystkie automaty
// DLC_01 działają jak przy zleceniu wprost.
export interface StageProceeding {
  id: string;
  number: string;
  title: string;
  status: "draft" | "published" | "awarded" | "cancelled";
  status_display: string;
  submission_deadline: string;
  delivery_deadline: string;
  published_at: string | null;
  /** Chwila publikacji ogłoszenia = chwila zamrożenia zakresu (punkty, obrys, pliki). */
  frozen_at: string | null;
  link_kind: "primary" | "correction";
  points_frozen: number;
  files_frozen: number;
  /** Dopóki true, zlecenie wprost na tym etapie jest zablokowane. */
  blocks_direct_order: boolean;
  /** Rozstrzygnięte, ale jeszcze niezlecone — panel prosi o klik „Zleć zwycięzcy". */
  awaiting_order: boolean;
  winner: { contractor_id: string; contractor_name: string; amount_net: string } | null;
  order_id: string | null;
}

/** Jedna firma z kręgu oferentów wraz z faktami, które uzasadniają zaproszenie. */
export interface StageRecipientCandidate {
  id: string;
  name: string;
  city: string;
  orders: number;
  last_order_at: string | null;
  offers: number;
  wins: number;
  rating: number | null;
  ratings_count: number;
  distance_km: number | null;
  /** Wstępne zaznaczenie (AI albo punktacja z historii) — propozycja, nie decyzja. */
  preselected: boolean;
  reason: string;
}

export interface StageRecipientsSuggestion {
  contractors: StageRecipientCandidate[];
  note: string;
  /** „ai" = wybrał model, „rules" = punktacja z historii (AI niedostępne). */
  source: "ai" | "rules" | "empty";
  voivodeship: string;
  /** Powiaty trasy w województwie (kody TERYT) — kryterium doprecyzowane jak w ogłoszeniu. */
  counties: string[];
  eligible_count: number;
}

export interface StageProceedingInput {
  title?: string;
  description?: string;
  submission_deadline: string;
  delivery_deadline: string;
  max_price_net?: string | null;
  max_price_visible?: boolean;
  price_basis?: string;
  access_mode?: string;
  voivodeship?: string;
  positions?: string[];
  link_kind?: "primary" | "correction";
  is_paid?: boolean;
  attach_tender_docs?: boolean;
  attach_route_dxf?: boolean;
  /** true = „utwórz i opublikuj od razu" (bez etapu szkicu). */
  publish?: boolean;
  /** Zaproszenia mailem przy publikacji od razu. Pominięte = tak (zachowanie dotychczasowe);
      false publikuje ogłoszenie bez wysyłki — firmy zobaczą je w systemie. */
  notify_by_mail?: boolean;
  /** Zaproszenie imienne: mail idzie WYŁĄCZNIE do tych firm (bije `notify_by_mail`). */
  mail_contractors?: string[];
}

export interface MdcpPanelState {
  points: MdcpPhotoPoint[];
  orders: MdcpOrderRow[];
  /** Wybrany obrys (zakres kolejnego zlecenia) — pozycja z `buffers` z is_active. */
  buffer: MdcpBuffer | null;
  /** Wszystkie obrysy projektu — lista rozwijana pod mapą. */
  buffers: MdcpBuffer[];
  route_length: MdcpRouteLengthCheck;
  proceeding: StageProceeding | null;
}

// Pre-upload validation report (the optional "monit przed wysłaniem"): every preset
// layer with its status in the file, plus non-preset layers that carry entities.
export interface DxfValidationLayer {
  layer: string;
  kind: DxfLayerKind;
  status: "objects" | "empty" | "missing";
  lines: number;
  points: number;
  other: number;
  // INSERTs of empty block definitions — invisible in CAD, never parsed into objects.
  empty_symbols?: number;
}

export interface DxfValidationReport {
  preset_name: string | null;
  preset_layers_total: number;
  detected: number;
  with_objects: number;
  empty: number;
  missing: number;
  layers: DxfValidationLayer[];
  extra_layers: {
    layer: string;
    lines: number;
    points: number;
    other: number;
    empty_symbols?: number;
  }[];
}

// Per-layer breakdown of an uploaded DXF (the "inspector"): how many line/point
// entities each layer holds and how the chosen preset maps it (null = unmapped).
export interface DxfLayerStat {
  layer: string;
  lines: number;
  points: number;
  other: number;
  // Optional: reports stored before empty-insert detection don't carry it.
  empty_symbols?: number;
  mapped_as: DxfLayerKind | null;
}

export interface RouteVariant {
  id: string;
  connection: string;
  label: string;
  // The binding variant — its current version drives parcel statuses, scope maps and
  // ENERGA descriptions. A single-variant project is binding automatically (flag false
  // until explicitly set). At most one variant per connection has this true.
  is_binding: boolean;
  versions: RouteVersion[];
  // May the current user delete this variant (koordynator projektu / dyrektor
  // techniczny / CEO)? Computed server-side; gates the delete control on the map tab.
  can_delete: boolean;
}

// Mirrors apps.dlc_01_power_project.models.ParcelRouteSegment — the route's course
// through one działka per version (geometry + length), recorded without mutating the parcel.
export interface ParcelRouteSegment {
  id: string;
  parcel: string;
  route_version: string;
  route_version_label: string | null;
  length_m: number | null;
  geometry: GeoJsonGeometry | null;
  created_at: string;
}

// Mirrors apps.geo.models — a named preset is a set of DXF layer mappings; the
// designer picks which preset their map follows when uploading a DXF.
export type DxfLayerKind = "line" | "object";

export interface DxfLayerPreset {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  line_count: number;
  object_count: number;
}

// Mirrors apps.dlc_01_power_project.models.ProjectEvent — one tagged row per change,
// the single "Historia postępów" feed sliceable by any tag.
export type ProjectEventKind =
  | "project_created"
  | "route_version_created"
  | "parcel_added"
  | "parcel_entered_route"
  | "parcel_left_route"
  | "parcel_route_changed"
  | "parcel_status_changed"
  | "parcel_data_edited"
  | "owner_access_status_changed"
  | "owner_access_route_extended"
  | "proceeding_status_changed"
  | "proceeding_item"
  | "decision_registered"
  | "milestone_changed"
  | "topic_status_changed"
  | "kpi_review"
  | "wpw_mail_letter";

export interface ProjectEvent {
  id: string;
  kind: ProjectEventKind;
  kind_display: string;
  title: string;
  detail: string;
  occurred_at: string;
  actor_name: string;
  payload: Record<string, unknown>;
  parcel: string | null;
  parcel_teryt: string | null;
  office: string | null;
  office_name: string | null;
  route_version: string | null;
  route_version_label: string | null;
  owner: string | null;
  owner_name: string | null;
  proceeding: string | null;
  proceeding_label: string | null;
  owner_access: string | null;
}

// Status techniczny warstwy — z niego (a nie ze zgadywania z nazwy typu) dobierają
// się formularze zgody WPW, warstwa demontażu na mapie i statystyki montaż/demontaż.
export type DxfLayerStatus = "" | "projektowane" | "likwidowane" | "modernizowane" | "istniejace";

export interface DxfLayerCatalogEntry {
  id: string;
  preset: string | null;
  layer_name: string;
  kind: DxfLayerKind;
  semantic_type: string;
  // "" = nieustalony, czytany awaryjnie z nazwy warstwy.
  status: DxfLayerStatus;
  is_active: boolean;
}

// ── access basis registry (extensible) ───────────────────────────────────────────
// Mirrors models.AccessBasisType — referenced by FK from OwnerAccess so new bases
// (e.g. służebność przesyłu) can be added by a director without a migration.
export interface AccessBasisType {
  id: string;
  code: string;
  label: string;
  is_active: boolean;
  order: number;
  // Domyślna forma zgody ENERGA (Załącznik 7) dla tej podstawy; "" = Inne/brak.
  z7_consent_form: string;
}

// ── per-owner access title (OwnerAccess) ─────────────────────────────────────────
// Mirrors serializers.OwnerAccessEventSerializer.
export interface OwnerAccessEvent {
  id: string;
  from_status: OwnerAccessStatus | "";
  from_status_display: string;
  to_status: OwnerAccessStatus;
  to_status_display: string;
  note: string;
  changed_at: string;
  changed_by_name: string;
}

// Mirrors serializers.OwnerAccessSerializer. The title is per-owner, carries the
// route version it was obtained for (is_stale = SECURED on a non-current version),
// and remembers where it came from (proceeding decision / WPW order).
export interface OwnerAccess {
  id: string;
  owner: string;
  owner_name: string;
  parcel_id: string;
  status: OwnerAccessStatus;
  status_display: string;
  basis: string | null;
  basis_code: string | null;
  basis_label: string | null;
  route_version: string | null;
  route_version_label: string | null;
  is_stale: boolean; // SECURED on a non-current route version → "nieaktualna wersja" badge
  secured_at: string | null;
  source_proceeding: string | null;
  source_proceeding_label: string | null;
  source_order: string | null;
  problem_note: string;
  // Załącznik 7 ENERGA: forma zgody wybrana przy pozyskiwaniu + kwota/nr AN.
  z7_consent_form: string;
  remuneration_amount: string | null;
  notary_deed_number: string;
  files: ManagedFileMini[]; // dokumenty tytułu (multifile): podpisana zgoda / decyzja / skan
  events: OwnerAccessEvent[];
  created_at: string;
  updated_at: string;
}

// ── parcels & owners ───────────────────────────────────────────────────────────
// Rola osoby/podmiotu względem działki (models.OwnerRole). "" = nie wskazano.
export type OwnerRole =
  | ""
  | "owner"
  | "leaseholder"
  | "perpetual_usufructuary"
  | "administrator"
  | "guarantor"
  | "contractor"
  | "designer";

export const OWNER_ROLE_OPTIONS: { value: Exclude<OwnerRole, "">; label: string }[] = [
  { value: "owner", label: "Właściciel" },
  { value: "leaseholder", label: "Dzierżawca" },
  { value: "perpetual_usufructuary", label: "Użytkownik wieczysty" },
  { value: "administrator", label: "Zarządca" },
  { value: "guarantor", label: "Gwarant" },
  { value: "contractor", label: "Wykonawca" },
  { value: "designer", label: "Projektant" },
];

export interface ParcelOwner {
  id: string;
  parcel: string;
  kind: "person" | "company";
  kind_display: string;
  // Rola względem działki — opcjonalna; puste = nie wskazano (traktujemy jak właściciela).
  role: OwnerRole;
  role_display: string;
  office: string | null;
  office_name: string | null;
  // offices.OfficeKind ("gmina" | "starostwo" | "zarzadca_drogi" | …) — rozróżnia
  // urząd-właściciela od urzędu użytkownika wieczystego przy gruntach w użytkowaniu
  // wieczystym (grupy 2/5/12/14/16 — utils.PERPETUAL_USUFRUCT_GROUPS).
  office_kind: string | null;
  office_full_address: string | null;
  full_name: string;
  street: string;
  building_no: string;
  postal_code: string;
  city: string;
  address: string; // legacy single-line address; kept as a fallback
  full_address: string; // composed structured address (read-only)
  pesel_nip: string;
  phone: string;
  email: string;
  ownership_group: string;
  ownership_share: string;
  // Udział trzymany SAMODZIELNIE obok udziału we wspólności małżeńskiej (wypis potrafi
  // wykazać tę samą osobę dwa razy: sama + małżeństwo). Rekord i pakiet są jedne;
  // ownership_share = udział wspólności, own_share = udział samodzielny.
  own_share: string;
  // Wspólność małżeńska odczytana wprost z wypisu — małżonkowie jednego udziału mają
  // tę samą niepustą wartość w obrębie działki; puste = wypis nie stwierdza wspólności.
  marital_group: string;
  comment: string;
  wypis_file: ManagedFileMini | null;
  access: OwnerAccess | null; // latest access title for this owner (per-owner drawer section)
  in_field: boolean; // THIS owner has an active WPW handover the negotiator has started (per-owner)
  /** Właściciel objęty OTWARTĄ sprawą na tej działce — tytuł „Nie rozpoczęto" czyta się
   * wtedy jako „W trakcie" (tak samo w kolumnie „Podstawa dostępu" tabeli statusów). */
  in_open_case: boolean;
  /** Właściciel objęty OTWARTĄ sprawą URZĘDOWĄ (wniosek w urzędzie: art. 124 / 124a UGN,
   * spadkobiercy…). Tytuł „Problem" (zgon, niemożliwe zebranie podpisu) czyta się wtedy jako
   * „W trakcie", a automat liczy działkę po wniosku. Sprawa zlecenia WPW tu nie wchodzi. */
  in_open_office_case: boolean;
  // Promowalna, niepotwierdzona zgoda WPW (np. z zaakceptowanego zwrotu) — 1-klik „Potwierdź” → tytuł SECURED.
  wpw_title_proposal: {
    handover_id: string;
    target_event_id: string | null;
    declared_status: string;
    declared_status_label: string;
    z7_consent_form: string;
    attachments: WpwHandoverFile[]; // skany negocjatora — podgląd „co potwierdzamy”
  } | null;
  // Porozumienia wgrane przez negocjatora czekające na per-plikową decyzję GK — chip
  // „porozumienie do zatwierdzenia" na liście działek + blok zatwierdzania w szczegółach.
  pending_agreements: {
    handover_id: string;
    order_id: string;
    order_number: string | null;
    file: WpwHandoverFile;
  }[];
  // Ścieżka listowna WPW: automatyczny list tego właściciela (null = brak pakietu listownego).
  wpw_mail_letter?: WpwMailLetterSummary | null;
  // Ręczna blokada automatycznej wysyłki listu TEGO właściciela (akcja wpw-mail-block).
  wpw_mail_blocked?: boolean;
  wpw_mail_blocked_reason?: string;
  wpw_mail_blocked_by_name?: string | null;
  wpw_mail_blocked_at?: string | null;
  regulatory_notes: string; // per-owner uwagi for the OMI regulatory application (#regulacja_uwagi#)
  // „Nieaktualny" — ponownie zamówiony wypis (zmiana właścicieli) nie potwierdził go.
  // Rekord zostaje z historią (tytuł, WPW, WNU, sprawy), ale karta działki go nie
  // pokazuje; widać go po checkboxie „pokaż nieaktualnych". Null = aktualny.
  outdated_at: string | null;
  outdated_reason: string;
  outdated_by_order: string | null;
  outdated_by_order_number: string | null;
}

// Read-only "Podstawa dostępu" rollup of a parcel (serializers get_access_summary).
export interface ParcelAccessSummary {
  owners_total: number;
  owners_agreed: number;
  access_not_required: boolean;
  bases: Record<string, number>; // {basis_code: count}
  stale: number; // SECURED titles on a non-current route version
  problem: number;
  refused: number;
  // Suma UDZIAŁÓW (a nie głów) właścicieli z pozyskanym tytułem — po jednostkach zgody:
  // małżeństwo ma jeden wspólny udział i liczy się dopiero, gdy tytuł mają OBOJE.
  shares_secured: string; // „37%"; „0%" gdy nikt jeszcze nie podpisał
  shares_total: string; // suma udziałów wszystkich jednostek (kontrolnie — powinno wyjść 100%)
  shares_unknown_units: number; // jednostki bez czytelnego zapisu udziału (zaniżają sumę)
  shares_majority: boolean; // ponad 50% (RÓWNE 50% to jeszcze nie większość)
}

// Porównanie tytułów dostępu z bieżącą trasą (backend title_reconcile): tytuł pozyskany na
// innej wersji wraca na bieżącą, gdy przebieg na działce (odcinek, typy linii i obiekty) mieści się
// w tolerancji 0,7 m; „changed" = inny przebieg, „unverifiable" = brak zapisanego przebiegu.
export type TitleReconcileVerdict = "extended" | "changed" | "unverifiable";
export interface TitleReconcileTitle {
  owner: string;
  owner_name: string;
  from_version: string | null; // etykieta wersji tytułu („A1"); null = tytuł bez wersji
  verdict: TitleReconcileVerdict;
  // Powód werdyktu (same / route / line_types / objects / left / no_old_course /
  // not_recomputed / types_unknown / no_version / no_parcel_geometry) + zdanie dla człowieka:
  // co się zmieniło („linie: było: … napowietrzna; jest: … kablowa") albo czego brakuje.
  reason: string;
  detail: string;
}
export interface TitleReconcileParcelResult {
  parcel: string;
  teryt: string;
  current_version: string | null;
  extended: number;
  changed: number;
  unverifiable: number;
  titles: TitleReconcileTitle[];
}
export interface TitleReconcileSummary {
  current_version: string | null;
  parcels_checked: number; // działki, które miały tytuł poza bieżącą wersją
  titles_extended: number;
  titles_changed: number;
  titles_unverifiable: number;
  parcels: TitleReconcileParcelResult[];
}

// Per-działka odrolnienie (wyłączenie z produkcji rolniczej) verdict — obiekt naziemny ×
// użytek rolny × klasa I–III. `required` = automat kwalifikuje do wyłączenia; `manual` =
// brak danych (nieznany użytek albo użytek rolny bez klasy bonitacyjnej) → decyzja ręczna.
// Znana klasa poza I–III to czyste „nie wymaga" — ani `required`, ani `manual`.
// Pojedynczy odczyt klasoużytku POD urządzeniem — wraz z tym, skąd się wziął.
// „Skąd wiemy" jest częścią odpowiedzi: wykaz klasoużytków działki nie rozstrzyga, na
// którym z nich stoi obiekt, więc werdykt bez podanego źródła jest nie do obrony.
export interface ParcelLandClassRead {
  id: string;
  device: string;
  device_type: string;
  parcel_teryt: string;
  in_project: boolean; // false = działka sąsiednia (np. droga), na której też stoi złącze
  klasouzytek: string;
  uzytek: string;
  kontur: string;
  method: "county_api" | "map_ai" | "manual" | "none" | (string & {});
  method_label: string;
  method_reason: string; // dlaczego TA metoda, a nie druga (dostępność usługi powiatu)
  confidence: "certain" | "uncertain" | "failed" | (string & {});
  near_boundary: boolean; // obiekt do 0,5 m od granicy działki albo konturu
  cross_checked: boolean; // czy odczyt dało się porównać z wykazem klasoużytków działki
  parcel_contours: string;
  contours_source: "kieg" | "wypis" | "geoportal" | "" | (string & {});
  source_url: string;
  source_detail: string;
  ai_model: string;
  ai_note: string;
  map_crops: { key: string; file_id: string; caption: string }[];
  manual_note: string;
  confirmed_at: string | null;
  checked_at: string | null;
}

// Pochodzenie werdyktu: metoda wiodąca + wszystkie odczyty, które się na niego złożyły.
// `method` = "rule" (brak obiektu naziemnego), "wypis"/"geoportal_list" (wykaz całej
// działki — nie wiadomo, na którym klasoużytku stoi obiekt), "county_api"/"map_ai"/
// "manual" (odczyt punktowy) albo "mixed".
export interface ParcelOdrolnienieSource {
  method: string;
  method_label: string;
  why: string;
  reads: ParcelLandClassRead[];
}

// Powierzchnia trwale zajęta przez urządzenia na działce — liczba, która idzie do wniosku
// o wyłączenie z produkcji rolniczej i którą widać zakreskowaną na mapie.
export interface ParcelExclusionArea {
  area_m2: number;
  devices: number;
  by_kind: { kind: string; count: number; area_m2: number }[];
  missing_rates: string[]; // typy urządzeń bez stawki — powierzchnia jest zaniżona
}

// Stawka powierzchni posadowienia urządzenia (słownik firmowy, per operator).
export interface DeviceFootprintRate {
  id: string;
  operator: string;
  kind: string;
  kind_label: string;
  width_m: number;
  length_m: number;
  area_m2: number;
  note: string;
  can_edit: boolean;
  updated_at: string;
}

// Propozycja podziału wniosków o odrolnienie: obręb po obrębie, z werdyktem przy
// każdej działce. Kreator pokazuje to zamiast kazać zgadywać, co wchodzi do wniosku.
export interface OdrolnienieScopeParcel {
  id: string;
  teryt: string;
  short_number: string;
  qualifies: boolean;
  required: boolean;
  manual: boolean;
  reason: string;
  source_label: string;
  devices: number;
  exclusion_area_m2: number;
}

export interface OdrolnienieScopeGroup {
  obreb: string;
  obreb_label: string;
  county: string;
  commune: string;
  qualifying: number;
  parcels: OdrolnienieScopeParcel[];
}

// Ręczna decyzja o odrolnieniu — bije sugestię automatu w obie strony; null = automat.
export type OdrolnienieOverride = "required" | "not_required";

export interface ParcelOdrolnienie {
  required: boolean;
  manual: boolean;
  reason: string;
  source?: ParcelOdrolnienieSource;
  override?: OdrolnienieOverride | null;
  override_note?: string;
  override_by?: string | null;
  override_at?: string | null;
  // Sugestia ELGW5 zachowana przy decyzji ręcznej — żeby było widać, CO odkliknięto.
  auto?: { required: boolean; manual: boolean; reason: string } | null;
}

// Werdykt analizy KW działki, liczony z NAJNOWSZEJ przeanalizowanej księgi:
// "none" = brak księgi, "pending" = wgrana, ale jeszcze nieanalizowana,
// "ok" = zielono (nic do zgłoszenia), "issues" = pomarańczowo (lista w `issues`).
export interface ParcelKwSummary {
  state: "none" | "pending" | "ok" | "issues";
  issues: string[];
  roads: KwRoadRow[];
  internal_road: boolean;
  kw_number: string;
  analyzed_at: string | null;
  files: number;
  accepted: number; // ile rozbieżności zaakceptowano ręcznie
}

// Czy działka JEST drogą (kw_summary.parcel_road_state): wpisy drogowe z KW albo decyzja
// koordynatora, która je przykrywa (akcja set-road). `entries` to ZAWSZE wpisy z księgi —
// decyzja „nie droga" ich nie kasuje, więc po powrocie do „droga" wracają na widok.
export interface ParcelRoadState {
  is_road: boolean;
  source: "kw" | "manual";
  kw_is_road: boolean; // co mówi sama księga
  internal: boolean; // droga wewnętrzna wg KW (przy decyzji „droga" zostaje z księgi)
  override: boolean | null;
  override_by_name: string;
  override_at: string | null;
  note: string;
  entries: KwRoadRow[];
}

export interface ParcelOfficeHint {
  reason: string;
  owner: { id: string; name: string; institution: string; label: string } | null;
}

/** Kategoria uzgodnienia działki (models.LocationCategory) — private_* to działki prywatne
 *  (WPW), reszta to instytucje (wniosek do urzędu). */
export type LocationCategory =
  | "jst" | "private_connected" | "private_transit" | "gddkia" | "pkp" | "kowr" | "lasy"
  | "wody" | "other";

export interface Parcel {
  id: string;
  connection: string;
  teryt: string;
  short_number: string;
  obreb: string;
  karta_mapy: string;
  jednostka_ewidencyjna: string;
  voivodeship: string;
  county: string;
  commune: string;
  street_and_number: string;
  locality: string; // miejscowość z geokodera („Pobierz adres") albo wpisana ręcznie
  document_locality: string; // to, co dostanie #dzialka_miejscowosc#: locality, a gdy puste — obręb/gmina
  land_use: string;
  soil_class: string;
  geoportal_land_use: string; // klasoużytek z i-geoportalu (KIEG) — cross-check wypisu
  geoportal_land_use_checked_at: string | null; // sprawdzono w KIEG; ustawione + puste geoportal_land_use = powiat nie publikuje („?")
  odrolnienie: ParcelOdrolnienie; // per-działka werdykt wyłączenia z produkcji rolniczej
  odrolnienie_override?: OdrolnienieOverride | null; // decyzja ręczna (surowo; werdykt niesie ją w `odrolnienie.override`)
  exclusion_area: ParcelExclusionArea; // powierzchnia do wyłączenia (suma stawek urządzeń)
  kw_number: string;
  kw_summary: ParcelKwSummary; // werdykt analizy KW (kolumna Status) + wpisy o drogach
  road?: ParcelRoadState; // droga wg KW albo decyzji koordynatora (skrót „droga + >50% udziałów")
  road_override?: boolean | null; // decyzja koordynatora (surowo); null = wg KW
  ownership_code: string;
  // Kanoniczna GRUPA rejestrowa policzona przez backend (jst_assign.ownership_group):
  // „4" z „4", „04", „4.G.379", „IV" i „gmina"; „" gdy kodu nie da się rozszyfrować albo
  // to sama jednostka rejestrowa („G.379"). Kod bywa poprawiany ręcznie z wypisu, więc
  // front NIE parsuje go sam — pyta o tę wartość.
  ownership_group: string;
  // Kategoria uzgodnienia (typ właściciela) policzona przez backend
  // (location_clock.classify_parcel z kodu własności, użytku i właścicieli; ręczne
  // ustawienie = locked). "" = jeszcze niesklasyfikowana. Kanoniczne źródło
  // „prywatna vs urzędowa" (utils.parcelOwnershipSide — lustro services.parcel_inclusion_kind).
  location_category: LocationCategory | "";
  location_category_display: string;
  location_category_locked: boolean;
  area_m2: number | null;
  intersection_length_m: number | null;
  mpzp_designation: string; // przeznaczenie w MPZP (źródło dla #dzialka_mpzp#)
  designed_devices_description: string; // urządzenia projektowane — źródło dla #regulacja_urzadzenia_projektowane#
  existing_devices_description: string; // urządzenia istniejące — źródło dla #regulacja_urzadzenia_istniejace#
  status: ParcelStatus;
  status_display: string;
  status_locked: boolean;
  status_changed_at: string | null; // data ostatniej zmiany statusu (ostatni ProjectParcelEvent)
  access_mode: ParcelAccessMode; // "Tryb pozyskania dostępu" — pozyskanie / powiadomienie / brak
  access_mode_display: string;
  access_not_required: boolean; // derived: access_mode === "not_required" (compat / map styling)
  is_connected_entity: boolean; // działka podmiotu przyłączanego (nie tranzytowa) — inny wzór na mapie
  can_set_connected_entity: boolean; // czy zalogowany (koordynator / dyrektor) może to przestawić
  can_set_road?: boolean; // czy zalogowany (koordynator / dyrektor) rozstrzyga „droga / nie droga"
  comment: string;
  is_manual: boolean;
  in_current_route: boolean;
  is_used: boolean; // trasa przebiega przez działkę / biegnie po jej granicy / stoi na niej obiekt (≠ zamówiona „na zapas")
  usage_override: boolean | null; // ręczne rozstrzygnięcie „wykorzystywana" (bije automat); null = automat
  route_review_needed: boolean; // geometryczna zmiana trasy na działce z postępem → do weryfikacji GK
  route_review_at: string | null;
  // Ręczna blokada automatycznej wysyłki listownej WPW dla całej działki (akcja wpw-mail-block).
  wpw_mail_blocked?: boolean;
  wpw_mail_blocked_reason?: string;
  wpw_mail_blocked_by_name?: string | null;
  wpw_mail_blocked_at?: string | null;
  geometry: GeoJsonGeometry | null;
  owners: ParcelOwner[];
  // Dlaczego automat urzędów („Przydziel urzędy") pomija tę działkę i co zrobić — null,
  // gdy nie ma czego podpowiadać (kod w automacie / prywatna / urząd już przypisany).
  // `owner` = właściciel-instytucja wpisany samą nazwą (bez rekordu urzędu), któremu
  // można od razu przypisać urząd.
  office_hint: ParcelOfficeHint | null;
  owners_agreed: number; // SECURED@current OR NOT_REQUIRED, per owner
  owners_total: number;
  access_summary: ParcelAccessSummary;
  in_field: boolean; // active WPW order on the parcel (terenowe/kompleksowe)
  linked_orders: LinkedOrder[];
  linked_requisitions: LinkedRequisition[];
  formatka_map: ManagedFileMini | null; // plan sytuacyjny (scalony PDF) prywatnego zestawu tej działki
  open_proceedings: ParcelOpenProceeding[]; // niezakończone sprawy na działce → czerwony „!" przy statusie
  wypis_orders: ParcelWypisOrder[]; // zamówienia wypisów EGiB obejmujące działkę (żywe przed anulowanymi)
  mpzp_orders: ParcelMpzpOrder[]; // zamówienia wypisu i wyrysu z MPZP obejmujące działkę (żywe przed anulowanymi)
  // Ręczne zgłoszenie „potrzebny wypis" — wraca działkę do panelu zamawiania mimo wpisanych
  // z ręki właścicieli. Gaśnie samo, gdy wypis do działki trafi.
  wypis_needed: boolean;
  // Powód zgłoszenia — obowiązkowy przy PONOWNYM zamówieniu (działka ma już wypis).
  wypis_needed_reason: string;
  // Ilu właścicieli działki jest nieaktualnych (ponowny wypis) — `owners` ich nie zawiera.
  outdated_owners_total: number;
}

// Tabela statusów działki („Dane ogólne"): jedna komórka na maszynę stanów, w kolejności
// z backendu (parcel_status_matrix.COLUMNS). `status` null = działka nie ma jeszcze
// obiektu tej maszyny (etykieta „brak"); `shared` = obiekt zbiorczy (sprawa na kilka
// działek, wypis na powiat, MZ/MDCP na projekt) — ten sam status widać na każdej z jego
// działek. `tone` steruje kolorem, żeby front nie musiał znać dziesięciu enumów.
export type StatusMatrixTone = "none" | "neutral" | "progress" | "waiting" | "field" | "ok" | "bad";
export type StatusMatrixKey =
  | "owner_titles"
  | "proceeding"
  | "application_item"
  | "official_application"
  | "wypis"
  | "mpzp"
  | "mz"
  | "mdcp"
  | "wpw_order"
  | "wpw_declared";
export interface StatusMatrixCell {
  key: StatusMatrixKey;
  label: string;
  status: string | null;
  status_display: string;
  tone: StatusMatrixTone;
  detail: string;
  shared: boolean;
  ref: { kind: string; id: string; number?: string; proceeding?: string } | null;
  // Grupa kolumn (np. "case" = sprawa + wnioski + WPW obok siebie na wspólnym tle).
  group: string | null;
  // Kolumna wyróżniona (sprawa).
  emphasis: boolean;
}
export interface ParcelStatusMatrix {
  parcel: string;
  columns: StatusMatrixCell[];
}

// Skrót zamówienia wypisu obejmującego działkę — tabela działek pokazuje go, gdy do działki
// nie jest jeszcze podpięty wypis (żeby było widać, czy wypis jest w drodze).
export interface ParcelWypisOrder {
  id: string;
  number: string;
  status: WypisOrderStatus;
  status_display: string;
  created_at: string;
  sent_at: string | null;
  received_at: string | null;
}

// Skrót zamówienia wypisu i wyrysu z MPZP obejmującego działkę — zasila filtr „Wypis MPZP"
// na liście działek (ten sam kształt co ParcelWypisOrder).
export interface ParcelMpzpOrder {
  id: string;
  number: string;
  status: MpzpOrderStatus;
  status_display: string;
  created_at: string;
  sent_at: string | null;
  received_at: string | null;
  /** Wypis pozyskany ręcznie poza ELGW5 (dodany z karty działki samymi plikami). */
  obtained_manually: boolean;
}

// Skrót otwartej sprawy obejmującej działkę (wprost albo przez właściciela) — status działki
// liczy się z tytułów dostępu, więc „Uzgodniona" może współistnieć z wiszącym postępowaniem.
export interface ParcelOpenProceeding {
  id: string;
  scope: ProceedingScope;
  label: string; // nazwa typu sprawy (albo opis, gdy typ „Inny")
}

export interface LinkedOrder {
  link_id: string;
  order_id: string;
  order_number: string | null;
  order_type: OrderType | null;
  order_type_display: string | null;
  order_status: string | null;
  scope_note: string;
}

export interface LinkedRequisition {
  link_id: string;
  requisition_id: string;
  status: string;
  status_display: string;
  contractor_name: string | null;
  wpw_order_type: string;
  order_id: string | null;
  order_number: string | null;
  created_at: string;
}

// ── proceedings (universal correspondence thread) ────────────────────────────────
// Mirrors serializers.ProceedingTypeSerializer.
export interface ProceedingType {
  id: string;
  name: string;
  is_active: boolean;
  /** Typ zakładany przez system (wysyłka wniosku, zlecenie WPW, zawiadomienie/list) —
   *  formularz nowej sprawy domyślnie nie dodaje takiej do ponagleń. */
  automatic: boolean;
}

// Mirrors serializers.ProceedingItemSerializer. One position in a thread (multifile).
// `kind` selects which field group is meaningful.
/** Pismo WYCHODZĄCE z Dziennika (DLC_02), które da się podpiąć pod właściciela jako list:
 * zarejestrowane przez użytkownika („Moje pisma do wysłania") albo księgowane na to
 * przedsięwzięcie, niewycofane i jeszcze niepodpięte pod żadną pozycję sprawy. */
export interface AttachableLetter {
  /** Wpis rejestru („Moje pisma do wysłania") albo zlecenie wydruku z wysyłką. */
  kind: "register" | "print_order";
  id: string;
  label: string;
  counterparty_name: string;
  counterparty_address: string;
  description: string;
  channel: ItemChannel;
  date: string | null;
  status: DispatchStatus | PrintOrderStatus;
  status_display: string;
  tracking_number: string;
  files: ManagedFileMini[];
  /** Doręczenie potwierdzone w Dzienniku (zwrotka / potwierdzenie odbioru). */
  delivered: boolean;
  created_by_name: string;
  created_at: string;
  /** Właściciele TEGO projektu, pod których to pismo już podpięto (jedno pismo bywa listem do
   *  kilku) — tych nie da się wybrać drugi raz. */
  attached_to: LetterRecipientRef[];
  /** Ile razy pismo podpięto w innych projektach. */
  attached_elsewhere: number;
}

export interface LetterRecipientRef {
  owner: string;
  owner_name: string;
  parcel: string;
  parcel_label: string;
}

/** Inny adresat tego samego obiektu z Dziennika — ma własną pozycję i własny wynik listu. */
export interface LetterSharedWith extends LetterRecipientRef {
  same_project: boolean;
  letter_outcome: LetterOutcome;
  letter_outcome_display: string;
}

export type PrintOrderStatus =
  | "pending" | "accepted" | "printed" | "sent" | "returned" | "rejected" | "cancelled";

/** Zlecenie wydruku (DLC_02) podpięte pod list do właściciela — druga postać „obiektu
 * z Dziennika" obok wpisu rejestru; potwierdzenie odbioru = doręczenie. */
export interface PrintOrderBrief {
  id: string;
  number: number;
  name: string;
  status: PrintOrderStatus;
  status_display: string;
  date: string | null;
  tracking_number: string;
  created_by_name: string;
  receipt_confirmation: ManagedFileMini | null;
}

/** Wynik listu do właściciela — krok PO potwierdzeniu doręczenia. */
export type LetterOutcome = "" | "agreement" | "refusal";

export interface ProceedingItem {
  id: string;
  proceeding: string;
  kind: ProceedingItemKind;
  kind_display: string;
  note: string;
  files: ManagedFileMini[];
  created_by_name: string | null;
  created_at: string;
  // APPLICATION (our outgoing letter; DRAFT → APPROVED → SUBMITTED)
  app_status: ApplicationStatus;
  app_status_display: string | null;
  author_name: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  submitted_by_name: string | null;
  submitted_at: string | null;
  channel: ItemChannel;
  channel_display: string | null;
  channel_ref: string;
  our_ref: string;
  date_sent: string | null;
  response_deadline: string | null;
  recipient_owner: string | null; // NOTIFICATION fan-out: the single owner this letter addresses
  recipient_owner_name: string | null;
  paper_dispatch: string | null;
  paper_dispatch_status: DispatchStatus | null;
  paper_dispatch_info: PaperDispatchBrief | null; // pełny stan wpisu w dzienniku DLC_02
  edelivery_info: EdeliveryBrief | null; // przesyłka w kolejce e-Doręczeń (DLC_09)
  paper_dispatch_confirmation: ManagedFileMini | null; // zwrotka filed on the DLC_02 entry (preview)
  // LETTER confirmation (potwierdzenie listu) — koordynator projektu / dyrektor techniczny
  // records that a scope=LETTER letter reached the owner; never resolves the uzgodnienie.
  letter_confirmed_at: string | null;
  letter_confirmed_by_name: string | null;
  letter_confirmation_note: string;
  /** Ręczne „Potwierdź list" — TYLKO list bez obiektu w Dzienniku i z uprawnieniem
   * (koordynator / dyrektor techniczny / CEO); list z wpisem rejestru lub zleceniem wydruku
   * potwierdza wyłącznie Dziennik (letter_delivered). */
  can_confirm_letter: boolean;
  /** Wynik listu (uzgodnienie / odmowa) — same uprawnienia, niezależnie od obiektu w Dzienniku
   * i od zwrotki (wynik wpisuje się od razu; opcjonalnie z pismem z uzgodnieniem / odmową). */
  can_set_letter_outcome: boolean;
  print_order: string | null;
  print_order_info: PrintOrderBrief | null;
  /** Doręczenie potwierdzone w Dzienniku (wpis „doręczone" / potwierdzenie odbioru na
   * zleceniu wydruku) — równoważne ręcznemu „Potwierdź list". Fakt organizacyjny, niezależny
   * od wyniku listu (zwrotka może przyjść później albo wcale). */
  letter_delivered: boolean;
  letter_outcome: LetterOutcome;
  letter_outcome_display: string;
  letter_outcome_at: string | null;
  letter_outcome_by_name: string | null;
  letter_outcome_note: string;
  /** List PODPIĘTY z Dziennika (nie wysłany z systemu) — tylko taki da się odpiąć. */
  attached_existing: boolean;
  letter_shared_with: LetterSharedWith[];
  /** „Odepnij" — podpięty, bez wyniku, użytkownik z bramką wysyłki listów. */
  can_detach_letter: boolean;
  // RESPONSE (incoming reply)
  direction: LetterDirection;
  office_ref: string;
  date_received: string | null;
  date_delivered: string | null;
  closes_thread: boolean;
  outcome: ItemOutcome;
  outcome_display: string | null;
  resolved_owner_ids: string[];
  /** Odpowiedź jako dokument tytułów wpisanych przez zamknięcie sprawy urzędowej:
   * "set" — skan leży na tytułach; "available" — można go ustawić („Ustaw jako dokument
   * tytułu”); null — nie dotyczy. */
  title_document: "set" | "available" | null;
  // EVENT (telefon / wyjazd / mail / inne)
  event_type: ProceedingEventType;
  event_type_display: string | null;
  // Pakiet z modułu Wnioski stojący za tą pozycją „wniosek" (założony przy wysyłce albo
  // podpięty pod istniejącą pozycję). null = pismo bez pakietu (wysłane z Korespondencji).
  official_application: ProceedingPackageBrief | null;
  // Decyzje z rejestru zarejestrowane z tej pozycji „odpowiedź" / podpięte pod nią.
  decisions: {
    id: string; sign: string; kind: DecisionKind; kind_display: string;
    finality: DecisionFinality; finality_display: string;
  }[];
}

// Skrót pakietu z modułu Wnioski przy sprawie / pozycji sprawy.
export interface ProceedingPackageBrief {
  id: string;
  number: string;
  status: OfficialAppStatus;
  application_type: string;
  type_label: string;
  proceeding_item: string | null;
}

// Mirrors serializers.ProceedingSerializer. A thread of applications/responses/events.
// OFFICE threads resolve owners (decyzja art.124) and may span owners from many działki
// (wniosek zbiorczy); TECHNICAL threads are project-level and do not touch parcel status.
// Zamówienie spięte ze sprawą (OrderProceedingLink): zlecenie WPW prowadzące sprawę
// (auto, zakładane przy tworzeniu zamówienia) albo dowolne zamówienie projektu
// powiązane ręcznie z zakładki „Sprawy".
export interface ProceedingOrderBrief {
  link_id: string;
  order_id: string;
  order_number: string;
  order_type: string;
  order_type_display: string;
  status: string;
  status_display: string;
  contractor_name: string | null;
  is_wpw: boolean;
  auto: boolean;
  /** Zakończone zlecenie WPW pod otwartą sprawą „owner" (podpięte przed automatem) —
   *  „Zamknij sprawę" przy zleceniu domyka ją wynikiem z uzgodnień / odmów. */
  case_left_open?: boolean;
}

// GET proceedings/{id}/link-candidates/ — co da się jeszcze podpiąć pod sprawę.
export interface ProceedingLinkCandidates {
  orders: (Omit<ProceedingOrderBrief, "link_id" | "auto" | "case_left_open"> & {
    /** Zakończone zlecenie WPW: podpięcie od razu zamknie sprawę „owner". */
    closes_case: boolean;
  })[];
  applications: (ProceedingPackageBrief & {
    status_display: string;
    wpw_parcel_teryt: string | null;
    wpw_owner_name: string | null;
    /** Pakiet zakończony (nie WPW): podpięcie zamknie sprawę z rozstrzygnięciem. */
    closes_case: boolean;
    /** Pakiet niezakończony, sprawa zamknięta: podpięcie otworzy ją ponownie (sam
     *  status) — tylko po potwierdzeniu (`reopen_case`). */
    reopens_case: boolean;
  })[];
}

export interface Proceeding {
  id: string;
  connection: string;
  scope: ProceedingScope;
  scope_display: string;
  type: string;
  type_name: string;
  type_note: string;
  recipient_office: string | null;
  office_name: string | null;
  owners: string[];
  owner_ids: string[];
  owners_label: string;
  // Właściciele sprawy, którzy po ponownym wypisie są nieaktualni — sprawa zostaje
  // widoczna, ale jej zamknięcie/anulowanie nie wpływa na działkę bez aktualnego
  // właściciela w sprawie.
  outdated_owner_ids: string[];
  parcels: string[];
  parcel_ids: string[];
  /** Rozstrzygnięcie częściowe: działki uzgodnione wskazane przy zamknięciu. */
  resolved_parcel_ids: string[];
  route_version: string | null;
  route_version_label: string | null;
  status: ProceedingStatus;
  status_display: string;
  outcome: ProceedingOutcome;
  outcome_display: string;
  closed_at: string | null;
  closed_by: string | null;
  closed_by_name: string | null;
  // Conditions the office attached to its decision (persisted from the close note).
  closing_note: string;
  kpa_days: number;
  kpa_deadline: string | null; // last SUBMITTED application date + kpa_days
  kpa_overdue: boolean;
  kpa_deadline_status: DeadlineStatus;
  // May the current user delete the whole case (koordynator przedsięwzięcia / dyrektor)?
  // Backend blockers (pismo w DLC_02/DLC_09, zabezpieczone tytuły, wniosek) come back
  // as an error message on the DELETE itself.
  can_delete: boolean;
  // Pakiety z modułu Wnioski podpięte do sprawy. Sprawa i pakiet domykają się RAZEM:
  // zamknięcie sprawy kończy złożony pakiet, ponowne otwarcie przywraca „złożony".
  // `parcels` = zakres pakietu (osobna relacja niż działki sprawy) — front porównuje
  // z parcel_ids sprawy i ostrzega o niezgodnym zakresie. Pakiet zakotwiczony na działce
  // (wpw_parcel: pakiet WPW, art. 124) jest per (działka, właściciel) i nigdy nie obejmuje
  // całej sprawy — dla niego sprawdza się tylko ZAWIERANIE działki i właściciela w sprawie.
  official_applications: (ProceedingPackageBrief & {
    parcels: { id: string; teryt: string }[];
    wpw_parcel: string | null;
    wpw_owner: string | null;
    wpw_owner_name: string | null;
    /** Pakiet zakończony, sprawa otwarta (para sprzed synchronizacji 2026-09-02) —
     *  „Zamknij sprawę" przy pakiecie domyka ją z rozstrzygnięciem. */
    case_left_open?: boolean;
    /** Pakiet jeszcze niezłożony (nie WPW) — sprawa nie zamknie się, dopóki nie wyjdzie. */
    blocks_close?: boolean;
    /** Sprawę założyła wysyłka TEGO pakietu — jego odpięcie usuwa sprawę (delete_case). */
    founded_case?: boolean;
  })[];
  orders: ProceedingOrderBrief[];
  // Sprawa założona przez system (nie ręcznie w Korespondencji): przy wysyłce wniosku albo
  // przy zleceniu WPW. `label` = „przy wysyłce wniosku WNU/…" / „przy zleceniu WPW ZAM/…".
  auto_origin?:
    | { kind: "application"; application_id: string; application_number: string; label: string }
    | { kind: "wpw_order"; order_id: string; order_number: string; label: string }
    | null;
  note: string;
  items: ProceedingItem[];
  /** Kalendarz ponagleń: kto dzwoni w tej sprawie; null = sprawa poza ponagleniami. */
  control_call_user: string | null;
  control_call_user_name: string;
  created_at: string;
  updated_at: string;
}

// ── per-project access permissions (approve / submit gate) ────────────────────────
// Mirrors serializers.ProjectAccessPermissionSerializer. Managed by a director in "Metadane".
export interface ProjectAccessPermission {
  id: string;
  connection: string;
  user: string;
  user_name: string | null;
  can_approve: boolean; // gate for APPROVE on applications
  can_submit: boolean; // gate for SUBMIT on applications
  created_at: string;
}

export interface Office {
  id: string;
  name: string;
  kind: string;
  kind_display: string;
  // Structured postal address (mail recipient — DLC_02 obieg dokumentów).
  street: string;
  building_no: string;
  postal_code: string;
  city: string;
  full_address: string;
  department: string;
  contact_person: string;
  phone: string;
  email: string;
  // Adres do doręczeń elektronicznych (DLC_09). Puste = urząd trzeba obsłużyć papierowo.
  edelivery_address: string;
  bank_account: string;
  notes: string;
  is_active: boolean;
  created_by?: string | null;
  // Czy bieżący użytkownik może poprawić / scalić / usunąć wpis — podwykonawca tylko własne.
  can_edit?: boolean;
}

// Mirrors apps.offices.models.OfficeKind — keep values in sync with the backend.
export const OFFICE_KIND_OPTIONS: { value: string; label: string }[] = [
  { value: "gmina", label: "Urząd gminy / miasta" },
  { value: "starostwo", label: "Starostwo powiatowe" },
  { value: "konserwator", label: "Konserwator zabytków" },
  { value: "archeolog", label: "Archeolog / WUOZ" },
  { value: "gestor", label: "Gestor sieci" },
  { value: "pkp", label: "PKP / kolej" },
  { value: "wody", label: "Wody Polskie / WP" },
  { value: "zarzadca_drogi", label: "Zarządca drogi" },
  { value: "inny", label: "Inny" },
];

// ── DLC_02 — firmowy dziennik korespondencji (CorrespondenceRegister) ─────────────
// Mirrors dlc_02_paper_send.serializers.CorrespondenceRegisterEventSerializer.
export interface CorrespondenceRegisterEvent {
  id: string;
  from_status: DispatchStatus | "";
  to_status: DispatchStatus;
  note: string;
  changed_at: string;
  changed_by_name: string;
  // Copied from a split's source entry — rendered collapsed in the history modal.
  inherited: boolean;
}

// Mirrors dlc_02_paper_send.serializers.CorrespondenceRegisterSerializer. A single
// nadawczo-odbiorczy register row in either direction over any channel.
export interface CorrespondenceRegister {
  id: string;
  direction: RegisterDirection;
  direction_display: string;
  channel: RegisterChannel;
  channel_display: string;
  source_app: string;
  source_id: string | null;
  source_label: string;
  counterparty_name: string;
  counterparty_address: string;
  date: string | null;
  files: ManagedFileMini[];
  status: DispatchStatus;
  status_display: string;
  tracking_number: string;
  confirmation_file: ManagedFileMini | null;
  target_users: string[];
  target_user_names: string[];
  // Adresaci, którzy już potwierdzili odbiór. Dodanie adresata po potwierdzeniu otwiera wpis
  // ponownie (acknowledged → filed), ale te osoby nie są pytane o odbiór drugi raz.
  acknowledged_by: string[];
  acknowledged_by_names: string[];
  target_project: string | null;
  target_project_name: string | null;
  // Przedsięwzięcie ma projekt ZE — tylko wtedy nazwa linkuje do /power-projects/:id.
  target_project_has_ze: boolean;
  // Projekt prowadzony przez firmę zewnętrzną: dekretacja kieruje pismo do jej rejestru spraw.
  led_contractor_name: string | null;
  // Numer pozycji w rejestrze firmy, gdy pismo zostało już przekazane.
  led_register_label: string | null;
  handled_by: string | null;
  handled_by_name: string | null;
  // Author of the entry (first event) — powers samodekretacja of self-registered letters
  // and the "Autor" column of the register.
  registered_by: string | null;
  registered_by_name: string | null;
  note: string;
  qr_scan_status: "" | "pending" | "matched" | "unmatched" | "failed";
  qr_suggestion: QrSuggestion | Record<string, never>;
  events: CorrespondenceRegisterEvent[];
  created_at: string;
  updated_at: string;
}

// Lekki słownik przedsięwzięć dla formularzy dziennika (project-options/) — ten sam
// zbiór co portfolio ZE, ale dostępny dla każdego użytkownika DLC_02.
export interface RegisterProjectOption {
  project_id: string;
  project_name: string;
  coordinator_ids: string[];
  /** Firma prowadząca projekt — dekretacja na taki projekt idzie do jej rejestru spraw. */
  led_contractor_name: string | null;
}

// Snapshot sugestii dekretacji z rozpoznanego kodu QR (dlc_01 wnioski.qr_stamp) —
// read-only, wypełniany przez task; nigdy nie dekretuje sam.
export interface QrSuggestion {
  source_app: string;
  payload: string;
  project_id: string | null;
  project_name: string;
  application_id: string | null;
  application_number: string;
  application_type: string;
  proceeding_id: string | null;
  route_version_id: string | null;
  route_version_label: string;
  current_route_version_id: string | null;
  current_route_version_label: string;
  proceeding_route_version_id: string | null;
  route_current: boolean | null;
  route_scope_diff: {
    changed: boolean | null;
    entries: { teryt: string; label: string; kind: "entered" | "left" | "moved"; from_m: number | null; to_m: number | null }[];
    summary: string;
  } | null;
}

export interface TaskResultFile {
  id: string | null;
  filename: string;
  url: string;
  size_bytes: number | null;
}

export interface AsyncTaskRecord {
  id: string;
  status: "pending" | "running" | "success" | "failure" | "cancelled";
  log: string;
  result: unknown;
  result_file: TaskResultFile | null;
  error: string;
}

// ── nakładanie działek po „Przelicz działki (ULDK)" (zakładka mapy) ─────────────
// Zadanie z check_overlaps próbkuje całą trasę i porównuje działki z ULDK z istniejącymi.
// Działka o INNYM numerze nakładająca się na istniejącą (podział/scalenie w ewidencji) nie
// jest dodawana — wraca w wyniku jako grupa do decyzji użytkownika (jedna decyzja na grupę).
export interface ParcelConflictExisting {
  id: string;
  teryt: string;
  short_number: string;
  area_m2: number | null;
  status: ParcelStatus;
  in_current_route: boolean;
  is_manual: boolean;
  usage_override: boolean | null;
  owners_count: number;
  has_progress: boolean;
}

export interface ParcelConflictIncoming {
  teryt: string;
  short_number: string;
  area_m2: number | null;
  intersection_length_m: number | null;
  /** Już w projekcie (np. dodana ręcznie w zakładce działek) — „dodaj" nic dla niej nie zmienia. */
  in_project: boolean;
  parcel_id: string | null;
}

export interface ParcelConflictGroup {
  id: string;
  existing: ParcelConflictExisting[];
  incoming: ParcelConflictIncoming[];
  overlap_m2: number;
}

export type ParcelConflictAction = "keep_old" | "replace" | "add_keep_unused";

export interface ParcelConflictDecision {
  existing_ids: string[];
  incoming_teryts: string[];
  action: ParcelConflictAction;
}

export interface ParcelConflictResolution {
  applied: number;
  added: string[];
  deleted: string[];
  marked_unused: string[];
  kept: string[];
}

// ── labels & option lists (PL UI) ────────────────────────────────────────────────
export const PARCEL_STATUS_COLOR: Record<ParcelStatus, string> = {
  not_started: "#9ca3af",
  in_progress: "#3b82f6",
  action_required: "#ea580c",
  application_sent: "#f59e0b",
  negotiator_sent: "#3b82f6",
  ok: "#059669",
  problem: "#ef4444",
};

export const PARCEL_STATUS_LABEL: Record<ParcelStatus, string> = {
  not_started: "Nie rozpoczęto",
  in_progress: "W trakcie",
  action_required: "W trakcie – wymagane działanie",
  application_sent: "Wniosek wysłany",
  negotiator_sent: "Negocjator wysłany",
  ok: "Uzgodniona",
  problem: "Problem",
};

// ── access mode (three-way "what does this parcel need?") ─────────────────────────
export const PARCEL_ACCESS_MODE_LABEL: Record<ParcelAccessMode, string> = {
  consent: "Wymaga pozyskania",
  notification: "Wymaga powiadomienia",
  not_required: "Nie wymaga pozyskania ani powiadomienia",
};

// Short badge form used on the Działki list.
export const PARCEL_ACCESS_MODE_BADGE: Record<ParcelAccessMode, string> = {
  consent: "Pozyskanie",
  notification: "Powiadomienie",
  not_required: "Niewymagane",
};

export const PARCEL_ACCESS_MODE_OPTIONS: { value: ParcelAccessMode; label: string }[] = [
  { value: "consent", label: PARCEL_ACCESS_MODE_LABEL.consent },
  { value: "notification", label: PARCEL_ACCESS_MODE_LABEL.notification },
  { value: "not_required", label: PARCEL_ACCESS_MODE_LABEL.not_required },
];

// ── access titles (OwnerAccess) ──────────────────────────────────────────────────
export const OWNER_ACCESS_STATUS_LABEL: Record<OwnerAccessStatus, string> = {
  not_started: "Nie rozpoczęto",
  in_progress: "W trakcie",
  secured: "Pozyskany",
  refused: "Odmowa",
  not_required: "Niewymagany",
  problem: "Problem (teren)",
};

export const OWNER_ACCESS_STATUS_COLOR: Record<OwnerAccessStatus, string> = {
  not_started: "#9ca3af",
  in_progress: "#3b82f6",
  secured: "#059669",
  refused: "#ef4444",
  not_required: "#6b7280",
  problem: "#ef4444",
};

// ── proceedings ──────────────────────────────────────────────────────────────────
export const PROCEEDING_SCOPE_LABEL: Record<ProceedingScope, string> = {
  office: "Urzędowe - uzgodnienie lokalizacji",
  owner: "WPW - uzgodnienie właściciel prywatny",
  technical: "Urzędowe/inne - techniczne i pozostałe",
  notification: "Zawiadomienie właścicieli (demontaż)",
  letter: "List do właściciela (pozyskanie)",
};

export const PROCEEDING_SCOPE_OPTIONS: { value: ProceedingScope; label: string }[] = [
  { value: "office", label: PROCEEDING_SCOPE_LABEL.office },
  { value: "owner", label: PROCEEDING_SCOPE_LABEL.owner },
  { value: "technical", label: PROCEEDING_SCOPE_LABEL.technical },
];

export const PROCEEDING_STATUS_LABEL: Record<ProceedingStatus, string> = {
  open: "Otwarte",
  closed: "Zamknięte",
};

export const PROCEEDING_OUTCOME_LABEL: Record<ProceedingOutcome, string> = {
  none: "Bez rozstrzygnięcia",
  positive: "Pozytywny",
  negative: "Negatywny",
  partial: "Częściowy",
};

export const PROCEEDING_OUTCOME_OPTIONS: { value: ProceedingOutcome; label: string }[] = [
  { value: "positive", label: "Pozytywny (zgoda / decyzja pozytywna)" },
  { value: "negative", label: "Odmowny (decyzja odmowna)" },
  { value: "partial", label: "Częściowy (część właścicieli)" },
  { value: "none", label: "Bez rozstrzygnięcia" },
];

export const PROCEEDING_ITEM_KIND_LABEL: Record<ProceedingItemKind, string> = {
  application: "Wniosek (nasz)",
  response: "Odpowiedź",
  event: "Zdarzenie",
};

export const APPLICATION_STATUS_LABEL: Record<Exclude<ApplicationStatus, "">, string> = {
  draft: "Szkic",
  approved: "Zatwierdzony",
  submitted: "Złożony / wysłany",
  completed: "Zakończony",
  withdrawn: "Wycofany",
};

export const ITEM_CHANNEL_LABEL: Record<Exclude<ItemChannel, "">, string> = {
  paper: "Papier (DLC_02)",
  edoreczenia: "e-Doręczenia",
  mail: "E-mail",
  other: "Inne",
};

export const ITEM_CHANNEL_OPTIONS: { value: Exclude<ItemChannel, "">; label: string }[] = [
  { value: "paper", label: ITEM_CHANNEL_LABEL.paper },
  { value: "edoreczenia", label: ITEM_CHANNEL_LABEL.edoreczenia },
  { value: "mail", label: ITEM_CHANNEL_LABEL.mail },
  { value: "other", label: ITEM_CHANNEL_LABEL.other },
];

export const ITEM_OUTCOME_OPTIONS: { value: ItemOutcome; label: string }[] = [
  { value: "positive", label: "Pozytywne" },
  { value: "negative", label: "Negatywne" },
  { value: "partial", label: "Częściowe" },
  { value: "none", label: "Brak / korespondencja" },
];

/** Zamknięcie sprawy z rozstrzygnięciem — okno „Zamknij sprawę", „Zakończ" wniosku i
 *  podpięcie zakończonego pakietu. `resolved_parcels` = działki uzgodnione (częściowe). */
export type CaseCloseValues = {
  outcome: string;
  resolved_parcels?: string[];
  note?: string;
  /** Odpowiedź tej sprawy, której skan staje się dokumentem wpisanych tytułów. */
  title_item?: string;
};

/** GET proceedings/{id}/resolution-parcels/ — działki sprawy do wskazania przy
 *  rozstrzygnięciu częściowym; sprawa WPW podpowiada zaznaczenie z danych zlecenia. */
export interface ResolutionParcelOwner {
  id: string;
  full_name: string;
  /** null = sprawa bez podpowiedzi (nie WPW). */
  agreed: boolean | null;
  reason: string;
}

export interface ResolutionParcel {
  id: string;
  teryt: string;
  obreb: string;
  owners: ResolutionParcelOwner[];
  suggested: boolean;
  selected: boolean;
}

export interface ResolutionParcels {
  suggestion_source: { kind: string; label: string } | null;
  parcels: ResolutionParcel[];
}

export const PROCEEDING_EVENT_TYPE_LABEL: Record<Exclude<ProceedingEventType, "">, string> = {
  telefon: "Telefon",
  wyjazd: "Wyjazd w teren",
  mail: "E-mail",
  inne: "Inne",
};

export const PROCEEDING_EVENT_TYPE_OPTIONS: { value: Exclude<ProceedingEventType, "">; label: string }[] = [
  { value: "telefon", label: PROCEEDING_EVENT_TYPE_LABEL.telefon },
  { value: "wyjazd", label: PROCEEDING_EVENT_TYPE_LABEL.wyjazd },
  { value: "mail", label: PROCEEDING_EVENT_TYPE_LABEL.mail },
  { value: "inne", label: PROCEEDING_EVENT_TYPE_LABEL.inne },
];

// ── orders ────────────────────────────────────────────────────────────────────
export const ORDER_TYPE_LABEL: Record<OrderType, string> = {
  standard: "Standardowe",
  wpw_terenowe: "WPW terenowe (tylko teren)",
  wpw_kompleksowe: "WPW kompleksowe (pełne pozyskanie, art.124)",
};

// Only the WPW kinds are linkable to a działka from the parcel drawer.
export const WPW_ORDER_TYPE_OPTIONS: { value: OrderType; label: string }[] = [
  { value: "wpw_terenowe", label: ORDER_TYPE_LABEL.wpw_terenowe },
  { value: "wpw_kompleksowe", label: ORDER_TYPE_LABEL.wpw_kompleksowe },
];

// ── DLC_02 register ──────────────────────────────────────────────────────────────
export const REGISTER_DIRECTION_LABEL: Record<RegisterDirection, string> = {
  outgoing: "Wychodzące",
  incoming: "Przychodzące",
};

export const REGISTER_CHANNEL_LABEL: Record<RegisterChannel, string> = {
  paper: "Papier",
  edoreczenia: "e-Doręczenia",
  mail: "E-mail",
  other: "Inne",
};

export const REGISTER_CHANNEL_OPTIONS: { value: RegisterChannel; label: string }[] = [
  { value: "paper", label: REGISTER_CHANNEL_LABEL.paper },
  { value: "edoreczenia", label: REGISTER_CHANNEL_LABEL.edoreczenia },
  { value: "mail", label: REGISTER_CHANNEL_LABEL.mail },
  { value: "other", label: REGISTER_CHANNEL_LABEL.other },
];

export const DISPATCH_STATUS_LABEL: Record<DispatchStatus, string> = {
  queued: "W kolejce",
  prepared: "Przygotowane",
  sent: "Wysłane",
  delivered: "Doręczone",
  returned: "Zwrot",
  undeliverable: "Niedoręczalne",
  received: "Zarejestrowane (wpływ)",
  filed: "Zadekretowane",
  acknowledged: "Potwierdzono odbiór",
  cancelled: "Anulowane",
};

// ── KPI (recurring per-project review meetings) ─────────────────────────────────
// Mirror apps.dlc_01_power_project.models.{KpiReview,KpiActionItem}.
export type KpiReviewStatus = "planned" | "held" | "cancelled";
export type KpiActionStatus = "open" | "in_progress" | "waiting" | "done" | "cancelled";
export type KpiActionPriority = "low" | "medium" | "high";
// Skąd wzięło się zadanie — decyduje MIEJSCE dodania (karta spotkania vs drzewo/lista).
export type KpiActionOrigin = "kpi" | "self";

export interface KpiActionItem {
  id: string;
  review: string; // origin KPI (where planned)
  review_sequence_no: number; // "z KPI #N"
  parent: string | null; // nesting: a task with children doubles as a bucket
  description: string;
  assignee: string | null;
  assignee_name: string | null;
  due_date: string | null;
  week_start: string | null; // due_date snapped to its Monday — the month/week group key
  status: KpiActionStatus;
  status_display: string;
  priority: KpiActionPriority;
  origin: KpiActionOrigin;
  done_at: string | null;
  done_by_name: string | null;
  done_review_sequence_no: number | null; // "po którym KPI" (last held when ticked off)
  order: number; // urgency rank among its OWN siblings (0 = top)
  comments_count: number;
  created_by_name: string | null;
  // Dzwoneczek dyrekcji: ile razy zadanie ponaglano i kto zrobił to ostatni. Licznik jest
  // śladem, nie hamulcem — limitów klikania świadomie nie ma.
  ping_count: number;
  last_ping: { at: string; by_name: string } | null;
  // Autorem jest CEO / dyrektor techniczny. Sam AUTOR — drugą drogę do „ustalenia dyrekcji"
  // niesie `origin`; łączy je isDirectorateKpiItem() z utils.
  by_directorate: boolean;
  created_at: string;
  updated_at: string;
}

/** Jedno ponaglenie pod zadaniem KPI — pozycja historii na karcie zadania. */
export interface KpiItemPing {
  id: string;
  at: string;
  by_name: string;
  recipients: string[];
  /** Stan maila z aron@ — powiadomienie w systemie idzie zawsze, niezależnie od poczty. */
  mail_status: "queued" | "sent" | "failed";
  mail_status_display: string;
}

export interface KpiReview {
  id: string;
  connection: string;
  sequence_no: number;
  scheduled_for: string;
  scheduled_deadline_status: DeadlineStatus;
  status: KpiReviewStatus;
  status_display: string;
  held_at: string | null;
  attendees: string[];
  attendees_names: { id: string; name: string }[];
  facilitator: string | null;
  facilitator_name: string | null;
  agenda: string;
  summary: string;
  // Frozen ProgressService snapshot at close (+ attendees/facilitator names); {} while planned.
  progress_snapshot: Partial<Progress> & { attendees?: string[]; facilitator?: string | null };
  suggested_next_date: string | null;
  action_items: KpiActionItem[];
  comments_count: number;
  created_at: string;
  updated_at: string;
}

export interface KpiComment {
  id: string;
  review: string;
  author_id: string | null;
  author_name: string;
  author_profile_picture_url: string | null;
  content: string;
  is_edited: boolean;
  mentions: { id: string; full_name: string }[];
  created_at: string;
  updated_at: string;
}

export const KPI_REVIEW_STATUS_LABEL: Record<KpiReviewStatus, string> = {
  planned: "Zaplanowane",
  held: "Odbyte",
  cancelled: "Odwołane",
};

export const KPI_ACTION_STATUS_LABEL: Record<KpiActionStatus, string> = {
  open: "Do zrobienia",
  in_progress: "W trakcie",
  waiting: "Wstrzymane",
  done: "Zrobione",
  cancelled: "Anulowane",
};

// ── WPW handover (działka | WPW) — negotiator-declared reality per (order × parcel) ──
export type WpwDeclaredStatus =
  | "not_started" | "in_progress" | "collected_in_field" | "problem_refusal"
  | "problem_impossible" | "consent_written" | "decision_art124";

export const WPW_DECLARED_STATUS_LABEL: Record<WpwDeclaredStatus, string> = {
  not_started: "Nie rozpoczęto",
  in_progress: "W trakcie",
  collected_in_field: "Zebrano w terenie",
  problem_refusal: "Problem (odmowa/żądania)",
  problem_impossible: "Problem (śmierć/niemożliwe zebranie podpisu)",
  consent_written: "Pozyskano zgodę pisemną",
  decision_art124: "Pozyskano decyzję art. 124 UGN",
};

export const WPW_DECLARED_STATUS_COLOR: Record<WpwDeclaredStatus, string> = {
  not_started: "#6b7280",
  in_progress: "#d97706",
  collected_in_field: "#0284c7",
  problem_refusal: "#dc2626",
  problem_impossible: "#dc2626",
  consent_written: "#059669",
  decision_art124: "#059669",
};

// Acquisition statuses: the negotiator must attach a document, and GK can confirm them to
// promote into the formal access model ("zatwierdź i przenieś"). Keep in sync with backend
// models.WPW_ACQUISITION_STATUSES.
export const WPW_ACQUISITION_STATUSES: WpwDeclaredStatus[] = ["consent_written", "decision_art124"];
// Statuses the negotiator may actively select (NOT_STARTED is only the initial value).
export const WPW_SELECTABLE_STATUSES: WpwDeclaredStatus[] = [
  "in_progress", "collected_in_field", "problem_refusal", "problem_impossible",
  "consent_written", "decision_art124",
];
// „Zebrano w terenie": signatures collected, the scan not uploaded yet — GK has nothing to
// accept; the negotiator's first upload moves it to „Pozyskano zgodę pisemną" (backend).
export const WPW_AWAITING_FILE_STATUS: WpwDeclaredStatus = "collected_in_field";
// Problem statuses: declaring one requires a reason (modal → `note`, stored as the
// STATUS_CHANGE event body). Keep in sync with backend models.WPW_PROBLEM_STATUSES.
export const WPW_PROBLEM_STATUSES: WpwDeclaredStatus[] = ["problem_refusal", "problem_impossible"];

export type WpwEventKind =
  | "status_change" | "comment" | "file_added" | "file_replaced" | "file_removed" | "confirmation"
  | "ai_analysis"
  // gk_docs (pliki pakietu przekazanego w teren) — nazwy plików jadą w `body`
  | "gk_doc_added" | "gk_doc_removed";

export type WpwDecision = "confirmed" | "rejected" | "needs_info" | "revoked";

export const WPW_DECISION_LABEL: Record<WpwDecision, string> = {
  confirmed: "Zaakceptowano przez GK",
  rejected: "Odrzucono przez GK",
  needs_info: "Wymaga uzupełnienia",
  revoked: "Cofnięto zatwierdzenie",
};

export interface WpwHandoverFile {
  id: string;
  original_name: string;
  state: "active" | "superseded" | "removed";
  url: string | null;
  uploaded_by_name: string | null;
  // Porozumienie (doc_kind="porozumienie") ma per-plikową decyzję GK (zatwierdzane razem ze zgodą,
  // odrzucane osobno); inne pliki mają review_status="" — chyba że GK odrzuci zgodę: wtedy każdy
  // aktywny plik dostaje "rejected" (bez osobnej odznaki w UI — odrzucenie widać w pasku
  // decyzji) i nie liczy się już jako dokument statusu pozyskania.
  doc_kind: "other" | "porozumienie";
  review_status: "" | "pending" | "approved" | "rejected";
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  review_note: string;
  created_at: string;
  removed_at: string | null;
  removed_by_name: string | null;
  superseded_by: string | null;
}

// Jedna uwaga z analizy AI dokumentów wgranych przez negocjatora. Uwagi widzą OBIE strony
// (także podwykonawca), zawsze z adnotacją, że wygenerowało je AI — nic nie zmieniają
// automatycznie, decyzję o przyjęciu porozumienia i tak podejmuje człowiek.
export type WpwAiSeverity = "critical" | "warning" | "info";

export const WPW_AI_SEVERITY_LABEL: Record<WpwAiSeverity, string> = {
  critical: "Poważna nieprawidłowość",
  warning: "Uwaga",
  info: "Informacja",
};

export interface WpwAiFinding {
  severity: WpwAiSeverity;
  // Czego uwaga dotyczy — zamknięta lista po stronie backendu (praca negocjatora w terenie).
  // Uwagi bez kategorii z tej listy w ogóle nie wychodzą z analizy.
  category?: string;
  title: string;
  detail: string;
  file_name?: string;
}

export type WpwAgreementAiStatus = "pending" | "running" | "ok" | "warning" | "error";

// Co model PRZEPISAŁ ze skanu, zanim wydał osąd — osoby z komparycji, każdy odręczny
// podpis (strona, miejsce, litery, czyj), data. Uwagi o brakach są na backendzie bramkowane
// tym zestawem; w UI pokazujemy go, żeby człowiek mógł w sekundę sprawdzić podstawę uwagi.
export interface WpwAiEvidence {
  persons_named?: { name: string; address_as_written: string; pesel_as_written: string }[];
  signatures?: { page: number; where: string; letters_read: string; person: string; on_map: boolean }[];
  signing_date_as_written?: string;
}

// Tylko critical/warning wymagają działania — `info` to kontekst (np. „zgodność potwierdzona",
// „do sprawdzenia w kartotece GK"). Info nie liczy się do odznaki i nie zapala statusu.
export function actionableAiFindings(findings: WpwAiFinding[]): WpwAiFinding[] {
  return findings.filter((f) => f.severity !== "info");
}

export interface WpwAgreementAiAnalysis {
  status: WpwAgreementAiStatus;
  status_display: string;
  summary: string;
  findings: WpwAiFinding[];
  evidence?: WpwAiEvidence;
  documents_readable: boolean;
  analyzed_files: { id: string; name: string; doc_kind: string }[];
  analyzed_at: string | null;
  // Po analizie doszły nowe pliki → wynik nie obejmuje kompletu.
  is_stale: boolean;
  requested_by_name: string | null;
  // Tylko dla użytkowników wewnętrznych (podwykonawca dostaje sam komunikat błędu).
  model_used?: string;
  error_message?: string;
  expected_owner?: string;
  expected_parcel?: string;
  owner_name_found?: string;
  parcel_number_found?: string;
}

export interface WpwHandoverEvent {
  id: string;
  kind: WpwEventKind;
  kind_display: string;
  from_status: string;
  from_status_display: string;
  to_status: string;
  to_status_display: string;
  body: string;
  attachment: string | null;
  attachment_name: string | null;
  decision: WpwDecision | "";
  decision_display: string;
  target_event: string | null;
  author_name: string;
  author_side: "negotiator" | "internal";
  changed_at: string;
}

export interface WpwHandover {
  id: string; // WpwOwnerHandover id (one per owner)
  // null = WPW „nie ma w żadnym zamówieniu" (wniosek o zmianę usunął je z ostatniego
  // zamówienia) — szczegóły w `detached`; dodanie do zamówienia przypina ten wiersz z powrotem.
  order: {
    id: string;
    order_number: string | null;
    order_type: string;
    order_type_display: string;
    status: string;
    status_display: string;
    contractor: string | null;
  } | null;
  detached?: {
    at: string | null;
    by: string | null;
    from_order_id: string | null;
    from_order_number: string | null;
    from_contractor: string | null;
  } | null;
  owner: { id: string; full_name: string; kind: string; kind_display: string     // nieaktualny (ponowny wypis) — wiersz widoczny tylko po „pokaż nieaktualnych"
    outdated_at?: string | null;
    outdated_reason?: string;
  };
  parcel: { id: string; teryt: string; short_number: string; obreb: string; connection_id: string };
  declared_status: WpwDeclaredStatus;
  declared_status_display: string;
  declared_status_at: string | null;
  scope_note: string;
  attachments: WpwHandoverFile[];
  gk_docs: ManagedFileMini[]; // dokumenty wejściowe od GK (obowiązkowe przy tworzeniu, aktualizowalne)
  gk_docs_updated: boolean; // GK zaktualizował po ostatnim przejrzeniu → badge „Zaktualizowana"
  gk_docs_updated_at: string | null;
  gk_docs_seen_at: string | null;
  current_confirmation: {
    decision: WpwDecision;
    decision_display: string;
    decided_by: string | null;
    decided_at: string;
    note: string;
    target_event: string;
  } | null;
  timeline: WpwHandoverEvent[];
  // Wynik analizy AI wgranych dokumentów (null = nigdy nie uruchamiana).
  ai_analysis: WpwAgreementAiAnalysis | null;
  // „Pakiet nieaktualny": nowsze żywe zlecenie WPW na tę samą działkę i tego samego
  // właściciela (null = pakiet z tego zlecenia jest aktualny).
  superseded_by: WpwSupersededBy | null;
  can_write: boolean;
  can_confirm: boolean;
}

// Nowsze zlecenie WPW, które unieważniło pakiet z tego wiersza. Negocjator dostaje odnośnik
// tylko do zlecenia własnej firmy (can_open) — cudze widzi po numerze, bez linku.
export interface WpwSupersededBy {
  order_id: string;
  order_number: string | null;
  status: string;
  status_display: string;
  contractor: string | null;
  created_at: string;
  can_open: boolean;
}

// Project parcel never sent into the field for WPW (no owner has a WpwOwnerHandover).
export interface WpwNeverSentParcel {
  id: string;
  teryt: string;
  short_number: string;
  obreb: string;
  status: string;
  status_display: string;
  owners_count: number;
}

// Per-connection "needs attention" rollup that drives the workspace tab badges + mute toggle.
export type AttentionTabKey =
  | "milestones" | "parcels" | "wpw" | "correspondence" | "technical" | "kosztorys" | "kpi"
  | "wody";

export interface ConnectionAttention {
  tabs: Record<AttentionTabKey, number>;
  detail: {
    milestones: { overdue: number; soon: number };
    parcels: { action_required: number; problem: number; never_sent_wpw: number; brak_wypisu: number; office_no_proc: number; route_review: number };
    wpw: { problem: number; awaiting_confirmation: number };
    correspondence: { open: number; kpa_overdue: number };
    technical: { to_review: number; needs_correction: number; route_suspended: boolean };
    kosztorys: { to_review: number; needs_correction: number };
    kpi: { open: number; overdue: number; review_due: boolean };
    // Wody Polskie: zdarzenia czekające na zlecenie (odznaka gaśnie dopiero przy
    // zamówieniu podwykonawczym) + analiza policzona na starszej wersji trasy.
    wody: { pending: number; stale: boolean };
  };
  total: number;
  is_manager: boolean;
  is_muted: boolean;
  can_mute: boolean;
}

// ── design stage (Projekt techniczny / Kosztorys) ────────────────────────────────
export type DesignFileState = "active" | "superseded" | "removed";

export interface DesignFile {
  id: string;
  original_name: string;
  state: DesignFileState;
  url: string | null;
  uploaded_by_name: string | null;
  created_at: string;
  removed_at: string | null;
  superseded_by: string | null;
}

export type DesignEventKind =
  | "status_change" | "comment" | "file_added" | "file_replaced" | "file_removed"
  | "confirmation" | "correction_requested";

export interface DesignEvent {
  id: string;
  kind: DesignEventKind;
  kind_display: string;
  from_status: string;
  to_status: string;
  body: string;
  attachment: string | null;
  attachment_name: string | null;
  decision: string;
  decision_display: string;
  target_event: string | null;
  author_name: string;
  author_side: "executor" | "internal";
  changed_at: string;
}

export interface DesignOrderRef {
  id: string;
  order_number: string | null;
  order_type: string;
  order_type_display: string;
  status: string;
  status_display: string;
  contractor: string | null;
  contractor_name: string | null;
}

export interface DesignAssignee {
  kind: "employee" | "contractor";
  id: string | null;
  name: string | null;
  order?: DesignOrderRef;
}

export interface DesignOrderLink {
  id: string;
  kind: "primary" | "correction";
  is_paid: boolean;
  note: string;
  order: DesignOrderRef | null;
}

export type DeliverableState =
  | "not_started" | "in_progress" | "submitted" | "needs_correction" | "accepted" | "na";

export const DELIVERABLE_STATE_LABEL: Record<DeliverableState, string> = {
  not_started: "Nie rozpoczęto",
  in_progress: "W trakcie",
  submitted: "Złożono (do GK)",
  needs_correction: "Do poprawy",
  accepted: "Zaakceptowano (GK)",
  na: "Nie dotyczy",
};

export const DELIVERABLE_STATE_COLOR: Record<DeliverableState, string> = {
  not_started: "#6b7280",
  in_progress: "#d97706",
  submitted: "#2563eb",
  needs_correction: "#dc2626",
  accepted: "#059669",
  na: "#6b7280",
};

// States the executor may set directly (GK uses Akceptuj / Zgłoś korektę instead).
export const DELIVERABLE_EXECUTOR_STATES: DeliverableState[] = ["in_progress", "submitted", "na"];

export type DeliverableTarget = "ze" | "urzad" | "urzad_ze" | "tom_prawny";

export interface TechnicalDeliverable {
  id: string;
  key: string;
  type: string;
  type_display: string;
  is_custom: boolean;
  label: string;
  display_name: string;
  target: DeliverableTarget;
  target_display: string;
  state: DeliverableState;
  state_display: string;
  order: number;
  attachments: DesignFile[];
  timeline: DesignEvent[];
  created_at: string;
}

export type TechPhaseAState =
  | "not_started" | "concept_sent" | "concept_accepted" | "route_finalized";

export type BundleState = "not_sent" | "sent" | "accepted" | "rejected";

export interface ConsentsSummary {
  total: number;
  secured: number;
  problem: number;
  all_secured: boolean;
}

export interface PhaseTimelineEntry {
  title: string;
  actor_name: string;
  occurred_at: string;
  from_state: TechPhaseAState | null;
  to_state: TechPhaseAState | null;
}

// Referencja do elementu repozytorium (szablony PZT/PAB/PT) podpięta do zlecenia PT.
export interface TemplateResourceRef {
  id: string;
  title: string;
  resource_type: string;
  url: string;
  file: ManagedFileMini | null;
}

export interface TechnicalProject {
  id: string;
  connection: string;
  phase_a_state: TechPhaseAState;
  phase_a_state_display: string;
  concept_sent_at: string | null;
  concept_accepted_at: string | null;
  route_finalized_at: string | null;
  route_finalized_by: string | null;
  route_reverted_at: string | null;
  route_reverted_by: string | null;
  route_revert_reason: string;
  is_suspended: boolean;
  route_version: string | null;
  waiting_note: string;
  ze_state: BundleState;
  ze_state_display: string;
  ze_sent_at: string | null;
  ze_accepted_at: string | null;
  urzad_state: BundleState;
  urzad_state_display: string;
  urzad_sent_at: string | null;
  urzad_accepted_at: string | null;
  tom_prawny_state: BundleState;
  tom_prawny_state_display: string;
  tom_prawny_sent_at: string | null;
  tom_prawny_accepted_at: string | null;
  ze_received_at: string | null;
  settled_at: string | null;
  settlement_note: string;
  deliverables: TechnicalDeliverable[];
  assignee: DesignAssignee | null;
  order_links: DesignOrderLink[];
  /** Postępowanie ofertowe wystawione dla tego etapu (null = nigdy nie wystawiano). */
  proceeding: StageProceeding | null;
  template_resource: string | null;
  template_resource_info: TemplateResourceRef | null;
  default_template_resource: TemplateResourceRef | null;
  // Foldery repozytorium z wzorami dla PT — picker szablonu pokazuje tylko te kategorie.
  pt_template_category_ids: string[];
  consents_summary: ConsentsSummary;
  phase_a_timeline: PhaseTimelineEntry[];
  can_write: boolean;
  can_manage: boolean;
  created_at: string;
  updated_at: string;
}

export type CostEstimateState =
  | "not_started" | "in_progress" | "submitted" | "needs_correction" | "accepted";

export const COST_ESTIMATE_STATE_LABEL: Record<CostEstimateState, string> = {
  not_started: "Nie rozpoczęto",
  in_progress: "W trakcie",
  submitted: "Złożono",
  needs_correction: "Do poprawy",
  accepted: "Zaakceptowano (GK)",
};

export const COST_ESTIMATE_STATE_COLOR: Record<CostEstimateState, string> = {
  not_started: "#6b7280",
  in_progress: "#d97706",
  submitted: "#2563eb",
  needs_correction: "#dc2626",
  accepted: "#059669",
};

export const COST_ESTIMATE_EXECUTOR_STATES: CostEstimateState[] = ["in_progress", "submitted"];

export interface CostEstimate {
  id: string;
  connection: string;
  state: CostEstimateState;
  state_display: string;
  accepted_at: string | null;
  accepted_by: string | null;
  settled_at: string | null;
  settlement_note: string;
  attachments: DesignFile[];
  timeline: DesignEvent[];
  assignee: DesignAssignee | null;
  order_links: DesignOrderLink[];
  /** Postępowanie ofertowe wystawione dla tego etapu (null = nigdy nie wystawiano). */
  proceeding: StageProceeding | null;
  /** PT zaakceptowane przez GK — tylko informacja; kosztorys prowadzi się niezależnie. */
  pt_accepted: boolean;
  /** Żywe zamówienia PT z pozycją kosztorysową (ryzyko podwójnego zlecenia); [] dla podwykonawcy. */
  pt_orders_with_cost_items: CostEstimatePtOrder[];
  can_write: boolean;
  can_manage: boolean;
  created_at: string;
  updated_at: string;
}

export interface CostEstimatePtOrder {
  order_id: string;
  order_number: string;
  contractor_name: string | null;
  items: string[];
}

export interface WypisPageEntry {
  parcel_id: string | null;
  teryt: string;
  pages: string;
}

export interface WypisFile {
  id: string;
  file: ManagedFileMini;
  covered_parcels: { id: string; teryt: string }[];
  note: string;
  // Manual (later AI-filled) mapping of which PDF pages cover which parcel.
  page_map: WypisPageEntry[];
  // Pages in the PDF (poppler). null = not yet computed, 0 = not a readable PDF.
  page_count: number | null;
  created_at: string;
  uploaded_by_name: string | null;
}

// Wynik odpięcia wypisu od działki: ilu właścicieli ubyło, kogo nie ruszono (bo jest już
// na nim praca) i czy działka wróciła do panelu zamawiania wypisów.
export interface WypisDetachSummary {
  owners_removed: number;
  owners_kept: string[];
  owners_left: number;
  orderable_again: boolean;
}

// ── wypis orders (zamawianie wypisów EGiB per powiat) ───────────────────────
export type WypisOrderStatus =
  | "new"
  | "fee_requested"
  | "fee_paid"
  | "sent"
  | "received"
  | "completed"
  | "cancelled";

export const WYPIS_ORDER_STATUS_LABELS: Record<WypisOrderStatus, string> = {
  new: "Nowe",
  fee_requested: "Prośba o opłatę",
  fee_paid: "Opłacona — do wysyłki",
  sent: "Wysłano do starostwa",
  received: "Wypisy otrzymane",
  completed: "Zakończone",
  cancelled: "Anulowane",
};

export const WYPIS_ORDER_STATUS_COLORS: Record<
  WypisOrderStatus,
  "gray" | "blue" | "green" | "red" | "yellow" | "purple" | "orange"
> = {
  new: "gray",
  fee_requested: "yellow",
  fee_paid: "orange",
  sent: "blue",
  received: "purple",
  completed: "green",
  cancelled: "red",
};

// „inwestor" nie ma stałych danych w kodzie — tożsamość niesie samo zamówienie wypisów
// (klient z kartoteki albo dane wpisane ręcznie). MZ i MPZP tej pozycji nie przyjmują.
export type WypisApplicant = "elektim" | "energa" | "tauron" | "inwestor";

// Etykiety wnioskodawcy wypisu/MZ — współdzielone przez strony i modale.
export const WYPIS_APPLICANT_LABELS: Record<WypisApplicant, string> = {
  elektim: "ELEKTIM",
  energa: "ENERGA-OPERATOR",
  tauron: "TAURON Dystrybucja",
  inwestor: "Inwestor",
};

export interface WypisCandidateParcel {
  id: string;
  teryt: string;
  short_number: string;
  obreb: string;
  commune: string;
  connection_id: string;
  project_id: string;
  project_name: string;
  // Operator (OSD) projektu — FE zasiewa nim domyślnego wnioskodawcę wypisu.
  operator: string;
  // Klient przedsięwzięcia — podpowiedź przy wnioskodawcy „inwestor".
  client_id: string | null;
  client_name: string;
  area_m2: number | null;
  // Działka trafiła do panelu z ręcznego zgłoszenia („potrzebny wypis"), mimo że MA już
  // wpisanych właścicieli — zamawiający musi to widzieć, bo inaczej wygląda jak pusta działka.
  wypis_needed: boolean;
  owners_total: number;
  // Powód zgłoszenia (obowiązkowy przy ponownym zamówieniu).
  wypis_needed_reason: string;
  // > 0 = działka MA już wypis → to PONOWNE zamówienie (zmiana właścicieli), nie zwykłe
  // „zgłoszona ręcznie".
  wypisy_total: number;
  // zamówienia wypisów w historii (0 wypisów + zamówienie = wypis odpięty od działki)
  wypis_orders_total: number;
}

export interface WypisCandidateGroup {
  voivodeship: string;
  county: string;
  parcel_count: number;
  parcels: WypisCandidateParcel[];
}

export interface WypisOrderRow {
  id: string;
  number: string;
  status: WypisOrderStatus;
  status_display: string;
  county: string;
  voivodeship: string;
  applicant: WypisApplicant;
  /** Nazwa z pola 1 wniosku — dla „inwestora" jego firma, nie etykieta wyboru. */
  applicant_display: string;
  applicant_client: string | null;
  applicant_client_name: string;
  starostwo: string | null;
  starostwo_name: string;
  created_by: string | null;
  created_by_name: string;
  /** Następca nieaktywnego autora (osoba do kontaktów) — ma jego uprawnienia; `null`, gdy
   *  autor nadal pracuje. Liczone na backendzie (dlc_01 takeover.py). */
  taken_over_by: string | null;
  taken_over_by_name: string;
  /** Czy zalogowany może prowadzić zamówienie — lustro bramki `_can_manage` backendu. */
  can_manage: boolean;
  parcel_count: number;
  project_names: string[];
  has_documents: boolean;
  has_doo: boolean;
  generated_at: string | null;
  fee_id: string | null;
  fee_status: string | null;
  fee_amount: string | null;
  fee_rejection_comment: string;
  sent_at: string | null;
  received_at: string | null;
  applied_at: string | null;
  created_at: string;
}

export interface WypisResultFile {
  id: string;
  file: ManagedFileMini;
  kind: "pdf" | "csv" | "xlsx" | "ods" | "other";
  page_count: number | null;
  uploaded_by_name: string;
  created_at: string;
}

export interface WypisProposalOwner {
  kind: "person" | "office";
  full_name: string;
  street: string;
  building_no: string;
  postal_code: string;
  city: string;
  share: string;
  pesel: string;
  // Wspólność małżeńska wprost z dokumentu (ta sama wartość u obojga małżonków udziału);
  // opcjonalne — stare propozycje jej nie mają. Ta sama osoba może mieć DWA wpisy:
  // małżeński (z grupą) i samodzielny (bez grupy, własna pozycja Lp) — apply składa je
  // w jeden rekord (own_share).
  marital_group?: string;
  // Pozycja Lp z tabeli podmiotów wypisu — po niej sprawdza się skład małżeństwa.
  lp?: string;
  // Set when an institutional owner is matched to an Office (picker) → apply links the FK.
  office_id?: string | null;
  // Zdublowany podmiot nadrzędny (np. Skarb Państwa obok GDDKiA) — domyślnie
  // pominięty przy zapisie, przywracalny w recenzji. Pola opcjonalne: stare
  // zapisane propozycje ich nie mają i zachowują się jak redundant=false.
  redundant?: boolean;
  redundant_with?: string;
  redundant_reason?: string;
  redundant_source?: "ai" | "rule" | "";
}

export interface WypisProposalParcel {
  teryt: string;
  parcel_id: string | null;
  matched: boolean;
  kw_number: string;
  land_use: string;
  // Grupa rejestrowa (1-15, opcjonalnie podgrupa) — po normalizacji backendu.
  ownership_code: string;
  // Jednostka rejestrowa (JRG, np. "G.379") — to NIE jest grupa rejestrowa.
  registry_unit?: string;
  area_m2: number | null;
  owners: WypisProposalOwner[];
  pages: { result_file_id: string; pages: string }[];
}

// ── księgi wieczyste (KW) — analiza AI ────────────────────────────────────────
export interface KwOwnerRow { name: string; share: string; pesel_nip: string; role: string; }
export interface KwEasementRow { type: string; description: string; beneficiary: string; }
export interface KwMortgageRow { amount: string; currency: string; creditor: string; type: string; }
// Wpis o drodze odczytany z księgi (dowolny dział). `internal` = droga WEWNĘTRZNA.
export interface KwRoadRow {
  section: string;
  kind: string;
  description: string;
  internal: boolean;
}
export interface KwAnalysis {
  kw_number?: string;
  owners?: KwOwnerRow[];
  easements?: KwEasementRow[];
  mortgages?: KwMortgageRow[];
  // Sposób korzystania z Działu I-O (np. „DR — DROGI") + wpisy o drogach.
  way_of_use?: string;
  roads?: KwRoadRow[];
  warnings?: string[];
}
// Rozbieżność nazw uznana przez człowieka za w porządku (ten sam podmiot, inny zapis).
export interface KwAcceptedDiff { kw: string; db: string; note: string; by: string; at: string; }
export interface KwOwnerDiff {
  only_in_kw: string[];
  only_in_db: string[];
  matched: string[];
  accepted: KwAcceptedDiff[];
  db_empty: boolean;
}

// PDF jednego działu księgi (okładka, I-O, I-Sp, II, III, IV) — budowany na serwerze
// z treści przysłanej przez wtyczkę albo wgrany ręcznie; idzie do wniosków urzędowych.
export interface KwSectionFile {
  id: string;
  section: string;
  section_label: string;
  page_count: number | null;
  source: string;
  file: ManagedFileMini | null;
}

export interface ParcelKwFile {
  id: string;
  parcel: string;
  parcel_teryt: string;
  connection: string;
  file: ManagedFileMini | null;
  page_count: number | null;
  // Surowe treści działów zapisane na stałe — nie trzeba wracać na EKW.
  sections: Record<string, string>;
  section_files: KwSectionFile[];
  owner_diff: KwOwnerDiff | null;
  owner_diff_accepted: KwAcceptedDiff[];
  roads: KwRoadRow[];
  analysis: KwAnalysis;
  analysis_model: string;
  analysis_cost_usd: string;
  analyzed_at: string | null;
  uploaded_by_name: string;
  created_at: string;
  updated_at: string;
}

// Wiersz zakładki „KW" projektu: status pobrania/analizy księgi jednej działki.
export interface KwOverviewRow {
  parcel: string;
  teryt: string;
  connection: string;
  project_id: string;
  project_name: string;
  kw_number: string;
  kw_valid: boolean;
  has_file: boolean;
  file_name: string;
  analyzed_at: string | null;
  sections_count: number;
  owners_count: number;
  attempts: number;
  last_attempt_at: string | null;
  last_attempt_outcome: string;
  last_attempt_outcome_label: string;
  last_attempt_detail: string;
  not_found: boolean;
  diff: {
    only_in_kw: number;
    only_in_db: number;
    accepted: number;
    db_empty: boolean;
  } | null;
}
export interface KwOverview {
  items: KwOverviewRow[];
  stats: {
    total: number;
    done: number;
    invalid: number;
    untried: number;
    truncated: boolean;
  };
}

export interface WypisExtractionProposal {
  model: string;
  files: { result_file_id: string; filename: string }[];
  parcels: WypisProposalParcel[];
  /** Uwagi z przebiegu analizy — linie ⚠ (odmowa modelu, za duży plik, ucięta
   *  odpowiedź) i ℹ (odchudzenie/podział pliku, przemapowane strony). Log zadania
   *  znika ze strony po jego zakończeniu; bez tych uwag „0 działek” było bez powodu. */
  notes?: string[];
}

export interface WypisOrderParcelBrief {
  id: string;
  teryt: string;
  short_number: string;
  obreb: string;
  commune: string;
  connection_id: string;
  project_id: string;
  project_name: string;
  // Aktualni właściciele działki z licznikami pracy — modal przeglądu ekstrakcji liczy
  // z nich, kto zostaje / kto nowy / kto po zapisie stanie się nieaktualny.
  owners: WypisOrderParcelOwnerBrief[];
  wypisy_total: number;
  wypis_needed: boolean;
  wypis_needed_reason: string;
}

export interface WypisOrderParcelOwnerBrief {
  id: string;
  full_name: string;
  kind: "person" | "company";
  office_id: string | null;
  titles_secured: number;
  wpw_n: number;
  wnu_n: number;
  open_cases_n: number;
}

// Jedna z opłat 17 zł za pełnomocnictwo podpiętych do zamówienia wypisów.
export interface WypisPoaFee {
  id: string;
  status: string;
  amount: string;
  office_name: string;
  // Data opłaty (paid_at) — auto z /official-fees; pusta, dopóki nieopłacona.
  paid_at: string | null;
  rejection_comment: string;
  payment_files: ManagedFileMini[];
}

/** Urząd, do którego idzie opłata skarbowa 17 zł za pełnomocnictwo — ZAWSZE urząd
 *  siedziby podmiotu wnioskującego (pole 1 wniosku), nigdy adresata z pola 3.
 *  `source` mówi, skąd go znamy: wskazany ręcznie / konfiguracja mocodawcy /
 *  rozpoznany z miasta siedziby / nierozpoznany. */
export interface StampDutyOfficeInfo {
  office_id: string | null;
  office_name: string;
  office_address: string;
  principal: string;
  /** Miasto siedziby wnioskodawcy odczytane z pola 1 — podstawa rozpoznania. */
  seat_city: string;
  bank_account: string;
  source: "manual" | "config" | "city" | "recipient" | "none";
  source_label: string;
  manual: boolean;
}

export interface WypisOrder extends WypisOrderRow {
  contact_person: string | null;
  contact_person_name: string;
  // Inwestor spoza kartoteki klientów — używane tylko gdy applicant === "inwestor"
  // i nie wskazano applicant_client.
  applicant_custom_name: string;
  applicant_custom_address: string;
  applicant_custom_nip: string;
  applicant_custom_regon: string;
  /** Kontakt zamawiającego — na żywo z profilu, więc jego zmiana zmienia i te pola. */
  created_by_phone: string | null;
  created_by_email: string | null;
  /** Kontakt następcy nieaktywnego zamawiającego (`null` bez przejęcia). */
  taken_over_by_phone: string | null;
  taken_over_by_email: string | null;
  subject_scope: string[];
  justification_kind: string;
  justification_text: string;
  // Legal interest (section 10): field 12 "wynikający z" / field 13 "w związku z".
  justification_basis: string;
  justification_context: string;
  document_form: "papierowa" | "elektroniczna";
  pickup_method: "poczta" | "odbior_osobisty" | "epuap";
  notes: string;
  form_file: ManagedFileMini | null;
  attachment_pdf: ManagedFileMini | null;
  attachment_xlsx: ManagedFileMini | null;
  // DOO (Dokument Obliczenia Opłaty) ze starostwa — wgrany przy składaniu prośby o opłatę.
  doo_file: ManagedFileMini | null;
  sent_by_name: string;
  received_by_name: string;
  result_files: WypisResultFile[];
  parcels_brief: WypisOrderParcelBrief[];
  extraction_proposal: WypisExtractionProposal | null;
  extraction_cost_usd: string | null;
  extracted_at: string | null;
  suggested_account_number: string;
  // 17 zł opłaty skarbowe za pełnomocnictwo — osobne od opłaty za wypis; może być
  // ich kilka (działki kilku klientów → kilku mocodawców, każdy z własnym urzędem).
  poa_fees: WypisPoaFee[];
  // Potwierdzenia płatności z /official-fees — do szybkiego pobrania z panelu.
  fee_payment_files: ManagedFileMini[];
  poa_fee_not_required: boolean;
  poa_fee_not_required_reason: string;
  stamp_duty_office: StampDutyOfficeInfo & { mixed_clients: boolean };
  /** Historia zamówienia — od najnowszego wpisu (zmiany statusu, opłaty, pliki). */
  history: OrderHistoryEvent[];
  /** Status, do którego cofnie akcja „Cofnij…" ("" = nie ma czego cofać). */
  revert_to: WypisOrderStatus | "";
  /** Etykieta przycisku cofania wyliczona przez backend (np. „Cofnij wysłanie"). */
  revert_label: string;
  created_fee_id?: string;
  apply_summary?: {
    owners_created: number;
    parcels_updated: number;
    wypis_files_registered: number;
    connections: string[];
  };
}

export interface WypisOrderCreatePayload {
  county: string;
  voivodeship?: string;
  parcel_ids: string[];
  starostwo: string;
  applicant: WypisApplicant;
  /** Wymagane przy applicant === "inwestor": klient z kartoteki… */
  applicant_client?: string | null;
  /** …albo dane wpisane ręcznie (gdy inwestora w kartotece nie ma). */
  applicant_custom_name?: string;
  applicant_custom_address?: string;
  applicant_custom_nip?: string;
  applicant_custom_regon?: string;
  contact_person?: string;
  subject_scope?: string[];
  justification_kind?: string;
  justification_text?: string;
  justification_basis?: string;
  justification_context?: string;
  document_form?: string;
  pickup_method?: string;
  notes?: string;
}

// ── mapa zasadnicza (zamówienia MZ — formularz P + obrys MDCP jako DXF) ───────
export type MzOrderStatus = WypisOrderStatus;

export const MZ_ORDER_STATUS_LABELS: Record<MzOrderStatus, string> = {
  new: "Nowe",
  fee_requested: "Prośba o opłatę",
  fee_paid: "Opłacona — do wysyłki",
  sent: "Wysłano do starostwa",
  received: "Mapa otrzymana",
  completed: "Zakończone",
  cancelled: "Anulowane",
};

export const MZ_ORDER_STATUS_COLORS = WYPIS_ORDER_STATUS_COLORS;

export type MzPurpose = "wlasne_niezwiazane" | "dowolne";
export type MzDeliveryMethod =
  | "email" | "odbior_osobisty" | "poczta" | "usluga_sieciowa" | "ftp" | "nosnik";

// Wiersz modułowej listy zamówień MDCP (GET /power-projects/mdcp/orders/) — jak
// MdcpOrderRow, ale bez pól mapy i z projektem ZE, do którego zamówienie należy.
export interface MdcpOrderListRow {
  id: string;
  order: string;
  order_number: string;
  order_status: string;
  order_status_display: string;
  contractor_name: string | null;
  deadline: string | null;
  kind: string;
  is_paid: boolean;
  points_count: number;
  ordered_length_units: number | null;
  ordered_area_units: number | null;
  connection: string;
  project_id: string;
  project_name: string;
  created_by_name: string;
  created_at: string;
}

export interface MzOrderRow {
  id: string;
  number: string;
  status: MzOrderStatus;
  status_display: string;
  county: string;
  /** Kod TERYT powiatu (WWPP). Jeden wniosek = jeden powiat; zakres przycięty do jego granicy. */
  county_teryt: string;
  voivodeship: string;
  applicant: WypisApplicant;
  starostwo: string | null;
  starostwo_name: string;
  created_by: string | null;
  created_by_name: string;
  /** Następca nieaktywnego autora (osoba do kontaktów) — ma jego uprawnienia; `null`, gdy
   *  autor nadal pracuje. Liczone na backendzie (dlc_01 takeover.py). */
  taken_over_by: string | null;
  taken_over_by_name: string;
  /** Czy zalogowany może prowadzić zamówienie — lustro bramki `_can_manage` backendu. */
  can_manage: boolean;
  connection: string;
  project_id: string;
  project_name: string;
  scale: string;
  has_documents: boolean;
  generated_at: string | null;
  /** Zakres zamówienia (obrys MDCP zamrożony przy generacji). Tylko przy ?with_buffer=1. */
  buffer_geometry: GeoJsonGeometry | null;
  /** Obrys MDCP, z którego liczy się zakres tego wniosku (pozycja listy w panelu MDCP). */
  buffer: string | null;
  /** Nazwa tego obrysu — dla skasowanego zostaje ta zapamiętana przy generacji. */
  buffer_name: string;
  fee_id: string | null;
  fee_status: string | null;
  fee_amount: string | null;
  fee_rejection_comment: string;
  sent_at: string | null;
  received_at: string | null;
  created_at: string;
}

export type MzResultKind = "map" | "licencja" | "other";

export interface MzResultFile {
  // Rodzaj pliku ze starostwa: wektor idzie do projektanta, licencja (PDF/XML/ODT) wchodzi
  // automatycznie jako załącznik wniosków urzędowych.
  kind: MzResultKind;
  kind_display: string;
  id: string;
  file: ManagedFileMini;
  uploaded_by_name: string;
  created_at: string;
}

export type OrderEventKind =
  | "created"
  | "status"
  | "revert"
  | "fee"
  | "poa_fee"
  | "file"
  | "orderer";

/** Wpis historii zamówienia urzędowego — MZ, wypisów EGiB i MPZP (backend trzyma
 *  osobną tabelę na moduł, ale kształt wpisu jest jeden): zmiany statusu w przód
 *  i wstecz, opłaty (urzędowa i za pełnomocnictwo) wraz z ich statusami, operacje
 *  na plikach. Pliki usunięte z wniosku zostają do pobrania przy swoim wpisie. */
export interface OrderHistoryEvent {
  id: string;
  kind: OrderEventKind;
  kind_display: string;
  description: string;
  reason: string;
  from_status: string;
  from_status_display: string;
  to_status: string;
  to_status_display: string;
  /** null = zmiana bez człowieka w pętli (lustro statusu opłaty z /official-fees). */
  actor: string | null;
  actor_name: string;
  /** Plik, którego wpis dotyczy — przy wpisach o usunięciu jedyna droga do pobrania. */
  file: ManagedFileMini | null;
  meta: Record<string, unknown>;
  created_at: string;
}

/** Komentarz pod zamówieniem MZ (wzór /orders) — osobny od `notes` (drukowanych na wniosku). */
export interface MzOrderComment {
  id: string;
  body: string;
  author: string;
  author_name: string;
  is_edited: boolean;
  created_at: string;
}

export interface MzOrder extends MzOrderRow {
  contact_person: string | null;
  contact_person_name: string;
  /** Kontakt zamawiającego — na żywo z profilu, więc jego zmiana zmienia i te pola. */
  created_by_phone: string | null;
  created_by_email: string | null;
  /** Kontakt następcy nieaktywnego zamawiającego (`null` bez przejęcia). */
  taken_over_by_phone: string | null;
  taken_over_by_email: string | null;
  purpose: MzPurpose;
  delivery_method: MzDeliveryMethod;
  /** Uwagi drukowane na wniosku (sekcja 11) — edycja jak reszta formularza. */
  notes: string;
  /** Komentarze — dodaje każdy z dostępem do modułu, w każdym statusie. */
  comments: MzOrderComment[];
  /** Historia zamówienia — od najnowszego wpisu. */
  history: OrderHistoryEvent[];
  /** Status, do którego cofnie akcja „Cofnij…" ("" = nie ma czego cofać). */
  revert_to: MzOrderStatus | "";
  /** Etykieta przycisku cofania wyliczona przez backend (np. „Cofnij wysłanie"). */
  revert_label: string;
  /** Strefa PUWG 2000 projektu (EPSG:2176–2179) — trafia do sekcji 11 i do DXF. */
  srid: number;
  form_file: ManagedFileMini | null;
  attachment_dxf: ManagedFileMini | null;
  /** DOO (Dokument Obliczenia Opłaty) ze starostwa — wgrany przy składaniu prośby o opłatę. */
  doo_file: ManagedFileMini | null;
  sent_by_name: string;
  received_by_name: string;
  result_files: MzResultFile[];
  suggested_account_number: string;
  poa_fees: WypisPoaFee[];
  fee_payment_files: ManagedFileMini[];
  poa_fee_not_required: boolean;
  poa_fee_not_required_reason: string;
  /** Wygenerowany zakres przycięty do granicy powiatu (projekt w kilku powiatach). */
  clipped_to_county: boolean;
  stamp_duty_office: StampDutyOfficeInfo & { mixed_clients: boolean };
  created_fee_id?: string;
}

export interface MzOrderCreatePayload {
  connection: string;
  starostwo: string;
  county?: string;
  /** Powiat wniosku (TERYT WWPP) — wymagany, gdy projekt leży w kilku powiatach. */
  county_teryt?: string;
  voivodeship?: string;
  applicant?: WypisApplicant;
  contact_person?: string;
  scale?: string;
  purpose?: MzPurpose;
  delivery_method?: MzDeliveryMethod;
  notes?: string;
  /** Obrys MDCP dla tego wniosku; pusty = obrys wybrany w panelu MDCP projektu. */
  buffer?: string;
}

export interface MzOfficeBrief {
  id: string;
  name: string;
  full_address: string;
}

/** Powiat w zakresie projektu — na każdy składa się OSOBNY wniosek do jego starostwa. */
export interface MzCountyOption {
  /** Kod TERYT powiatu (WWPP), np. „2204". */
  teryt: string;
  county: string;
  voivodeship: string;
  label: string;
  parcel_count: number;
  starostwo_matches: MzOfficeBrief[];
  existing_order: { id: string; number: string; status: MzOrderStatus } | null;
}

// Prefill dla modala tworzenia: powiaty trasy (jeden wniosek na powiat), dominujący
// powiat, strefa PUWG, dostępność obrysu MDCP i dopasowania starostwa z rejestru.
/** Obrys MDCP projektu do wyboru jako zakres wniosku MZ. */
export interface MzBufferOption {
  id: string;
  display_name: string;
  is_active: boolean;
  area_m2: number | null;
  buffer_width_m: number;
}

export interface MzSuggestContext {
  county: string;
  voivodeship: string;
  srid: number;
  has_outline: boolean;
  buffer_width_m: number;
  /** Obrysy projektu — zakres wniosku bierze się z jednego z nich. */
  buffers: MzBufferOption[];
  /** Domyślny wybór: obrys wybrany w panelu MDCP. */
  buffer: string;
  starostwo_matches: MzOfficeBrief[];
  counties: MzCountyOption[];
  /** Zapas przy przycinaniu zakresu do granicy powiatu [m]. */
  clip_margin_m: number;
}

// ── wnioski urzędowe (silnik pakietów) ───────────────────────────────────────
export type ApplicationTypeKey =
  | "lokalizacyjny" | "klauzula" | "wpw_pakiet" | "odrolnienie" | "ulicp" | "uniwersalny"
  | (string & {});

export type ApplicationPhaseKey =
  | "scope" | "poa" | "document" | "maps" | "attachments" | "compile" | "submit";

export interface ApplicationChecklistConfigItem {
  kind: string;
  label: string;
  required: boolean;
  auto_source: string | null;
  hint: string;
}

export interface ApplicationTypeConfig {
  key: ApplicationTypeKey;
  label: string;
  description: string;
  instructions: string;
  phases: { key: ApplicationPhaseKey; label: string }[];
  requires_poa: boolean;
  poa_fee: boolean;
  /** Czy typ wymaga opłaty skarbowej OD SAMEGO WNIOSKU (obok 17 zł od pełnomocnictwa)
   *  — dziś ULICP: 598 zł za decyzję. Kwota jest tylko domyślna (edytowalna). */
  application_fee: boolean;
  application_fee_amount: string;
  /** Stawka jest ZA EGZEMPLARZ (klauzula: 17 zł/egz. zaświadczenia) — kwota domyślna
   *  = stawka × liczba egzemplarzy wniosku. */
  application_fee_per_copy: boolean;
  application_fee_label: string;
  doc_template_kind: string;
  formatka_purpose: string;
  recipient_mode: string;
  unique_rule: string;
  /** false = procedura jeszcze niedostępna: kreator jej nie pozwala wybrać, API odrzuca
   *  zakładanie nowych; istniejące wnioski tego typu działają bez zmian. */
  available: boolean;
  /** true = typ BEZ własnego druku (wniosek uniwersalny): przy zakładaniu wskazuje się
   *  szablon rodzaju `doc_template_kind` („Uniwersalny"), a kreator pokazuje takie typy
   *  w osobnej sekcji z listą szablonów. */
  template_pick: boolean;
  default_copies: number;
  qr_cover: boolean;
  /** true = do urzędu idzie PODPISANY pakiet (dziś ULICP): po kompilacji trzeba wgrać
   *  podpisany pakiet („Wgraj podpisany pakiet"), inaczej faza kompilacji się nie zamknie
   *  i wniosek nie wyjdzie. Podpisu w pliku backend nie sprawdza. */
  signed_package: boolean;
  /** MilestoneType value the procedure advances on the process board ("" = none). */
  milestone_key: string;
  checklist: ApplicationChecklistConfigItem[];
}

// "OfficialApp" prefix — `ApplicationStatus` already names the proceedings' item
// lifecycle (draft/approved/submitted) earlier in this file.
export type OfficialAppStatus =
  | "draft" | "waiting_fee" | "ready" | "pending_approval" | "submitted"
  | "return_received" | "completed" | "cancelled";

export const APPLICATION_STATUS_LABELS: Record<OfficialAppStatus, string> = {
  draft: "Szkic",
  waiting_fee: "Czeka na opłatę",
  ready: "Do wysyłki",
  pending_approval: "Do akceptacji dyrektora",
  submitted: "Złożony",
  return_received: "Zwrot otrzymany",
  completed: "Zakończony",
  cancelled: "Anulowany",
};

export const APPLICATION_STATUS_COLORS: Record<
  OfficialAppStatus, "gray" | "blue" | "green" | "red" | "yellow" | "purple" | "orange"
> = {
  draft: "gray",
  waiting_fee: "yellow",
  ready: "orange",
  pending_approval: "purple",
  submitted: "blue",
  return_received: "purple",
  completed: "green",
  cancelled: "red",
};

export interface PowerOfAttorneyRow {
  id: string;
  principal_kind: "client" | "elektim";
  principal_client: string | null;
  principal_client_name: string;
  principal_label: string;
  principal_display: string;
  attorney_user: string | null;
  attorney_user_name: string;
  attorney_name: string;
  attorney_display: string;
  // Nr pełnomocnictwa — obowiązkowy dla TAURONA (drukuje się na porozumieniu TD),
  // u pozostałych operatorów opcjonalny. `operator` = OSD mocodawcy, `number_required`
  // liczone na backendzie po nim.
  number: string;
  number_required: boolean;
  operator: string;
  scope: string;
  eop_client: string | null;
  eop_client_name: string;
  eop_branch: string | null;
  eop_branch_name: string;
  project: string | null;
  project_name: string;
  file: ManagedFileMini | null;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  notes: string;
  validity: "active" | "expiring" | "expired" | "inactive";
  created_at: string;
}

// Lightweight przedsięwzięcie option for the PoA form picker
// (GET /power-projects/powers-of-attorney/projects/).
export interface PoaProjectOption {
  id: string;
  name: string;
  short_name: string;
  client_id: string | null;
  client_name: string;
}

export type DecisionKind = "ulicp" | "wz" | "art124" | "odrolnienie" | "lokalizacyjna" | "inna";
export type DecisionFinality = "not_final" | "clause_requested" | "final";

export const DECISION_KIND_LABELS: Record<DecisionKind, string> = {
  ulicp: "Decyzja ULICP",
  wz: "Decyzja WZ",
  art124: "Decyzja art. 124",
  odrolnienie: "Zezwolenie na wyłączenie z produkcji",
  lokalizacyjna: "Zgoda/decyzja lokalizacyjna",
  inna: "Inna decyzja",
};

export const DECISION_FINALITY_LABELS: Record<DecisionFinality, string> = {
  not_final: "Nieostateczna",
  clause_requested: "Wniosek o klauzulę w toku",
  final: "Ostateczna",
};

export interface AdministrativeDecisionRow {
  id: string;
  connection: string;
  sign: string;
  kind: DecisionKind;
  kind_display: string;
  issuing_office: string | null;
  issuing_office_name: string;
  issuing_display: string;
  issued_at: string | null;
  description: string;
  file: ManagedFileMini | null;
  clause_file: ManagedFileMini | null;
  finality: DecisionFinality;
  finality_display: string;
  final_at: string | null;
  covered_parcels: { id: string; teryt: string }[];
  // Pozycja sprawy, z której decyzję zarejestrowano / pod którą ją podpięto; null = luzem.
  source_item: string | null;
  proceeding_info: { id: string; type_name: string; status: ProceedingStatus; item_id: string } | null;
  created_at: string;
}

/** Konsekwencje cofnięcia klauzuli ostateczności — treść modala potwierdzenia. */
export interface ClauseRevertImpact {
  sign: string;
  kind_display: string;
  final_at: string | null;
  clause_file: string;
  parcels: string[];
  applications: {
    id: string;
    number: string;
    type_label: string;
    status_display: string;
    already_sent: boolean;
    /** Znak innej ostatecznej decyzji, która wskoczy na jej miejsce ("" = żadna). */
    replacement: string;
  }[];
  milestone: { key: string; label: string; kept_by: string } | null;
  clause_application: { number: string; status_display: string } | null;
}

export interface ApplicationPoaLink {
  id: string;
  poa: PowerOfAttorneyRow;
  fee: string | null;
  fee_status: string | null;
  fee_amount: string | null;
  fee_paid_at: string | null;
  fee_not_required: boolean;
  fee_not_required_reason: string;
  order: number;
  manual: boolean;
}

// Registry PoA offerable for manual attach, with the scoping-fit rating
// (GET /official-applications/:id/poa-candidates/).
export interface ApplicationPoaCandidate extends PowerOfAttorneyRow {
  match: "project" | "branch" | "universal" | "other_scope";
}

export interface ApplicationMapRow {
  id: string;
  sheet_set: string | null;
  file: ManagedFileMini;
  label: string;
  stale: boolean;
  stale_reason: string;
  created_at: string;
}

export interface ApplicationAttachmentRow {
  id: string;
  kind: string;
  file: ManagedFileMini;
  source: string;
  note: string;
  created_at: string;
}

export interface ApplicationChecklistStatus {
  kind: string;
  label: string;
  required: boolean;
  hint: string;
  auto_source: string | null;
  satisfied: boolean;
  source: "auto" | "upload" | "physical" | null;
  files: { id: string | null; name: string }[];
  warning: string;
}

// Opłacona opłata urzędowa (z potwierdzeniem przelewu) do podpięcia jako
// załącznik „potwierdzenie opłaty skarbowej" — zamiast wgrywania pliku ręcznie.
export interface PaidFeeCandidate {
  id: string;
  office_name: string;
  amount: string;
  status: string;
  transfer_name: string;
  paid_at: string | null;
  same_project: boolean;
  is_power_of_attorney: boolean;
  confirmations: string[];
}

// Proposal for the "sklej wypisy" button: per wypis the deduplicated union of
// page_map ranges covering the application's parcels + parcels without coverage.
export interface WypisMergeWypisRow {
  wypis_id: string;
  name: string;
  page_count: number | null;
  created_at: string;
  parcels: { parcel_id: string; teryt: string; pages_spec: string; error: string }[];
  proposed_pages: number[];
  proposed_spec: string;
}

export interface WypisMergeProposal {
  wypisy: WypisMergeWypisRow[];
  missing: { parcel_id: string; teryt: string }[];
}

export interface QualificationRow {
  parcel_id: string;
  teryt: string;
  auto_required: boolean;
  manual: boolean;
  reason: string;
  // Pochodzenie werdyktu utrwalone w migawce wniosku — wniosek przygotowany miesiąc temu
  // nadal musi umieć powiedzieć, czy klasa wzięła się z usługi powiatu, z odczytu mapy,
  // czy tylko z wykazu klasoużytków całej działki.
  source?: ParcelOdrolnienieSource;
  override: boolean | null;
  effective_required: boolean;
  land_use: string;
  soil_class: string;
  area_m2: number | null;
  // Powierzchnia do WYŁĄCZENIA (nie powierzchnia działki) — liczona na backendzie ze
  // stawek za urządzenia; `*_override_m2` to ręczna korekta, która bije wyliczenie.
  exclusion_area_m2?: number;
  exclusion_area_override_m2?: number | null;
  exclusion_area_note?: string;
  exclusion_land_class?: string;
}

export interface OfficialApplicationRow {
  id: string;
  number: string;
  application_type: ApplicationTypeKey;
  type_label: string;
  connection: string;
  project_name: string;
  // Id przedsięwzięcia (hub) — /power-projects/:id przyjmuje JE, nie id connection.
  project_id: string;
  status: OfficialAppStatus;
  status_display: string;
  recipient_office: string | null;
  recipient_name: string;
  commune: string;
  county: string;
  created_by_name: string;
  responsible_name: string;
  // Osoba odpowiedzialna, która przejęła wniosek po nieaktywnym autorze (ma jego
  // uprawnienia) — `null`, gdy autor nadal pracuje.
  taken_over_by: string | null;
  taken_over_by_name: string;
  phase_progress: { approved: number; total: number };
  submitted_at: string | null;
  created_at: string;
  // szkic WPW zastąpiony nowszym (nigdy nie gotowy/wysłany) — pokazuj jako nieaktywny
  superseded?: boolean;
  // pakiet WPW właściciela NIEAKTUALNEGO (ponowny wypis) — na liście tylko po
  // „pokaż nieaktualnych właścicieli"
  wpw_owner_outdated?: boolean;
  wpw_owner_outdated_reason?: string;
  // Czy zalogowany user może edytować (autor / dyrektor / CEO, wniosek niezłożony)
  // — liczone na backendzie, lustro bramki PATCH.
  can_edit: boolean;
  // Czy może PROWADZIĆ wniosek także po złożeniu (autor lub jego następca / dyrektor /
  // CEO) — „Zakończ", obieg oryginału decyzji przy klauzuli; lustro bramki `_can_manage`.
  can_manage: boolean;
  // Wniosek uniwersalny: szablon wskazany przy zakładaniu — to on mówi, czym jest pismo
  // (`type_label` dokleja jego nazwę do etykiety typu). Puste dla typów z własnym drukiem.
  doc_template?: string | null;
  doc_template_name?: string;
}

export interface ApplicationPhaseState {
  key: ApplicationPhaseKey;
  label: string;
  state: "pending" | "approved";
  approved_by?: string;
  approved_at?: string;
  /** Faza „poa” zatwierdzona bez potwierdzenia opłaty, która wciąż czeka na przelew. */
  fees_unpaid?: boolean;
}

// Dane drogi krajowej wniosku lokalizacyjnego GDDKiA (OfficialApplication.road_data):
// wyliczone z API mapy GDDKiA (compute-road-data) albo poprawione ręcznie (PATCH).
export interface ApplicationRoadData {
  road_number?: string;
  // Surowa linia referencyjna KPD (np. "S7i" = POG Gdańsk z lokalnym pikietażem)
  // — do weryfikacji kilometrażu na mapie GDDKiA; do druku idzie road_number.
  ref_road?: string;
  relacja?: string;
  km_start?: number | string;
  m_start?: number | string;
  km_end?: number | string;
  m_end?: number | string;
  side?: string;
  source?: "api" | "manual";
  computed_at?: string;
}

/** Opłata skarbowa OD SAMEGO WNIOSKU (ULICP 598 zł): kwota domyślna, urząd (ADRESAT
 *  wniosku — organ wydający decyzję, nie siedziba mocodawcy jak przy 17 zł) i stan
 *  zleconej opłaty. `null` dla typów, które takiej opłaty nie znają. */
export interface ApplicationFeeInfo {
  /** Kwota domyślna: stawka, a przy opłacie za egzemplarz stawka × `copies`. */
  amount_default: string;
  per_copy: boolean;
  copies: number;
  label: string;
  office_id: string | null;
  office_name: string;
  office_address: string;
  bank_account: string;
  source: "manual" | "recipient" | "none";
  source_label: string;
  manual: boolean;
  fee: {
    id: string;
    status: string;
    amount: string;
    office_name: string;
    transfer_name: string;
    paid_at: string | null;
    confirmations: string[];
  } | null;
}

/** Plik jadący OBOK pakietu (nie scalany): pełnomocnictwo w oryginalnych bajtach,
 *  potwierdzenie opłaty skarbowej albo inny plik z wykrytym podpisem elektronicznym. */
export interface SeparatePackageFile {
  label: string;
  role?: "poa" | "fee_confirmation" | "signed";
  reason?: string;
  file: ManagedFileMini;
}

export const SEPARATE_FILE_ROLE_LABELS: Record<NonNullable<SeparatePackageFile["role"]>, string> = {
  poa: "pełnomocnictwo — oryginał",
  fee_confirmation: "potwierdzenie opłaty",
  signed: "podpis elektroniczny",
};

/** Pismo pakietu ze stanem pól ręcznych — kind/id to adres slotu edytora w przeglądarce. */
export interface DocumentFillRow {
  kind: "generated" | "own_document";
  id: string;
  label: string;
  filename: string;
  editable: boolean;
  /** Plik źródłowy (ODT/DOCX…) bieżącej wersji. */
  file?: ManagedFileMini | null;
  /** PDF bieżącej wersji do kontroli — z generacji albo zapisu w edytorze; null = brak. */
  pdf?: ManagedFileMini | null;
  pdf_error?: string;
  manual_fields: string[];
  unfilled: string[];
  complete: boolean;
}

// Wiersz kolejki „Wnioski do akceptacji" (GET official-applications/approval-queue/):
// widok przekrojowy dyrektora technicznego — wnioski w statusie pending_approval ze
// wszystkich projektów, które zalogowany może zatwierdzić.
export interface OfficialApplicationQueueRow extends OfficialApplicationRow {
  approval_requested_at: string | null;
  approval_requested_by_name: string;
  approval_channel: string;
  approval_channel_ref: string;
  // Kompilat do obejrzenia przed zatwierdzeniem — dokładnie ten, który przekazano.
  compiled_pdf: ManagedFileMini | null;
  compiled_at: string | null;
  compiled_is_manual: boolean;
  // Dyrektorzy techniczni przypisani do przedsięwzięcia (dostali dzwonek). Pusta
  // lista = nikt nieprzypisany → dzwonek poszedł do wszystkich dyrektorów technicznych.
  project_directors: string[];
  // Czy dzwonek o tym wniosku poszedł do zalogowanego.
  addressed_to_me: boolean;
  can_approve_submission: boolean;
}

export interface OfficialApplicationDetail extends OfficialApplicationRow {
  qualification: QualificationRow[];
  copies: number;
  notes: string;
  // Wspólny opis urządzeń ENERGA dla wszystkich działek wniosku (AI, edytowalny)
  // — zasila #dzialki_jst_opis_prac_energa_ai#.
  energa_work_description: string;
  decision: string | null;
  decision_detail: AdministrativeDecisionRow | null;
  // Klauzula: obieg ORYGINAŁU decyzji — wysyłka ustawia „wyszedł", zakończenie „wrócił";
  // korekta ręczna akcją decision-original. null = jeszcze nie wyszedł / nie wrócił.
  decision_original_sent_at: string | null;
  decision_original_returned_at: string | null;
  wpw_parcel: string | null;
  wpw_parcel_teryt: string;
  responsible_user: string | null;
  // Nadrzędny przełącznik na cały wniosek: opłaty skarbowe za pełnomocnictwa
  // rozliczone poza systemem (wprowadzanie wstecznie / opłata przy okienku) —
  // zdejmuje wymóg maszyny opłat w fazie „poa".
  poa_fees_settled_externally: boolean;
  // Opłata skarbowa od samego wniosku (ULICP 598 zł) — druga, obok 17 zł za
  // pełnomocnictwo: `application_fee_info` niesie kwotę domyślną, urząd i stan opłaty.
  application_fee: string | null;
  application_fee_not_required: boolean;
  application_fee_not_required_reason: string;
  application_fee_info: ApplicationFeeInfo | null;
  poas: ApplicationPoaLink[];
  maps: ApplicationMapRow[];
  attachments: ApplicationAttachmentRow[];
  own_document: ManagedFileMini | null;
  /** PDF własnego pliku po zapisie z edytora w przeglądarce (do kontroli). */
  own_document_pdf?: ManagedFileMini | null;
  compiled_pdf: ManagedFileMini | null;
  // Pliki, które jadą OBOK pakietu, a nie w nim: każdy plik pakietu, w którym backend
  // wykrył podpis elektroniczny (pełnomocnictwo PAdES, podpisana decyzja, czasem
  // potwierdzenie przelewu) — scalenie by go zniszczyło. Wysyłka dokłada je do
  // pozycji sprawy / gk_docs sama.
  separate_files?: SeparatePackageFile[];
  // Pisma pakietu (własny plik albo generaty) ze stanem pól ręcznych `#wpisz_…#`
  // — do przycisku „Edytuj w przeglądarce" i ostrzeżeń kreatora.
  document_fill?: DocumentFillRow[];
  /** Pakiet wgrany ręcznie (poprawiony poza systemem) zamiast złożonego kompilacją. */
  compiled_is_manual?: boolean;
  compiled_at: string | null;
  /** Opłaty, których bieżący kompilat NIE objął — skompilowano go przed przelewem.
   *  `paid`: przelew potwierdzono już po kompilacji (wystarczy skompilować ponownie). */
  package_missing_fees?: { label: string; paid: boolean }[];
  /** Opłaty, których potwierdzenie NIE POSZŁO do urzędu (wysłano bez opłaty) — do dosłania. */
  submitted_missing_fees?: { label: string; paid: boolean }[];
  // PNG podglądu zakresu: obiekty projektu wystylowane, działki wniosku na
  // niebiesko, pozostałe na szaro. Artefakt pochodny (poza pakietem PDF).
  scope_map_png: ManagedFileMini | null;
  scope_map_generated_at: string | null;
  // ULICP: szerokość pasa od sieci + ostatnio policzona powierzchnia terenu inwestycji
  // (cache do listy; druk i mapa liczą ją na żywo z bufora).
  ulicp_buffer_m: number;
  ulicp_area_m2: number | null;
  ulicp_area_computed_at: string | null;
  // Parametry obiektu z Załącznika C druku (C.4.1/C.4.4/C.4.5/C.4.6): AI proponuje
  // z wykazu urządzeń mapy, człowiek poprawia — i to jego wartości jadą do druku.
  ulicp_object_params: {
    powierzchnia?: string;
    szerokosc?: string;
    wysokosc?: string;
    uwagi?: string;
  };
  // Transient: parcels that could not be drawn on the scope map (missing geometry),
  // returned only by the generate-scope-map action so the UI can flag an incomplete
  // attachment. Not persisted on the row.
  scope_map_warnings?: string[];
  proceeding: string | null;
  proceeding_item: string | null;
  // Sprawa pakietu. „Zakończ" na OTWARTEJ sprawie zamyka ją z rozstrzygnięciem — właściciele
  // w wątku służą do wskazania rozstrzygniętych (pozytywne / częściowe, sprawy urzędowe).
  proceeding_info: {
    id: string; status: ProceedingStatus; kpa_days: number; scope: ProceedingScope;
    type_name: string; outcome: ProceedingOutcome; closed_at: string | null;
    /** Sprawę założyła wysyłka tego pakietu — odpięcie ją usuwa (delete_case). */
    auto: boolean;
    /** Lustro bramki tego odpięcia: autor, koordynator przedsięwzięcia, dyrektor, CEO. */
    can_detach_auto: boolean;
    owners: { id: string; full_name: string }[];
  } | null;
  // Pozycja „wniosek" sprawy związana z pakietem: własna (z wysyłki) albo istniejąca,
  // pod którą pakiet podpięto przed wysyłką (wysyłka dołoży do niej pliki).
  proceeding_item_info: {
    id: string; our_ref: string; app_status: ApplicationStatus; app_status_display: string | null;
    date_sent: string | null; channel_display: string | null;
  } | null;
  dispatch_info: PaperDispatchBrief | null; // przesyłka papierowa w dzienniku DLC_02 (faza „Wysyłka")
  edelivery_info: EdeliveryBrief | null; // przesyłka w kolejce e-Doręczeń (DLC_09)
  // „Wycofaj złożony wniosek" (tylko złożony wniosek urzędowy, inaczej null): `blocker` =
  // dlaczego już nie można (pismo wyszło / kanał poza systemem), `deletes_case` = sprawa
  // automatyczna zniknie (inaczej pismo wraca w niej do szkicu). Bramka: can_manage.
  withdrawal: {
    blocker: string | null;
    channel: string | null;
    // Co stanie się ze sprawą: usunięta (automatyczna, nic poza pismem wniosku) / zostaje,
    // a pismo w niej „Wycofany” / brak pisma w sprawie (pakiet odpięty — tylko DLC_02 / DLC_09).
    case_effect: "delete" | "mark" | "none";
    deletes_case: boolean;
    // System nie śledzi wysyłki pisma (brak wpisu DLC_02 i przesyłki DLC_09 — np. e-mail,
    // ePUAP, stary wniosek bez sprawy): opis do ostrzeżenia; wycofanie wymaga potwierdzenia.
    untracked: string | null;
  } | null;
  submitted_by_name: string;
  // Złożony wniosek wycofany, zanim pismo wyszło — status „Anulowany" + kto i kiedy wycofał.
  withdrawn_at: string | null;
  withdrawn_by_name: string | null;
  // Obieg akceptacji wysyłki (2026-09-25): koordynator kompletuje i przekazuje
  // (request-approval → pending_approval), pismo wypuszcza dyrektor / CEO / osoba
  // z can_approve. Kanał z przekazania podpowiada się przy zatwierdzeniu.
  approval_requested_at: string | null;
  approval_requested_by_name: string | null;
  approval_channel: string;
  approval_channel_ref: string;
  // Notatka dyrektora ze zwrotu do poprawy (czyszczona przy kolejnym przekazaniu).
  approval_review_note: string;
  // Kto i kiedy ZAAKCEPTOWAŁ wysyłkę przekazanego wniosku — składającym
  // (submitted_by_name) jest przekazujący. Puste = złożony bez akceptacji.
  submission_approved_at: string | null;
  submission_approved_by_name: string;
  // Lustro bramki submit: czy ZALOGOWANY może wypuścić pismo (dyrektor / CEO /
  // jawne can_approve w projekcie) — decyduje o formularzu wysyłki vs „Przekaż".
  can_approve_submission: boolean;
  // Lustro bramki withdraw-approval: „Wycofaj z akceptacji" ma tylko przekazujący
  // albo autor wniosku.
  can_withdraw_approval: boolean;
  // Pakiet WPW: rodzaje formularzy ENERGA obecne na mapie (DXF, 1–3) — sterują tym,
  // które szablony statusowe pokazuje faza dokumentu. Puste dla typów innych niż WPW.
  wpw_form_kinds: string[];
  // Pakiet WPW: ostrzeżenie bramki KW działki, które NIE blokuje druku (właściciele
  // inni niż w Dziale II księgi). Plansza zostaje na stronie, „Generuj"/„Skompiluj"
  // pytają o potwierdzenie. null = brak ostrzeżenia / nie pakiet WPW.
  wpw_kw_warning: WpwMassBlocked | null;
  // Stan kompletu formularzy zgody WPW: które rodzaje mapa wskazuje, które już
  // wygenerowano i czy własny plik przykrywa generaty. null poza pakietami WPW.
  wpw_forms_state: {
    // description: opis urządzeń TEGO rodzaju formularza (kubełek statusu działki,
    // wspólny dla pakietów wszystkich jej właścicieli) — edytowalny nad przyciskiem
    // kompletu; pusty = AI ułoży z mapy przy generowaniu.
    // stale: komplet powstał z innym opisem tego druku (albo bez niego) — kompilacja
    // pakietu wygeneruje komplet ponownie.
    kinds: {
      kind: string; label: string; generated: boolean; in_batch: boolean;
      stale: boolean; description: string;
    }[];
    batch: {
      generated_at: string; file: ManagedFileMini | null; kinds: string[]; stale: boolean;
    } | null;
    own_document_wins: boolean;
  } | null;
  // Rodzaj szablonu pisma proponowany dla TEGO wniosku — dla lokalizacyjnych do
  // GDDKiA dedykowany druk pasa drogowego (auto-wybór w oknie generowania).
  proposed_doc_template_kind: string;
  // Dane drogi krajowej (GDDKiA) do druku: z API mapy GDDKiA, edytowalne.
  road_data: ApplicationRoadData;
  // Podgląd weryfikacyjny danych drogi: trasa + słupki pikietażu na OSM,
  // regenerowany przy każdym „Pobierz kilometraż”. Display-only.
  road_map_png: ManagedFileMini | null;
  road_map_generated_at: string | null;
  parcels_brief: {
    id: string; teryt: string; short_number: string; obreb: string; commune: string;
    county: string; land_use: string; soil_class: string;
    project_name: string; connection_id: string;
    wypisy: { id: string; pages: string; page_count: number | null; file: ManagedFileMini | null }[];
  }[];
  recipient_office_detail: {
    id: string; name: string; kind_display: string; full_address: string;
    street: string; building_no: string; postal_code: string; city: string;
    department: string; contact_person: string; phone: string; email: string;
    bank_account: string; edelivery_address: string;
    /** Czy bieżący użytkownik może poprawić wpis rejestru (CEO / dyrektor techniczny). */
    can_edit: boolean;
  } | null;
  phases: ApplicationPhaseState[];
  config: ApplicationTypeConfig | null;
  stamp_duty_office: StampDutyOfficeInfo & { configured: boolean };
  checklist: ApplicationChecklistStatus[];
  poa_report?: {
    links: string[];
    warnings: string[];
    second_link_required?: boolean;
    attached?: number;
    removed?: number;
    // Human outcome — shown after the button so a no-op re-run is visibly a no-op.
    summary?: string;
  };
}

// Widok uwzględnienia działki przy generacji pakietów: czy WYMAGA AKCJI
// (access_mode ≠ „nie wymaga") czy jest tylko DODATKOWA (kontekst), plus strona
// własności (prywatna/urzędowa). Liczone na backendzie z access_mode + location_category.
// `state` to stan DO WYŚWIETLANIA: gdy na działce wisi już złożony wniosek (sprawa otwarta),
// piłka jest po stronie urzędu → „oczekiwanie na odpowiedź", a nie „wymaga akcji"; gdy sprawa
// zamknęła się pozytywnie (tytuł dostępu właściciela pozyskany) → „pozyskana" — załatwione, nie
// wraca do akcji po zakończeniu pakietu. `requires_action` zostaje przy starym znaczeniu
// (wymaga pozyskania) — trzyma preselekcję zakresu; gaśnie tylko dla działki pozyskanej.
export interface ParcelInclusion {
  access_mode: "consent" | "notification" | "not_required";
  access_mode_label: string;
  requires_action: boolean;
  // wniosek złożony (oba stany: czekamy / po terminie KPA)
  awaiting_response: boolean;
  // wniosek złożony, sprawa otwarta, termin KPA urzędu minął — monituj, nie składaj drugiego
  kpa_overdue: boolean;
  kpa_deadline: string | null;
  secured: boolean;
  state: "action" | "awaiting_response" | "kpa_overdue" | "secured" | "additional";
  state_label: string;
  kind: "private" | "office";
  kind_label: string;
}

// Wynik masowego zakładania wniosków lokalizacyjnych. Rozdzielone na „powstało"
// i „pominięto z powodem", bo przy kilku wnioskach naraz człowiek musi widzieć nie
// tylko ile ich jest, ale i czemu któregoś nie ma.
export interface BulkCreateApplicationsResult {
  created: {
    office_id: string; office_name: string;
    application_id: string; number: string; parcel_count: number;
  }[];
  skipped: {
    office_id: string; office_name: string;
    // existing = ma już aktywny wniosek; existing_case = sprawę z tym urzędem prowadzi
    // się już w Korespondencji; no_parcels = brak działek na bieżącej trasie;
    // unknown = adresat spoza listy właścicieli (lista zdążyła się zmienić)
    reason: "existing" | "existing_case" | "no_parcels" | "unknown";
    application_id?: string; number?: string;
    proceeding_id?: string; case_status?: "open" | "closed"; case_outcome?: string;
  }[];
  // Wynik po ludzku — żeby ponowne kliknięcie było WIDOCZNIE bez efektu.
  summary: string;
}

// Wynik masowego generowania formatek: co ruszyło i co pominięto (z powodem przy
// konkretnym wniosku, bo powody bywają różne — brak działek, brak geometrii, szablon).
export interface BulkGenerateFormatkiResult {
  started: {
    application_id: string; number: string; office_name: string;
    sheet_set_id: string; task_id: string;
    frames_count: number; skipped_parcels: string[];
  }[];
  failed: {
    application_id: string; number: string; office_name: string; reason: string;
  }[];
  summary: string;
}

export interface OfficeCaseSummary {
  id: string;
  type_name: string;
  status: "open" | "closed";
  outcome: "none" | "positive" | "negative" | "partial";
  // czy w sprawie jest już ZŁOŻONY wniosek (szkic = nadal nasz ruch)
  submitted: boolean;
  date_sent: string | null;
  our_ref: string;
  kpa_deadline: string | null;
  kpa_overdue: boolean;
  parcel_count: number;
}

export interface ApplicationsOverview {
  office_owners: {
    office_id: string; office_name: string; parcel_count: number;
    // rozbicie działek urzędu: nasz ruch / czekamy na odpowiedź po złożonym wniosku /
    // po terminie KPA (monituj) / pozyskane (sprawa zamknięta pozytywnie) / dodatkowe (kontekst)
    action_parcel_count: number;
    awaiting_parcel_count: number;
    overdue_parcel_count: number;
    secured_parcel_count: number;
    additional_parcel_count: number;
    parcels: { id: string; teryt: string; inclusion: ParcelInclusion }[];
    existing_application: { id: string; number: string; status: OfficialAppStatus } | null;
    // Sprawa urzędowa z Korespondencji (otwarta albo zamknięta pozytywnie) — wniosek
    // wysłany stamtąd też jest wnioskiem, więc bez pakietu to ONA zastępuje „Złóż".
    existing_case: OfficeCaseSummary | null;
  }[];
  // Działki prywatne (WPW) → wniosek „Pakiet WPW” (1 działka = 1 pakiet, strona faz).
  wpw_parcels: {
    parcel_id: string; teryt: string; owners: string[]; inclusion: ParcelInclusion;
    existing_application: { id: string; number: string; status: OfficialAppStatus } | null;
  }[];
  decisions_without_clause: {
    id: string; sign: string; kind: string; issuing: string; finality: DecisionFinality;
    has_active_application: boolean;
  }[];
  communes: string[];
  counties: string[];
  // Co wchodzi do wniosku o odrolnienie — obręb po obrębie, z powodem przy każdej działce.
  odrolnienie_scope: OdrolnienieScopeGroup[];
  // Co wchodzi do wniosku o ULICP — gmina po gminie; zaznaczone są działki BEZ planu
  // miejscowego (decyzję ULICP wydaje się właśnie w braku planu).
  ulicp_scope: UlicpScopeGroup[];
}

// Stan pokrycia planem miejscowym — cztery stany, bo „nie wiadomo" i „nie sprawdzono"
// znaczą co innego i żadne z nich nie może udawać braku planu.
export type MpzpStatus = "covered" | "none" | "unknown" | "unchecked";

export interface UlicpScopeGroup {
  commune: string;
  commune_label: string;
  county: string;
  voivodeship: string;
  preselected_count: number;
  covered_count: number;
  unchecked_count: number;
  parcels: {
    id: string;
    teryt: string;
    short_number: string;
    obreb: string;
    area_m2: number | null;
    preselected: boolean;
    mpzp_status: MpzpStatus;
    mpzp_status_label: string;
    mpzp_checked_at: string | null;
  }[];
}

// Teren inwestycji ULICP: pas wokół sieci docięty do działek wniosku. Powierzchnia
// liczy się zawsze z `buffer_m` — ręcznego nadpisania liczby nie ma.
export interface UlicpArea {
  buffer_m: number;
  buffer_default_m: number;
  buffer_min_m: number;
  buffer_max_m: number;
  // true = obrys narysowany ręcznie na mapie (pas od sieci go nie nadpisuje).
  is_customized: boolean;
  area_m2: number;
  computed_at: string | null;
  srid: number;
  geometry: GeoJsonGeometry | null;
  route: GeoJsonGeometry | null;
  per_parcel: {
    parcel_id: string;
    teryt: string;
    short_number: string;
    obreb: string;
    area_m2: number;
    parcel_area_m2: number | null;
    geometry: GeoJsonGeometry | null;
  }[];
  warnings: string[];
}

/** Pas służebności przesyłu wniosku regulacyjnego.
 *
 *  W odróżnieniu od ULICP nie ma tu JEDNEJ szerokości do ustawienia — bierze się ona
 *  z wytycznych TAURON per rodzaj urządzenia. Człowiek podaje tylko obrysy urządzeń,
 *  których nie ma w rysunku (`dimensions`), albo rysuje cały pas ręcznie. */
export interface RegulacjaArea {
  // true = obrys narysowany ręcznie na mapie (wytyczne go nie nadpisują).
  is_customized: boolean;
  area_m2: number;
  computed_at: string | null;
  srid: number;
  geometry: GeoJsonGeometry | null;
  // Sieć PROJEKTOWANA, z której powstał pas — SAME linie (urządzenia idą osobno,
  // żeby mapa rysowała je symbolami, a nie domyślnymi pinezkami Leafleta).
  route: GeoJsonGeometry | null;
  devices: { slug: string; kind: string; label: string; geometry: GeoJsonGeometry | null }[];
  per_parcel: {
    parcel_id: string;
    teryt: string;
    short_number: string;
    obreb: string;
    area_m2: number;
    parcel_area_m2: number | null;
    geometry: GeoJsonGeometry | null;
  }[];
  // {id obiektu | rodzaj urządzenia: {width_m, length_m}} — wymiar wpisany dla rodzaju
  // obowiązuje wszystkie urządzenia tego rodzaju na trasie.
  dimensions: Record<string, { width_m: number; length_m: number }>;
  // Urządzenia czekające na obrys. Dopóki tu są, NIE wchodzą do powierzchni.
  missing_dimensions: {
    object_id: string | null;
    kind: string;
    label: string;
    slug: string;
  }[];
  // Po jednym wierszu na RODZAJ urządzenia liczonego od obrysu — także tego z już
  // wpisanym wymiarem, żeby dało się poprawić literówkę. `missing` = ile sztuk tego
  // rodzaju wciąż czeka na obrys.
  device_summary: {
    kind: string;
    label: string;
    count: number;
    missing: number;
    width_m: number | null;
    length_m: number | null;
  }[];
  // Reguły szerokości do pokazania projektantowi — skąd się biorą metry.
  rules: { kind: string; label: string; needs_dimensions: boolean }[];
  // Treści pól formularza OMI (Projektowane/Istniejące urządzenia) — z ręcznych pól
  // działki; AI je proponuje, człowiek poprawia, pismo czyta przez #regulacja_urzadzenia_*#.
  device_descriptions: { projektowane: string; istniejace: string };
  // Wypełniane TYLKO w odpowiedzi na zapis wymiarów: wpisy, których backend nie przyjął
  // (zero, minus, tekst, ponad 100 m). Bez tego zła wartość znika bez śladu.
  rejected_dimensions?: { key: string; reason: string }[];
  warnings: string[];
}

export interface ConnectionFile {
  id: string;
  file: ManagedFileMini;
  size_bytes: number | null;
  description: string;
  created_at: string;
  uploaded_by_name: string | null;
  // Set when the file was uploaded from a milestone's details modal — the Pliki tab
  // annotates it ("z kamienia milowego: …"). `milestone` is the UUID, `milestone_key`
  // the board key, `milestone_label` the display name. All null for plain project files.
  milestone: string | null;
  milestone_key: string | null;
  milestone_label: string | null;
  // Pole wgrywania w oknie kamienia, gdy jeden krok wydaje dwa rodzaje artefaktu
  // (MDCP + FOTO). "" = pole domyślne; "foto" kieruje plik do INWENTARYZACJI zamiast MAP.
  milestone_slot: string;
  // True dla plików wywodzących się z dokumentów przetargowych źródłowego przetargu ZE
  // (auto-podpięte przy tworzeniu projektu) — zakładka Pliki grupuje je osobno.
  from_tender: boolean;
  // Tag folderu ręcznego (folders.py). "" = bez folderu (kosz „Inne").
  folder_slug: string;
}

// ── Drzewo folderów projektu (zakładka Pliki) ────────────────────────────────
// Deep-link do modułu-źródła pliku automatycznego; FE mapuje `kind`+`id` na trasę.
export interface FolderSourceRef {
  kind:
    | "route_version" | "wypis_order" | "parcel_wypis" | "connection_file"
    | "official_application" | "wpw_handover" | "mdcp_point" | "cost_estimate"
    | "technical_deliverable" | "decision" | "tender_contract"
    // Pozycja „odpowiedź" sprawy z Korespondencji — rozstrzygające pismo urzędu
    // rzutowane do folderu UZGODNIENIA I DECYZJE (extra.proceeding = id sprawy).
    | "proceeding_item"
    // Dokument pozyskanego tytułu prawnego (UZGODNIENIA I DECYZJE; zgody prywatne także
    // POROZUMIENIA PRIV / Uzgodnione) — extra.parcel / extra.owner.
    | "owner_access";
  id: string;
  extra: Record<string, unknown> | null;
}

// Znormalizowany wpis pliku — spina wiele modeli backendu.
export interface FolderFileEntry {
  managed_file_id: string;
  filename: string;
  url: string | null;
  size_bytes: number | null;
  created_at: string | null;
  source_label: string;
  source_ref: FolderSourceRef;
  read_only: boolean;
  // Ustawione tylko dla wpisów ręcznych → włącza usuwanie/edycję opisu inline.
  connection_file_id: string | null;
  // EFEKTYWNA poufność pliku (dziedziczy stan folderu, jawna flaga nadpisuje) +
  // jawna flaga (null = dziedziczone). Nieuprawnieni nie dostają poufnych wpisów wcale.
  is_confidential: boolean;
  confidential_own: boolean | null;
  // Poufność zamknięta na klucz (plik leży w folderze zastrzeżonym z definicji, np.
  // UMOWA PODPISANA) — nie da się jej zdjąć ani wynieść pliku do jawnego folderu.
  confidential_locked: boolean;
  // Lustro z dziennika korespondencji (dekretacja wpływu z projektem) — bez usuwania i
  // zmiany nazwy z zakładki Pliki; odpina je adresat/dyrektor w widoku korespondencji.
  from_correspondence: boolean;
  // Zamówienia, które ten plik DZIŚ widzą (backend liczy to na żywo z tych samych reguł,
  // co projekcja w zamówieniu). Puste dla poufnych; brak pola u nieuprawnionych.
  shared_orders?: SharedOrderRef[];
}

// Powód, dla którego zamówienie widzi folder/plik: „all" = udostępnienie „wszystkie
// niepoufne", „folder" = folder wskazany wprost, „inherited" = podfolder takiego folderu,
// „file" = pojedynczy plik. Tryb rozstrzyga, czy da się cofnąć punktowo.
export type ShareMode = "all" | "folder" | "inherited" | "file";

export interface SharedOrderRef {
  order_id: string;
  order_number: string;
  contractor_name: string;
  mode: ShareMode;
}

// Wiersz panelu „Udostępnienia" — jedno zamówienie z plikami tego projektu.
export interface ProjectFileShareRow {
  order_id: string;
  order_number: string;
  contractor_name: string;
  status: string;
  status_display: string;
  // „all" = żywa reguła „wszystkie niepoufne"; „selected" = wskazane foldery/pliki.
  mode: "all" | "selected";
  folder_count: number;
  file_count: number;
  // Ile plików realnie widzi dziś to zamówienie (po odjęciu poufnych).
  shared_file_count: number;
  created_at: string | null;
}

export interface FolderNode {
  slug: string;
  label: string;
  kind: "auto" | "manual";
  // Folder własny projektu (CustomFolder) — tylko takie można przemianować/usunąć.
  is_custom: boolean;
  read_only: boolean;
  source_hint: string;
  // EFEKTYWNA poufność (dziedziczenie w dół z jawnymi nadpisaniami — poufny rodzic czyni
  // poufnym poddrzewo, ale podrzędny węzeł można jawnie „odpoufnić"). Nieuprawnieni
  // (spoza koordynator/dyrektor/CEO) dostają drzewo bez poufnych węzłów/wpisów.
  is_confidential: boolean;
  // Jawna flaga tego węzła: true/false = nadpisanie, null = stan dziedziczony.
  confidential_own: boolean | null;
  // Folder zastrzeżony z definicji taksonomii (UMOWA PODPISANA) — poufności nie da się
  // zdjąć, folder nie podlega udostępnieniu, plików nie da się z niego wynieść.
  confidential_locked: boolean;
  // Wyjątek od szczelności folderu zastrzeżonego (KORESPONDENCJA): wolno PRZENIEŚĆ plik
  // do innego folderu — wynoszony plik zachowuje jawną flagę poufności (backend).
  move_out_allowed: boolean;
  // Puste, gdy źródło folderu odpowiedziało. Niepuste = TEN folder nie dał się zbudować
  // (backend zaloguje traceback), a reszta zakładki działa — pokazujemy komunikat zamiast
  // udawać, że folder jest pusty. Dotąd taka awaria wywracała cały endpoint (500).
  source_error: string;
  file_count: number; // rekurencyjny
  entries: FolderFileEntry[];
  children: FolderNode[];
  // Zamówienia, które ten folder DZIŚ widzą (patrz SharedOrderRef). Puste dla poufnych;
  // backend wysyła je tylko temu, kto może udostępniać (can_share_to_order).
  shared_orders?: SharedOrderRef[];
}

export interface FolderTreeResponse {
  folders: FolderNode[];
  // Czy bieżący użytkownik może oznaczać foldery/pliki jako poufne (koordynator/dyrektor/CEO).
  can_manage_confidential: boolean;
  // Czy może udostępniać foldery/pliki w zleceniach (kierownik przedsięwzięcia/dyrektor/CEO).
  can_share_to_order: boolean;
  // Czy może tworzyć foldery własne projektu (kierownik przedsięwzięcia/dyrektor/CEO).
  can_create_folder: boolean;
  // Czy może odpinać wypisy od działek z folderu WYPISY (kierownik przedsięwzięcia/dyrektor/CEO).
  can_detach_wypis?: boolean;
  // Udostępnienia plików tego projektu w zamówieniach (tylko dla can_share_to_order).
  shares?: ProjectFileShareRow[];
  // Numer zamówienia, którego dotyczyła ostatnia operacja udostępnienia/cofnięcia
  // (tylko w odpowiedzi POST folders/share-to-order/ i folders/unshare-from-order/).
  order_number?: string;
  shared?: boolean;
  // Liczba plików przetworzonych ostatnią operacją kopiuj/przenieś (tylko w odpowiedzi
  // POST folders/transfer/).
  transferred?: number;
  // Liczba plików przeniesionych do kosza „Inne / bez folderu" (tylko w odpowiedzi
  // POST folders/delete/).
  moved_to_inne?: number;
}

// Zamówienie do wyboru w oknie „udostępnij w zleceniu" (zakładka Pliki).
export interface ShareableOrder {
  id: string;
  order_number: string;
  contractor_name: string;
  status: string;
  status_display: string;
  order_type_display: string;
  // Zamówienie ma już jakieś udostępnienie plików projektu.
  has_project_files_share: boolean;
  // Tryb tego udostępnienia albo null, gdy zamówienie go nie ma.
  share_mode: "all" | "selected" | null;
  // Udostępnienie pochodzi z INNEGO projektu ZE — jedno zamówienie ma jedno udostępnienie,
  // więc takiego zamówienia nie da się tu użyć.
  share_from_other_project: boolean;
}

// ── Załączniki do wniosku regulacyjnego (per działka / projekt) ──────────────
export interface RegulatoryAttachment {
  id: string;
  connection: string;
  parcel: string | null;
  kind: string;
  kind_display: string;
  file: ManagedFileMini;
  note: string;
  uploaded_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface RegulatoryChecklistItem {
  kind: string;
  label: string;
  level: "parcel" | "project";
  source: "uploaded" | "auto" | "missing";
  present: boolean;
  files: ManagedFileMini[];
}

export interface RegulatoryChecklist {
  items: RegulatoryChecklistItem[];
  missing: string[]; // kinds still missing
}

// ── Generacja dokumentów (szablony ODF) ──────────────────────────────────────
export interface DocVariable {
  name: string;
  description: string;
  source?: string; // skąd zmienna jest liczona/pobierana
}

export interface DocVariableGroup {
  key: string;
  label: string;
  description: string;
  note: string | null;
  contexts?: string[]; // konteksty generacji, w których grupa jest dostępna
  variables: DocVariable[];
}

// Soft category of a template — filters the per-context generate modals. "general"
// (uniwersalny) shows in every context, "none" (bez typu) in NONE of them — to jest
// kubełek szablonów wycofanych z obiegu (dawna „Zgoda prywatna", zastąpiona pakietami
// WPW). Mirrors backend TemplateKind. The app_* kinds (one per wnioski procedure type)
// all share the `official_application` generation context.
export type DocTemplateKind =
  | "general"
  | "none"
  | "regulatory_application"
  | "app_lokalizacyjny"
  | "app_lokalizacyjny_gddkia"
  | "app_klauzula"
  | "app_wpw"
  | "app_wpw_projektowane"
  | "app_wpw_likwidowane"
  | "app_wpw_modernizowane"
  | "app_wpw_porozumienie_td"
  | "app_wpw_pismo_przewodnie"
  | "app_odrolnienie"
  | "app_ulicp"
  | "app_art124"
  | "ze_tom_titles"
  | "ze_report_form_energa";

export const DOC_TEMPLATE_KIND_LABELS: Record<DocTemplateKind, string> = {
  general: "Uniwersalny",
  none: "Bez typu (nieużywany)",
  regulatory_application: "Wniosek o regulację - TD Oddział Gliwice",
  app_lokalizacyjny: "Wniosek lokalizacyjny (urząd-właściciel)",
  app_lokalizacyjny_gddkia: "Wniosek lokalizacyjny — GDDKiA (pas drogowy)",
  app_klauzula: "Klauzula ostateczności",
  app_wpw: "Pakiet WPW — karta informacyjna",
  app_wpw_projektowane: "Pakiet WPW — formularz ENERGA (projektowane)",
  app_wpw_likwidowane: "Pakiet WPW — formularz ENERGA (likwidowane)",
  app_wpw_modernizowane: "Pakiet WPW — formularz ENERGA (modernizowane)",
  app_wpw_porozumienie_td: "Pakiet WPW — porozumienie TAURON (TD)",
  app_wpw_pismo_przewodnie: "Pakiet WPW — pismo przewodnie (wysyłka listowna)",
  app_odrolnienie: "Odrolnienie (wyłączenie z produkcji)",
  app_ulicp: "Wniosek o ULICP",
  app_art124: "Wniosek art. 124 UGN",
  ze_tom_titles: "TOM II — Tytuły prawne do nieruchomości",
  // Plik .docx wzoru operatora wypełniany po układzie tabel (bez #zmiennych#) przez
  // generator raportu okresowego — nie występuje w żadnym oknie generowania.
  ze_report_form_energa: "Raport o postępie prac — formatka ENERGA (Zał. nr 4 do OWU)",
};

// Wszystkie rodzaje „Pakiet WPW …" — karta informacyjna + formularze operatora.
// Zastąpiły wycofaną „Zgodę prywatną", więc okno generowania zgody właściciela
// wybiera szablon spośród NICH (patrz GenerateConsentModal).
export const WPW_PACKAGE_KINDS = [
  "app_wpw",
  "app_wpw_projektowane",
  "app_wpw_likwidowane",
  "app_wpw_modernizowane",
  "app_wpw_porozumienie_td",
] as const satisfies readonly DocTemplateKind[];

// ── Masowe pakiety WPW (formularze WNU) ────────────────────────────────────────
// ENERGA: 3 formularze per status urządzeń. TAURON: JEDEN dokument — porozumienie TD,
// którego wzór opisuje cały zakres działki łącznie (kubełek opisu „wspolny").
export type WpwEnergaFormKind =
  | "app_wpw_projektowane"
  | "app_wpw_likwidowane"
  | "app_wpw_modernizowane"
  | "app_wpw_porozumienie_td";

export const WPW_ENERGA_KIND_SHORT: Record<WpwEnergaFormKind, string> = {
  app_wpw_projektowane: "Projektowane",
  app_wpw_likwidowane: "Likwidowane",
  app_wpw_modernizowane: "Modernizowane",
  app_wpw_porozumienie_td: "Porozumienie TD",
};

// Rodzaje formularzy WNU wg operatora (OSD) projektu — lustro backendowego
// wpw_mass.breakdown.OPERATOR_FORM_KINDS. Operator nieznany/„inny" → zestaw ENERGA.
export const WPW_ENERGA_KINDS: WpwEnergaFormKind[] = [
  "app_wpw_projektowane",
  "app_wpw_likwidowane",
  "app_wpw_modernizowane",
];
export const WPW_TAURON_KINDS: WpwEnergaFormKind[] = ["app_wpw_porozumienie_td"];

export const wpwFormKindsFor = (operator: string | undefined): WpwEnergaFormKind[] =>
  operator === "tauron" ? WPW_TAURON_KINDS : WPW_ENERGA_KINDS;

// Kubełek opisu urządzeń (klucz consent_work_descriptions) odpowiadający rodzajowi
// formularza — formularz ENERGA bierze opis swojego statusu, porozumienie TD wspólny.
export type WpwConsentStatus = "projektowane" | "likwidowane" | "modernizowane" | "wspolny";

export const WPW_KIND_STATUS: Record<WpwEnergaFormKind, WpwConsentStatus> = {
  app_wpw_projektowane: "projektowane",
  app_wpw_likwidowane: "likwidowane",
  app_wpw_modernizowane: "modernizowane",
  app_wpw_porozumienie_td: "wspolny",
};

export interface WpwMassBreakdownEntry {
  element_type: string;
  kind: "line" | "object";
  label: string;
  status: "projektowane" | "likwidowane" | "modernizowane" | null;
  length_m?: number;
  count?: number;
}

// Etykieta CAD dotycząca działki: co mówi o rodzaju prac i gdzie leży względem
// wielokąta (inside=false → distance_m za granicą, przypisanie niepewne).
export interface WpwMassLabelFinding {
  text: string;
  layer: string;
  statuses: string[];
  inside: boolean;
  distance_m: number;
}

// Wątpliwość analizy mapy — praca z etykiety bez warstwy, etykieta o kilku pracach,
// etykieta tuż za granicą. Każda czeka na zatwierdzenie przez człowieka.
export interface WpwMassScopeDoubt {
  code: "label_status_beyond_layers" | "label_multi_status" | "label_near_boundary" | string;
  reason: string;
  labels: string[];
  statuses: string[];
}

export interface WpwMassBreakdown {
  entries: WpwMassBreakdownEntry[];
  approximate: boolean;
  unclassified: string[];
  labels: WpwMassLabelFinding[];
  // prace nazwane WYŁĄCZNIE przez etykiety wewnątrz działki (dokładają formularz)
  label_statuses: string[];
  doubts: WpwMassScopeDoubt[];
  doubt_fingerprint: string;
}

// Stan zatwierdzenia zakresu przez człowieka (ProjectParcel.wpw_scope_review).
export interface WpwMassScopeReview {
  doubts: WpwMassScopeDoubt[];
  fingerprint: string;
  needed: boolean;
  confirmed: { by: string; at: string | null; note: string; form_kinds: string[] } | null;
  // zatwierdzenie istnieje, ale dla innego zestawu wątpliwości (mapa się zmieniła)
  stale: boolean;
}

export interface WpwMassBlocked {
  code: string;
  reason: string;
  // Blokada POMIJALNA z potwierdzeniem (dziś: błąd analizy KW `kw_owners_unreadable`) —
  // kreator pyta przed generacją/kompilacją i zapisuje zwolnienie na działce.
  skippable?: boolean;
}

export interface WpwMassFormDocument {
  id: string;
  kind: string;
  version: number;
  status: "success" | "failure";
  error: string;
  template_name: string;
  file: ManagedFileMini | null;
  odt_file: ManagedFileMini | null;
  created_at: string;
  /** Znaczniki `#wpisz_…#` wciąż w ODT — do uzupełnienia w edytorze; blokują kompilację. */
  unfilled_fields?: string[];
}

export interface WpwMassCompilation {
  id: string;
  version: number;
  contents: string[];
  file: ManagedFileMini | null;
  created_at: string;
}

// ── WPW — ścieżka listowna (wpw_mass.mail_letter) ──────────────────────────────
// Stan wyliczony listu automatycznego jednego właściciela: brak pakietu / gotowy /
// zaplanowano / wstrzymano / wstrzymano ręcznie / zlecono wysyłkę / wysłano / zwrot /
// wynik / anulowano. Lekki opis (wiersz przeglądu, karta właściciela) i pełny wiersz API.
export type WpwMailState =
  | "none"
  | "ready"
  | "scheduled"
  | "held"
  | "blocked"
  | "dispatched"
  | "sent"
  | "undeliverable"
  | "dispatch_rejected"
  | "returned"
  | "decided_agreement"
  | "decided_refusal"
  | "decided_cancel"
  | "decided_outdated"
  | "cancelled";

export const WPW_MAIL_STATE_LABELS: Record<WpwMailState, string> = {
  none: "brak pakietu",
  ready: "gotowy",
  scheduled: "zaplanowano wysyłkę",
  held: "wstrzymano",
  blocked: "wstrzymano ręcznie",
  dispatched: "zlecono wysyłkę",
  sent: "wysłano",
  undeliverable: "zwrot niedoręczony",
  dispatch_rejected: "zlecenie odrzucone",
  returned: "zwrot — do rozstrzygnięcia",
  decided_agreement: "uzgodnienie",
  decided_refusal: "odmowa",
  decided_cancel: "zwrot nie podpięty",
  decided_outdated: "nieaktualny",
  cancelled: "anulowano",
};

/** Wysłany list, któremu kolejna wgrana trasa zmieniła przebieg na działce względem trasy
    z chwili wysyłki — „może być nieaktualny”. Gaśnie, gdy list ma wynik. */
export interface WpwMailOutdatedInfo {
  outdated: boolean;
  outdated_at: string | null;
  outdated_reason: string;
}

/** Poprzedni (wysłany) list pakietu — przy liście kolejnym. */
export interface WpwMailPreviousLetter extends WpwMailOutdatedInfo {
  id: string;
  state: WpwMailState;
  state_display: string;
  print_order_number: number | null;
  dispatched_at: string | null;
}

export type WpwMailDecisionValue = "" | "agreement" | "refusal" | "cancel_attach" | "outdated";

export interface WpwMailPrintOrderBrief {
  id: string;
  number: number;
  status: string;
  status_display: string;
  tracking_number: string;
}

/** Część listu otwierana w edytorze online: pismo przewodnie albo kopia formularza w liście.
    Poprawka dotyczy tylko listu — pakiet terenowy i jego formularze zostają bez zmian. */
export interface WpwMailLetterPart {
  part: string; // "cover" albo rodzaj formularza (app_wpw_*)
  label: string;
  editor_kind: "wpw_mail_cover" | "wpw_mail_form";
  editor_id: string;
  has_source: boolean; // false = formularz wgrany jako gotowy PDF, nie ma czego edytować
  edited: boolean;
  edited_at: string | null;
  edited_by_name: string;
  error: string; // poprawka zapisana, ale konwersja do PDF padła — list czeka
}

export interface WpwMailLetterSummary extends WpwMailOutdatedInfo {
  id: string;
  state: WpwMailState;
  state_display: string;
  status: "ready" | "scheduled" | "held" | "dispatched" | "cancelled";
  file: ManagedFileMini | null;
  cover_file: ManagedFileMini | null;
  compiled_at: string | null;
  is_stale: boolean;
  stale_reason: string;
  due_at: string | null;
  scheduled_from: string | null;
  hold_reason: string;
  hold_reason_display: string;
  cancel_reason: string;
  cancel_reason_display: string;
  blocked: boolean; // blokada skuteczna (właściciel ALBO cała działka)
  blocked_reason: string;
  owner_blocked: boolean; // blokada na karcie właściciela
  parcel_blocked: boolean; // blokada całej działki (Dane ogólne)
  cancel_note: string;
  print_order: WpwMailPrintOrderBrief | null;
  returned_at: string | null;
  decision: WpwMailDecisionValue;
  decision_display: string;
  attached: boolean; // zlecenie podpięte pod właściciela (Podstawa dostępu → Listy)
  detached: boolean; // było podpięte, odpięte ręcznie
  last_error: { code: string; reason: string } | null;
  /** Czekający list bez terminu przez poprzedni list (kolejny — tylko ręcznie; po nieaktualnym —
      automat od nowego zamówienia WPW); "" = zwykły przypadek. */
  clock_note: string;
  /** List kolejny: dlaczego jeszcze nie można go wysłać (poprzedni bez wyniku); "" = można. */
  send_blocker: string;
  /** List kolejny: poprzedni, już wysłany list tego pakietu. */
  previous: WpwMailPreviousLetter | null;
  /** Dlaczego listu nie da się (już) poprawić w edytorze; "" = można. */
  edit_blocker?: string;
  /** Części do poprawienia w edytorze (puste, gdy list jest zlecony / anulowany). */
  parts?: WpwMailLetterPart[];
}

// Wiersz przeglądu bez pakietu listownego: dlaczego (TAURON / brak szablonu / jeszcze nie skompilowano).
export interface WpwMailMissing {
  state: "none";
  state_display: string;
  reason: string;
  /** List kolejny nie powstał — poprzedni (wysłany) i tak jest w wierszu. */
  previous?: WpwMailPreviousLetter | null;
}

export interface WpwMailLetter extends WpwMailOutdatedInfo {
  id: string;
  application: { id: string; number: string; status: string; connection_id: string };
  owner: { id: string; full_name: string; full_address: string } | null;
  parcel: { id: string; teryt: string; short_number: string; obreb: string } | null;
  status: WpwMailLetterSummary["status"];
  status_display: string;
  state: WpwMailState;
  state_display: string;
  file: ManagedFileMini | null;
  cover_file: ManagedFileMini | null;
  template_name: string;
  compiled_at: string | null;
  compiled_by_name: string | null;
  is_stale: boolean;
  stale_reason: string;
  scheduled_from: string | null;
  due_at: string | null;
  hold_reason: string;
  hold_reason_display: string;
  held_at: string | null;
  cancel_reason: string;
  cancel_reason_display: string;
  cancel_note: string;
  cancelled_at: string | null;
  cancelled_by_name: string | null;
  // Blokady trwałe żyją poza listem: właściciel (karta właściciela) i cała działka (Dane ogólne).
  owner_blocked: boolean;
  owner_blocked_reason: string;
  parcel_blocked: boolean;
  parcel_blocked_reason: string;
  effective_blocked: boolean;
  effective_blocked_reason: string;
  print_order: (WpwMailPrintOrderBrief & { name: string; receipt_confirmed: boolean }) | null;
  dispatched_at: string | null;
  proceeding_item: string | null;
  attached_at: string | null;
  detached: boolean;
  return_entry: {
    id: string; status: string; status_display: string; date: string | null;
    counterparty_name: string; files: ManagedFileMini[];
  } | null;
  returned_at: string | null;
  decision: WpwMailDecisionValue;
  decision_display: string;
  decided_by_name: string | null;
  decided_at: string | null;
  decision_note: string;
  /** Wersja trasy obowiązująca w chwili wysyłki (punkt odniesienia dla „może być nieaktualny”). */
  route_version: string | null;
  route_version_label: string;
  clock_note: string;
  send_blocker: string;
  previous_letter: WpwMailPreviousLetter | null;
  can_manage: boolean;
  can_decide: boolean;
  created_at: string;
  updated_at: string;
}

/** „Opcje dla zaznaczonych” w liście listów automatycznych (wpw-mail-letters/bulk/). */
export type WpwMailBulkAction = "send_now" | "hold" | "resume" | "cancel";

export interface WpwMailBulkResult {
  done: WpwMailLetter[];
  failed: { id: string; owner: string | null; error: string }[];
}

// Pakiet WPW, któremu nowa trasa zdezaktualizowała mapę — wynik zadania przeliczenia działek
// (`wpw_stale`), do komunikatu potwierdzanego po wgraniu trasy.
export interface WpwRouteStalePackage {
  application_id: string;
  number: string;
  parcel_id: string;
  teryt: string;
  short_number: string;
  owner: string;
  code: string;
  reason: string;
  letter_id: string | null;
  letter_state: WpwMailState;
  letter_held: boolean;
  /** Wysłany list tego pakietu może być nieaktualny (trasa zmieniła się na działce po wysyłce). */
  sent_letter_outdated?: boolean;
  sent_letter_print_order?: number | null;
}

export interface WpwMailSettings {
  id: string;
  auto_dispatch_days: number;
  updated_by_name: string | null;
  updated_at: string;
}

export interface WpwReturnItem {
  id: string;
  application: { id: string; number: string; status: string } | null;
  parcel_id: string | null;
  teryt: string;
  pages: string;
  matched_by: "qr" | "ai" | "manual";
  confidence: number | null;
  has_annotations: boolean;
  annotations: { page: number; quote: string; kind: string; note: string }[];
  detected_z7_consent_form: string;
  status: "proposed" | "accepted" | "rejected" | "applied";
  review_note: string;
  reviewed_by: string | null;
  package_pdf: ManagedFileMini | null;
  applied_at: string | null;
}

export interface WpwReturnBatch {
  id: string;
  connection_id: string;
  order_id: string | null;
  file: ManagedFileMini | null;
  page_count: number | null;
  status: "uploaded" | "segmenting" | "proposed" | "applied" | "cancelled";
  unmatched_pages: number[];
  ai_cost_usd: string | null;
  extracted_at: string | null;
  uploaded_by: string | null;
  created_at: string;
  items_count: number;
  items?: WpwReturnItem[];
}

// Werdykt „droga wewnętrzna" — WYŁĄCZNIE z księgi wieczystej. Użytek „dr" z wypisu ani
// prywatna własność nie są dowodem; bez księgi stan to zawsze "unknown".
export interface WpwRoadVerdict {
  state: "internal" | "road" | "not_road" | "unknown";
  label: string;
  note: string;
  entries: string[];
  manual?: boolean; // werdykt z decyzji koordynatora (road_override), nie z samej księgi
}

// Bramka: działka prywatna z ≥4 JEDNOSTKAMI ZGODY (małżeństwo = 1) nie idzie do druku
// bez przeanalizowanej księgi. `satisfied` mówi o stanie księgi, `blocker` o egzekwowaniu
// (znika po świadomym zwolnieniu — `override`). `warning` = rozbieżność właścicieli
// z Działem II księgi: NIE blokuje druku, ale kreator żąda potwierdzenia przed startem,
// a wynik zadania niesie ją w `warnings[]`.
export interface WpwKwGate {
  required: boolean;
  threshold: number;
  units: number;
  state: "ok" | "missing_number" | "missing_book" | "pending" | "owners_unreadable" | "owner_mismatch";
  satisfied: boolean;
  blocker: WpwMassBlocked | null;
  warning: WpwMassBlocked | null;
  override: { active: boolean; reason: string; by: string; at: string | null } | null;
}

// Jednostka zgody = jeden podpis do zebrania. Małżeństwo we wspólności to jedna
// jednostka o dwóch WNU (stąd lista `owner_ids`/`applications`).
export interface WpwConsentUnit {
  key: string;
  owner_ids: string[];
  names: string[];
  label: string;
  marital: boolean;
  share_raw: string;
  share_text: string;
  share_value: number | null;
  excluded: boolean;
  excluded_reason: string;
  applications: { id: string; number: string; status: string }[];
}

export interface WpwConsentScope {
  unit_count: number;
  units: WpwConsentUnit[];
  road: WpwRoadVerdict;
  kw_gate: WpwKwGate;
  total_share: { text: string; unknown_units: number };
  selected_units: number;
  selected_share: { text: string; unknown_units: number };
  // droga wewnętrzna wg KW → podpowiedź większości udziałów zamiast kompletu zgód
  hint: {
    code: "internal_road_majority";
    reached: boolean;
    text: string;
    selected_units: number;
    selected_share_text: string;
    unknown_units: number;
  } | null;
  // ≥6 jednostek, a księga NIE wskazuje drogi wewnętrznej → dopytaj ZE/kierownika
  alert: { code: "ask_ze_majority"; text: string } | null;
}

export interface WpwMassRow {
  parcel: {
    id: string;
    teryt: string;
    short_number: string;
    obreb: string;
    commune: string;
    status: string;
    kw_number: string;
    energa_work_description: string;
    // Opis urządzeń per status (projektowane/likwidowane/modernizowane) — źródło dla
    // formularzy WPW; każdy formularz bierze kubełek swojego statusu.
    consent_work_descriptions: Record<string, string>;
    // Pochodzenie kubełka opisu: manual (wpisany ręcznie — „Przygotuj i sprawdź" go nie
    // nadpisze) | ai (Generuj opisy) | review (oględziny mapy w sekwencji). Brak = nieznane
    // (traktowane jak ręczne).
    consent_description_sources?: Record<string, "manual" | "ai" | "review">;
    // widok uwzględnienia: wymaga akcji (consent/notification) vs dodatkowa (not_required)
    inclusion: ParcelInclusion;
    // zakres zgód działki: jednostki + udziały, werdykt KW, bramka KW, podpowiedzi
    consent: WpwConsentScope;
    // wątpliwości analizy etykiet CAD i ich zatwierdzenie przez człowieka
    scope_review: WpwMassScopeReview;
  };
  // Wiersz jest per (działka, WŁAŚCICIEL) — każdy współwłaściciel = osobny WNU.
  // owner=null gdy działka nie ma właściciela (wiersz informacyjny).
  owner: { id: string; full_name: string; declared_status: string | null } | null;
  owners: { id: string; full_name: string; declared_status: string | null }[];
  application: { id: string; number: string; status: string; compiled_at: string | null } | null;
  // pakiet świadomie pominięty w terenie (zgody części współwłaścicieli drogi wewnętrznej)
  field_excluded: boolean;
  field_excluded_reason: string;
  breakdown: WpwMassBreakdown;
  auto_form_kinds: string[];
  form_kinds: string[];
  effective_form_kinds: string[];
  map: { ok: boolean; code?: string; reason?: string; label?: string };
  wypis: { ok: boolean; code?: string; reason?: string; filename?: string; pages?: number[] };
  form_documents: WpwMassFormDocument[];
  // Własny plik wniosku wgrany na stronie pojedynczego WNU — ZASTĘPUJE formularze w pakiecie.
  own_document: ManagedFileMini | null;
  compilations: WpwMassCompilation[];
  quality?: { ready: boolean; code: string; reason: string; checked_at?: string } | null;
  // Ścieżka listowna: osobny stan obok pakietu terenowego (null = wniosek bez kompilacji).
  mail_package?: WpwMailLetterSummary | WpwMailMissing | null;
  return_items: {
    id: string; status: string; batch_id: string; pages: string; matched_by: string;
    has_annotations: boolean; package_pdf: ManagedFileMini | null; created_at: string;
  }[];
  blocked: WpwMassBlocked[];
}

// Pełnomocnictwo zlecenia w przeglądzie masówki WPW: numer, na kogo i do kiedy ważne,
// albo powód, dlaczego nie ma czego wydrukować na porozumieniu.
export type WpwOrderPoa =
  | { ok: false; reason: string }
  | {
      ok: true;
      id: string;
      number: string;
      attorney: string;
      valid_until: string | null;
      validity: "active" | "expiring" | "expired" | "inactive";
    };

export interface WpwMassOverview {
  // null w trybie „connection" (sekwencja masowa bez zlecenia — powstaje w kroku 5)
  order: { id: string; number: string; order_type: string; status: string; contractor: string | null } | null;
  mode: "order" | "connection";
  connection_id: string;
  project_name: string;
  // operator (OSD) klienta wykryty automatycznie — pod niego dobrane są szablony
  operator: string;
  client_name: string;
  templates: Record<string, { template_id: string; name: string; operator: string; exact: boolean } | null>;
  // Pełnomocnictwo opracowującego na tym zleceniu — obecne tylko wtedy, gdy szablon
  // operatora drukuje jego numer (TAURON: porozumienie TD). null = nie dotyczy.
  poa: WpwOrderPoa | null;
  can_mutate: boolean;
  // zakładanie WNU (draftów) nie wymaga `can_submit` — osobna, luźniejsza bramka
  can_found_applications: boolean;
  can_handoff: boolean;
  // trwały szkic sekwencji (tryb „connection"): wybrane działki + krok kreatora.
  // status „completed" = krok 5 zakończył ją BEZ zamówienia (pakiety zapisane) — kreator
  // wraca wtedy do kroku 5 z listą zamówień do wniosku o zmianę / opcją zamówienia teraz.
  sequence: {
    id: string; selected_parcels: string[]; step: string;
    status: "active" | "completed"; completed_at: string | null;
  } | null;
  // ostrzeżenie, gdy koncepcja jest w niewiążącym wariancie, a wiążący (czytany przez
  // WPW/AI) jest pusty → „0 obiektów / brak geometrii" mimo poprawnego uploadu
  variant_warning: WpwVariantWarning | null;
  rows: WpwMassRow[];
}

// Dodatkowe strony A4 pakietu WPW (krok „Generuj i kompiluj"): PDF doklejany do
// egzemplarza właściciela ZA formularzami, PRZED mapą (formatką A3). all_parcels =
// wchodzi do pakietów wszystkich działek sekwencji; inaczej tylko do parcel_ids.
//
// Pakiet niesie DWA komplety formularzy z mapą: pierwszy zostaje u właściciela, drugi
// („egzemplarz WPW") wraca podpisany do zbierającego. `placement` mówi, na końcu sekcji
// A4 której części ląduje strona — u właściciela, u zbierającego, albo w obu.
export type WpwExtraPagePlacement = "owner" | "collector" | "both";

export interface WpwMassExtraFile {
  id: string;
  label: string;
  file: ManagedFileMini | null;
  page_count: number | null;
  position: number;
  all_parcels: boolean;
  parcel_ids: string[];
  placement: WpwExtraPagePlacement;
  created_at: string | null;
  uploaded_by: string;
}

export interface WpwVariantWarning {
  code: "binding_variant_poor";
  binding: { variant: string; version: string | null; objects: number; lines: number };
  richer: { variant: string; version: string | null; objects: number; lines: number };
  reason: string;
}

export interface DocTemplate {
  id: string;
  name: string;
  kind: DocTemplateKind;
  operator: string; // energa / tauron / inny — dobór szablonów WPW per operator klienta
  description: string;
  variables: string[];
  /** Pola `#wpisz_…#` — uzupełniane po wyrenderowaniu, w edytorze w przeglądarce. */
  manual_fields?: string[];
  author: { id: string; name: string };
  file: ManagedFileMini | null;
  /** Numer bieżącej wersji pliku (1 = pierwotny upload, rośnie z każdą podmianą). */
  current_version: number;
  created_at: string;
  updated_at: string;
}

/** Jedna wgrana rewizja pliku szablonu — historia „kto kiedy wgrał". */
export interface DocTemplateVersion {
  id: string;
  version: number;
  file: ManagedFileMini | null;
  variables: string[];
  uploaded_by_name: string | null;
  created_at: string;
}

// Generation context: what object the document is generated FOR (backend resolvers.py).
export type DocGenContextKind = "private_consent" | "regulatory_application";

// ── Anonimizacja PDF (AI wskazuje dane osobowe, Celery je zamalowuje) ─────────
// Presety modeli to te same wiersze co w pickerze ARON-a (backend: aron modes.work_modes).
export interface DocAnonymizationModel {
  value: string;
  label: string;
  model: string;
  cost: string;
  detail: string;
  hint: string;
  /** Czy model czyta obrazy — bez tego skany (brak warstwy tekstowej) nie przejdą. */
  vision: boolean;
}

// „cancelled" = przerwane ręcznie („Anuluj" na wierszu), osobno od „failure" —
// decyzja użytkownika nie jest awarią.
export type DocAnonymizationStatus =
  | "pending"
  | "running"
  | "success"
  | "failure"
  | "cancelled";

export interface DocAnonymizationFinding {
  page: number;
  /**
   * text = warstwa tekstowa (ramki z popplera; model bez wizji — grafika nieprzeszukana),
   * text+vision = warstwa tekstowa + przebieg graficzny (podpisy odręczne, pieczątki),
   * vision = skan (ramki modelu).
   */
  mode: "text" | "text+vision" | "vision";
  redactions: number;
  categories: Record<string, number>;
  /** Dane o sztywnym formacie złapane deterministycznie, bez modelu (PESEL, NIP…). */
  auto_hits: number;
  /** Ile model wskazał PONAD to — czyli ile wnosi druga warstwa sprawdzenia. */
  ai_extra_hits: number;
}

export interface DocAnonymization {
  id: string;
  original_filename: string;
  page_count: number;
  pages_spec: string;
  pages: number[];
  reasoning_mode: string;
  model_id: string;
  model_label: string;
  /** „Anonimizacja przyspieszona": bez drugiej analizy obrazu i kontroli wizyjnej. */
  fast_mode: boolean;
  /** „Tylko wybrane strony": plik wynikowy zawiera wyłącznie wybrane strony
   *  (wyciąg), a nie cały dokument z zamalowanymi wybranymi stronami. */
  selected_only: boolean;
  status: DocAnonymizationStatus;
  error: string;
  findings: DocAnonymizationFinding[];
  warnings: string[];
  redaction_count: number;
  /** Ile stron z `pages` już się skończyło — postęp NA ŻYWO (`findings` powstają
   *  dopiero na końcu przebiegu, więc nie nadają się na licznik). */
  processed_pages: number;
  source_file: ManagedFileMini | null;
  result_file: ManagedFileMini | null;
  task_id: string | null;
  /** Kto puścił przebieg — pokazujemy tylko w podglądzie „wszystkich" (deweloper). */
  user_name: string;
  is_mine: boolean;
  cost_usd: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface DocTemplateField {
  name: string;
  // manual — pole `#wpisz_…#`: zostaje w dokumencie, uzupełniane w edytorze po generacji
  status: "filled" | "empty" | "missing" | "manual";
  source: string; // gdzie ustawić / skąd liczona
  writable?: boolean; // ręczny wpis można też utrwalić w bazie (write_target mówi gdzie)
  write_target?: string | null;
  write_shared?: boolean; // pole wspólne (klient/projekt) — zapis uzupełni też inne działki
  ai?: boolean; // zmienna liczona przez AI — można ją policzyć „na żywo" w oknie
}

/** Jedna #zmienna# szablonu formatki w oknie generowania: co się na niej wydrukuje
 *  i skąd ta wartość jest. `editable` = da się nadpisać wpisem (pola liczone per
 *  arkusz/działkę — skala, numeracja, dane działki — mają false). */
export interface FormatkaCheckField {
  name: string;
  label: string;
  // auto — liczone osobno na każdym arkuszu / dla każdej działki (bez pola wpisu)
  status: "filled" | "empty" | "missing" | "auto";
  source: string; // gdzie jeszcze można to ustawić
  value: string; // wartość, która wyjdzie na formatce (z uwzględnieniem wpisów)
  editable: boolean;
  overridden: boolean; // wartość pochodzi z wpisu w tym oknie (field_values zestawu)
}

/** Kontrakt `formatka-templates/{id}/check/` — rozkład zmiennych TEGO szablonu dla
 *  TEGO zestawu. Pola okna generowania stawiane są z `fields`, nie z listy wpisanej
 *  na sztywno w UI: szablon, który nie ma #branza#, nie dostaje pola „Branża". */
export interface FormatkaTemplateCheck {
  ok: boolean;
  missing: string[]; // zmiennych nie ma w tym kontekście → blokują generowanie
  empty: string[]; // dostępne, ale bez danych → wyjdą puste
  fields: FormatkaCheckField[];
}

export interface DocTemplateCheck {
  ok: boolean;
  missing: string[]; // zmienne, których w tym kontekście w ogóle nie ma → blokują generowanie
  empty: string[]; // zmienne dostępne, ale bez danych → wyjdą puste (generowanie dozwolone)
  fields: DocTemplateField[]; // pełny rozkład: każda zmienna szablonu + status + źródło
  // szablon używa pól oddziału klienta: needed=true → projekt nie ma oddziału,
  // okno oferuje ustawienie go w miejscu (PATCH project-branch/)
  client_branch_setup?: {
    needed: boolean;
    connection_id: string;
    client_name: string;
    branches: { id: string; name: string }[];
  } | null;
}

// Latest generated document per (context_kind, context_id) — only the newest render is kept.
export interface GeneratedDoc {
  id: string;
  context_kind: string;
  context_id: string;
  template: string | null;
  template_name: string;
  status: "pending" | "success" | "failure";
  error: string;
  file: ManagedFileMini | null;
  /** PDF tej samej wersji (konwersja po generacji / po zapisie z edytora) — do kontroli. */
  pdf_file?: ManagedFileMini | null;
  /** Powód braku PDF-a (konwersja LibreOffice nie powiodła się). */
  pdf_error?: string;
  /** Raport z generacji liczonej po WIELU obiektach (TOM II) — {} dla zwykłych renderów. */
  report?: TomCompletenessReport | Record<string, never>;
  /** Kiedy dokument poprawiono RĘCZNIE w przeglądarce (ISO). Puste = nikt go nie ruszał
   *  od generacji. Niepuste tylko tam, gdzie wersje poprawia się W MIEJSCU (TOM II). */
  edited_at?: string;
  edited_by_name?: string;
  generated_at: string | null;
  generated_by_name: string | null;
}

// ── TOM II — Tytuły prawne do nieruchomości ──────────────────────────────────
// Generacja na poziomie CAŁEGO projektu ZE: część redagowana z szablonu (DOCX) +
// scalony PDF ze skanami wypisów i tytułów prawnych.

/** Czego w tomie brakuje — listy numerów działek, liczone z tych samych źródeł co dokument. */
export interface TomCompletenessReport {
  parcels: number;
  consent_parcels: number;
  no_owners: string[];
  no_kw_analysis: string[];
  no_wypis: string[];
  no_title: string[];
}

export interface TomTitlesOverview {
  /** Niepusty = generowanie zablokowane, z powodem do pokazania wprost. */
  blocked: string;
  template: { id: string; name: string } | null;
  templates: { template_id: string; name: string; operator: string; exact: boolean; shared: boolean }[];
  report: TomCompletenessReport;
  report_lines: string[];
  latest: GeneratedDoc | null;
}

// ── formatki (plan sytuacyjny — DXF paper-space layouts) ─────────────────────
export type FormatkaMode = "parcel" | "parcel_office" | "project" | "custom" | "bundle";
// "wstega" = format pochodny ISO 5457 (wysokość modułu A4/A3, długość n×moduł,
// np. A4×3 = 630×297) — wstęga na plany liniowe, składana w harmonijkę do A4.
export type FormatkaPaper =
  | "a4_portrait"
  | "a4_landscape"
  | "a3_portrait"
  | "a3_landscape"
  | "wstega"
  | "custom";
export type SheetSetStatus = "draft" | "generating" | "success" | "failure";
// Typ formatki — steruje tytułem metryki (EOP/prawo wymaga innego zapisu).
export type FormatkaType = "koncepcja_projektowa" | "plan_sytuacyjny" | "pzt";

// One A4 sheet's map window over the model — CANONICAL in the project's PUWG
// zone METERS (see FormatkaSheetSet.srid). The same numbers reach the DXF
// viewport with no reprojection anywhere; the map editor converts to lat/lng
// only at the Leaflet display boundary (components/formatki/puwg.ts).
export interface SheetFrame {
  order: number;
  center: [number, number]; // [x_m, y_m] in the sheet set's PUWG zone
  rotation_deg: number; // CCW in PUWG — equals the DXF viewport twist (negated)
  scale_denominator: number;
  parcel_id: string | null;
  label: string;
}

export type FormatkaScope = "wpw" | "office";

export const FORMATKA_SCOPE_LABEL: Record<FormatkaScope, string> = {
  wpw: "Działki WPW (prywatne)",
  office: "Działki urzędowe",
};

// Jedna komórka siatki domyślnych szablonów (operator × przeznaczenie). Pusty
// `template` = komórka nieustawiona → dobór historyczny (szablon firmowy).
export interface FormatkaDefaultCell {
  operator: string; // energa / tauron / inny
  operator_display: string;
  scope: FormatkaScope;
  scope_display: string;
  template: string | null;
  template_name: string;
}

export interface FormatkaTemplate {
  id: string;
  name: string;
  description: string;
  is_company: boolean;
  // Etykieta OSD — tylko opis i sortowanie list; „co jest domyślne" niesie siatka.
  operator: string; // energa / tauron / inny
  operator_display: string;
  paper: FormatkaPaper;
  paper_size: number[]; // [w_mm, h_mm] — authoritative dims (A3/wstęga-ready)
  layout_name: string;
  variables: string[];
  viewport_center: number[]; // [x_mm, y_mm] of the map window on paper
  viewport_size: number[]; // [w_mm, h_mm]
  author: { id: string; name: string } | null;
  file: ManagedFileMini | null;
  // Numer najnowszej wgranej wersji DXF (1 = pierwotny upload; podmiana pliku
  // dokłada wersję — historia pod `versions/`).
  current_version: number;
  // Present only on the create/patch response: layers without the FORMATKA_
  // prefix detected by the scan (informational, does not block).
  layer_warnings?: string[];
  created_at: string;
  updated_at: string;
}

// Jedna wgrana rewizja pliku DXF szablonu formatki — jak DocTemplateVersion.
export interface FormatkaTemplateVersion {
  id: string;
  version: number;
  file: ManagedFileMini | null;
  variables: string[];
  paper: FormatkaPaper | "";
  uploaded_by_name: string | null;
  created_at: string;
}

export interface FormatkaSheetPdf {
  id: string;
  version: number;
  kind: "merged" | "overwrite";
  note: string;
  file: ManagedFileMini | null;
  created_by_name: string | null;
  created_at: string;
}

export interface FormatkaSheetSet {
  id: string;
  connection: string;
  route_version: string;
  route_version_label: string | null;
  parcel_label: string | null; // per-działka: which parcel (short number / TERYT)
  name: string; // custom name overriding the auto title (empty = auto)
  mode: FormatkaMode;
  template: string | null;
  template_name: string;
  frames: SheetFrame[];
  source_parcel_count: number; // office (parcel_office): how many parcels it covers
  // Działki zestawu (per działka: ta jedna; urzędowy: wszystkie — jego kafle nie niosą
  // parcel_id). Edytor rozplanowania podpisuje z nich działki i właścicieli.
  source_parcel_ids: string[];
  // Gminy działek zestawu (bez duplikatów) — na jednym terenie bywają DWIE różne
  // gminy, więc mapa dla urzędu musi mówić, dla jakiej gminy powstaje.
  communes: string[];
  field_values: Record<string, string>;
  scale_denominator: number;
  // Dodatkowy pierwszy arkusz z rozkładem pozostałych arkuszy (obrysy + numery).
  include_orientation: boolean;
  // Typ formatki → tytuł metryki. Domyślnie "plan_sytuacyjny".
  formatka_type: FormatkaType;
  // Opisz przy krawędziach numer sąsiedniego (stykającego się) arkusza.
  number_adjacent_sheets: boolean;
  srid: number; // 2176–2179 — picks the proj4 definition on the frontend
  status: SheetSetStatus;
  error: string;
  // Ostrzeżenia ostatniego generowania: opisy ucięte w wybranej skali, mieszane skale
  // w komplecie działki, obiekty poza arkuszami. Generator nic z tego nie naprawia sam
  // (skala arkuszy jest wyborem człowieka) — decyzja należy do użytkownika.
  warnings: string[];
  // Ręczne ukrycie ostrzeżeń („opisy dołożone ręcznie w CAD, PDF wgrany"): kto, kiedy,
  // dlaczego. Lista `warnings` zostaje; ukrycie znika przy nowym generowaniu / zmianie ramek.
  warnings_dismissed_at: string | null;
  warnings_dismissed_by_name: string | null;
  warnings_dismissed_note: string;
  output_file: ManagedFileMini | null;
  // The current merged PDF (latest version) + full version archive.
  merged_pdf: ManagedFileMini | null;
  merged_at: string | null;
  pdf_versions: FormatkaSheetPdf[];
  can_overwrite_pdf: boolean;
  // Author, dyrektor or CEO. The server computes it with the very function that guards
  // DELETE, so the kosz shows exactly on the sets whose deletion will go through.
  can_delete: boolean;
  created_by_name: string | null;
  generated_by_name: string | null;
  generated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FormatkaFieldDefault {
  id: string;
  key: string;
  value: string;
  updated_at: string;
}

export const FORMATKA_MODE_LABEL: Record<FormatkaMode, string> = {
  parcel: "Działka",
  parcel_office: "Działki urzędowe",
  project: "Cały projekt",
  custom: "Własny zakres",
  bundle: "Paczka",
};

// Krótka etykieta typu w UI (chip na kartach, opcje w select).
export const FORMATKA_TYPE_LABEL: Record<FormatkaType, string> = {
  koncepcja_projektowa: "Koncepcja projektowa",
  plan_sytuacyjny: "Plan sytuacyjny",
  pzt: "Projekt zagospodarowania terenu",
};

export const SHEET_SET_STATUS_LABEL: Record<SheetSetStatus, string> = {
  draft: "Szkic",
  generating: "Generowanie…",
  success: "Wygenerowany",
  failure: "Błąd",
};

export const FORMATKA_PAPER_LABEL: Record<FormatkaPaper, string> = {
  a4_portrait: "A4 pionowo",
  a4_landscape: "A4 poziomo",
  a3_portrait: "A3 pionowo",
  a3_landscape: "A3 poziomo",
  wstega: "Wstęga",
  custom: "Niestandardowy",
};

// ── cross-project działka export ("Pobierz listę działek") ─────────────────────────
export interface ParcelExportFacets {
  projects: { id: string; name: string }[];
  coordinators: { id: string; name: string }[];
  branches: { id: string; name: string }[];
  clients: { id: string; name: string }[];
  voivodeships: string[];
  counties: string[];
  communes: string[];
  land_uses: string[];
  soil_classes: string[];
  statuses: { value: string; label: string }[];
  access_modes: { value: string; label: string }[];
  location_categories: { value: string; label: string }[];
}

export interface ParcelExportFilters {
  project_ids?: string[];
  coordinator_ids?: string[];
  branch_ids?: string[];
  client_ids?: string[];
  search?: string;
  deadline_status?: string;
  progress?: string;
  agreements?: string;
  statuses?: string[];
  access_modes?: string[];
  location_categories?: string[];
  voivodeships?: string[];
  counties?: string[];
  communes?: string[];
  land_uses?: string[];
  soil_classes?: string[];
  parcel_search?: string;
  owner_search?: string;
  in_current_route?: string;
  /** „Wypis MPZP": yes = działka w nieanulowanym zamówieniu wypisu i wyrysu, no = w żadnym. */
  mpzp_ordered?: string;
  /** „Wypis": yes = działka z podpiętym wypisem z rejestru gruntów, no = bez. */
  wypis_attached?: string;
  /** „Koncepcja": yes = koncepcja wysłana lub uzgodniona (kamień koncepcja SUBMITTED/DONE
   *  lub concept_sent_at w projekcie technicznym), no = brak. */
  concept?: string;
  /** „Koncepcja uzgodniona z EOP": yes = kamień koncepcja / uzgodnienie_koncepcji_z_EOP
   *  DONE lub concept_accepted_at w projekcie technicznym, no = nieuzgodniona. */
  concept_agreed_eop?: string;
  /** Jawna lista działek — eksport z zakładki działek projektu wysyła id wierszy,
   *  które zostały po filtrach klienckich, więc plik = dokładnie widoczna tabela. */
  parcel_ids?: string[];
}

// ── globalna strona "Działki" (parcels/global-list/) ───────────────────────────
// Ten sam kształt filtrów co eksport CSV — strona i plik zawsze się zgadzają.
export interface GlobalParcelRow {
  id: string;
  teryt: string;
  short_number: string;
  obreb: string;
  connection_id: string;
  project_id: string;
  project_name: string;
  branch_name: string;
  coordinators: string;
  voivodeship: string;
  county: string;
  commune: string;
  street_and_number: string;
  area_m2: number | null;
  land_use: string;
  soil_class: string;
  geoportal_land_use: string;
  kw_number: string;
  ownership_code: string;
  mpzp_designation: string;
  status: ParcelStatus;
  status_display: string;
  access_mode: ParcelAccessMode;
  access_mode_display: string;
  location_category: string;
  location_category_display: string;
  in_current_route: boolean;
  is_manual: boolean;
  intersection_length_m: number | null;
  owners_total: number;
  owners_agreed: number;
  owner_names: string[];
}

export interface GlobalParcelListRequest {
  filters: ParcelExportFilters;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface GlobalParcelList {
  count: number;
  page: number;
  page_size: number;
  pages: number;
  results: GlobalParcelRow[];
}

// ── globalna strona "Zadania KPI" (kpi-action-items/global-list/) ───────────────
// Zadania KPI ze WSZYSTKICH projektów ZE w jednej tabeli. Backend zawęża zakres:
// CEO / dyr. techniczny → całość portfela, reszta → tylko własne projekty
// (koordynator przedsięwzięcia lub prowadzący ZE).
export interface GlobalKpiFilters {
  project_ids?: string[];
  coordinator_ids?: string[];
  designer_ids?: string[];
  client_ids?: string[];
  branch_ids?: string[];
  assignee_ids?: string[];
  statuses?: string[];
  due?: string;
  search?: string;
  // Domyślnie strona pokazuje tylko ustalenia ze spotkań; to włącza także zapiski własne.
  include_self?: boolean;
}

export interface GlobalKpiFacets {
  projects: { id: string; name: string }[];
  coordinators: { id: string; name: string }[];
  designers: { id: string; name: string }[];
  clients: { id: string; name: string }[];
  branches: { id: string; name: string }[];
  /** Osoby odpowiedzialne; id "none" = nieprzypisane. */
  assignees: { id: string; name: string }[];
  statuses: { value: string; label: string }[];
  due_options: { value: string; label: string }[];
  scope: "all" | "self";
}

export interface GlobalKpiRow {
  id: string;
  description: string;
  // Ścieżka kubełków nad zadaniem („Sprawy bieżące → Korekta PB") — w płaskiej tabeli
  // sama treść podzadania bywa nieczytelna.
  parent_id: string | null;
  parent_path: string;
  status: KpiActionStatus;
  status_display: string;
  priority: KpiActionPriority;
  origin: KpiActionOrigin;
  due_date: string | null;
  week_start: string | null;
  days_left: number | null;
  overdue: boolean;
  assignee_id: string | null;
  assignee_name: string;
  connection_id: string;
  project_id: string;
  project_name: string;
  contract_number: string;
  client_name: string;
  branch_name: string;
  coordinators: string;
  designer_name: string;
  review_id: string;
  review_sequence_no: number;
  review_scheduled_for: string;
  done_at: string | null;
  done_by_name: string;
  created_at: string;
  created_by_name: string;
  /** Autorem jest CEO / dyrektor techniczny — patrz isDirectorateKpiItem(). */
  by_directorate: boolean;
}

export interface GlobalKpiSummary {
  total: number;
  overdue: number;
  week: number;
  no_date: number;
  open: number;
  done: number;
}

export interface GlobalKpiListRequest {
  filters: GlobalKpiFilters;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface GlobalKpiList {
  count: number;
  page: number;
  page_size: number;
  pages: number;
  results: GlobalKpiRow[];
  summary: GlobalKpiSummary;
  scope: "all" | "self";
}

export interface ParcelExportPreviewRow {
  id: string;
  teryt: string;
  short_number: string;
  project_name: string;
  county: string;
  commune: string;
  area_m2: number | null;
  status_display: string;
}

export interface ParcelExportPreview {
  count: number;
  results: ParcelExportPreviewRow[];
  truncated: boolean;
  limit: number;
}

export interface ParcelExportResult {
  file_id: string;
  file_url: string | null;
  filename: string;
  count: number;
}

// Podwykonawca, który pracował na przedsięwzięciu (zebrany z zamówień projektu).
export interface ProjectContractorRow {
  id: string;
  name: string;
  short_name: string;
  is_active: boolean;
  orders_count: number;
  open_orders_count: number;
  // Anulowane zamówienia wchodzą do `orders_count` — kolumna „Anulowane" mówi, ile
  // z nich nie skończyło się pracą. Usunięte (miękko) nie wchodzą tu w ogóle.
  cancelled_orders_count: number;
  order_types: string[];
  last_order_at: string | null;
  // Ocena widoczna wyłącznie dla GK — endpoint jest niedostępny dla podwykonawców.
  rating: number | null;
  ratings_count: number;
}

// Read-only „dane zapotrzebowania" jednego zamówienia podwykonawcy — rozwijany wiersz
// w zakładce „Podwykonawcy". `files` = załączniki zapotrzebowania.
export interface ContractorOrderRow {
  id: string;
  number: string;
  order_type_display: string;
  status: string;
  status_display: string;
  approved_at: string | null;
  deadline: string | null;
  priority_display: string;
  reporter_name: string | null;
  description: string;
  requisition_id: string | null;
  files: import("@/features/orders/types").ManagedFileBrief[];
}

// ── Raporty okresowe do ZE ─────────────────────────────────────────────────────
// Mirror apps.dlc_01_power_project.models.{ReportingMode,ZEReport} + reports.py.
export type ReportingMode = "auto" | "required" | "off";
export type ZEReportStatus = "draft" | "sent";
export type ReportSlotState = "done" | "overdue" | "due_soon" | "planned";

export const REPORTING_MODE_LABEL: Record<ReportingMode, string> = {
  auto: "Automatycznie (z napięcia trasy)",
  required: "Wymagane",
  off: "Niewymagane",
};

export const ZE_REPORT_STATUS_LABEL: Record<ZEReportStatus, string> = {
  draft: "Roboczy",
  sent: "Wysłany do ZE",
};

/** Stan szkicu pisma pisanego przez ARON — niezależny od statusu samego raportu. */
export type ZEReportDraftStatus = "none" | "pending" | "success" | "failure";

export interface ZEReportDraftSection {
  month_key: string;
  heading: string;
  text: string;
}

export interface ZEReportDraft {
  /** false = treść to zestawienie faktów (AI było niedostępne), nie tekst pisma. */
  ai: boolean;
  sections: ZEReportDraftSection[];
  closing: string;
  period: { from?: string; to?: string; from_display?: string; to_display?: string };
  facts_total: number;
  /** Wiersze Tabeli 1 formatki operatora (puste, gdy generowano tylko pismo). */
  form_rows: ZEFormRow[];
  form_planned: ZEPlannedRow[];
  form_metric: ZEFormMetric | Record<string, never>;
  /** Wykaz plików do dopięcia do maila (ten sam, który jest na końcu .docx). */
  form_attachments?: { document: string; file: string; date_display: string; file_id?: string }[];
}

/** Metryka formatki operatora — rubryki, które inspektor sprawdza po kolei. */
export interface ZEFormMetric {
  report_no: number;
  subject: string;
  task_name: string;
  contract_number: string;
  obi_number: string;
  cru_number: string;
  zn_number: string;
  our_contact: { name: string; phone: string; email: string };
  their_contact: { name: string; phone: string; email: string };
  annex_count: number;
  deadline: string;
  /** Rubryka „przewidywany termin zakończenia" + skąd data pochodzi:
   *  declared = wpisał koordynator, annex = ze złożonego wniosku o aneks,
   *  plan = oszacowane z harmonogramu, contract = termin umowny. */
  expected_completion: string;
  expected_completion_source: "declared" | "annex" | "plan" | "contract" | "";
}

/** Rubryki metryki, których nie da się wyliczyć — koordynator wpisuje je raz
 *  w „Kolumnach dodatkowych" projektu pod dokładnie tymi etykietami. */
export const ZE_FORM_METRIC_GAPS: { key: keyof ZEFormMetric; label: string; where: string }[] = [
  // „Nr CRU" tu nie ma: wzór raportu (Załącznik nr 4 do OWU) takiej rubryki nie zawiera —
  // przyszła kiedyś z wniosku o aneks i wychodziła pusta na każdym raporcie.
  { key: "obi_number", label: "Nr OBI/OBM", where: "zakładka Metadane — pole „Nr ST”" },
  { key: "contract_number", label: "Nr umowy", where: "zakładka Metadane" },
  // Pustej rubryki nie da się zapełnić automatem: szacunek systemu wykraczający poza
  // termin umowny byłby deklaracją opóźnienia, której nikt świadomie nie podjął.
  {
    key: "expected_completion",
    label: "Przewidywany termin zakończenia",
    where: "Ustawienia raportowania — to Twoja decyzja, nie szacunek systemu",
  },
];

/** Postać dokumentu: pismo do maila i/lub formatka operatora. Mirror ZEDocFormat. */
export type ZEDocFormat = "letter" | "eop_form";

export const ZE_DOC_FORMAT_LABEL: Record<ZEDocFormat, string> = {
  letter: "Pismo (e-mail, TAURON)",
  eop_form: "Raport o postępie prac — formatka operatora (Zał. nr 4 do OWU, ENERGA)",
};

export interface ZEFormRow {
  date_display: string;
  action: string;
  note: string;
  /** Nazwa DOKUMENTU do kolumny załączników („Decyzja znak … z dnia …") albo
   *  „załączono do raportu nr …", gdy skan poszedł już wcześniej. */
  attachment: string;
  /** Pliki z systemu stojące za tym dokumentem — wykaz do dopięcia do maila. */
  attachment_files?: string[];
}

export interface ZEPlannedRow {
  date: string;
  date_display: string;
  text: string;
  /** "milestone" = termin z kamienia, "estimate" = data policzona przez system. */
  source: "milestone" | "estimate";
}

export interface ZEReport {
  id: string;
  connection: string;
  title: string;
  /** Termin z harmonogramu, który ten raport domyka; null = raport doraźny. */
  period_due: string | null;
  status: ZEReportStatus;
  status_display: string;
  sent_at: string | null;
  sent_by_name: string | null;
  notes: string;
  files: ManagedFileMini[];
  draft_status: ZEReportDraftStatus;
  draft_status_display: string;
  draft_error: string;
  draft_generated_at: string | null;
  draft_file: ManagedFileMini | null;
  /** Formatka operatora — osobny plik, żeby generacja pisma jej nie kasowała. */
  draft_form_file: ManagedFileMini | null;
  draft_formats: ZEDocFormat[];
  draft: ZEReportDraft | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

// Mirror apps.dlc_01_power_project.models.ZEAnnexRequest — wniosek o aneks do umowy.
export type ZEAnnexScope = "term" | "amount" | "term_amount" | "other";
export type ZEAnnexStatus = "draft" | "sent" | "signed" | "rejected";

export const ZE_ANNEX_SCOPE_LABEL: Record<ZEAnnexScope, string> = {
  term: "Termin",
  amount: "Kwota",
  term_amount: "Termin i kwota",
  other: "Inne",
};

/** Pozycja listy „Wymagane załączniki" ze wzoru wniosku. `manual` = system tego
 *  nie sprawdza i wprost to mówi, zamiast pokazywać haczyk na kredyt. */
export interface ZEAnnexChecklistRow {
  key: string;
  label: string;
  state: "ok" | "missing" | "manual";
  detail: string;
}

export interface ZEAnnexDraft {
  ai: boolean;
  done: string;
  remaining: string;
  cause: string;
  period: { from_display?: string; to_display?: string };
  facts_total: number;
  planned_rows: ZEPlannedRow[];
  pending: {
    waiting_for: { text: string; since_display: string; days: number | null }[];
    owner_problems: { parcel: string; status: string; note: string }[];
    owners_total: number;
    owners_secured: number;
    open_milestones: { text: string; state: string }[];
  } | null;
}

export interface ZEAnnexRequest {
  id: string;
  connection: string;
  number: number;
  issued_on: string | null;
  scope: ZEAnnexScope;
  scope_display: string;
  current_deadline: string | null;
  proposed_deadline: string | null;
  current_value: string | null;
  proposed_value: string | null;
  justification: string;
  status: ZEAnnexStatus;
  status_display: string;
  sent_at: string | null;
  sent_by_name: string | null;
  resolved_on: string | null;
  annex_number: string;
  signed_deadline: string | null;
  notes: string;
  files: ManagedFileMini[];
  draft_status: ZEReportDraftStatus;
  draft_status_display: string;
  draft_error: string;
  draft_generated_at: string | null;
  draft_file: ManagedFileMini | null;
  draft: ZEAnnexDraft | null;
  checklist: ZEAnnexChecklistRow[];
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReportSlot {
  sequence_no: number;
  due_date: string;
  days_left: number;
  state: ReportSlotState;
  report_ids: string[];
  sent_at: string | null;
}

export interface ReportSchedule {
  /** "SN" | "nN" | null (brak narysowanej trasy). */
  voltage: string | null;
  mode: ReportingMode;
  required: boolean;
  /** Zdanie po polsku: dlaczego raporty są (nie)wymagane. */
  reason: string;
  interval_months: number;
  start_date: string | null;
  start_source: "override" | "contract" | null;
  closed: boolean;
  /** Czy przypomnienia 10/3/1 dzień faktycznie działają dla tego projektu. */
  active: boolean;
  slots: ReportSlot[];
  ad_hoc_report_ids: string[];
  overdue_count: number;
  next_slot: ReportSlot | null;
}

// ── MPZP: zamawianie wypisu i wyrysu z planu miejscowego (urząd gminy/miasta) ──
export type MpzpOrderStatus = MzOrderStatus;

export const MPZP_ORDER_STATUS_LABELS: Record<MpzpOrderStatus, string> = {
  new: "Nowe",
  fee_requested: "Złożono prośbę o opłatę",
  fee_paid: "Opłata opłacona — do wysyłki",
  sent: "Wysłano do gminy",
  received: "Wypis i wyrys otrzymany",
  completed: "Zakończone",
  cancelled: "Anulowane",
};

export const MPZP_ORDER_STATUS_COLORS = WYPIS_ORDER_STATUS_COLORS;

/**
 * Stan pokrycia planem miejscowym. CZTERY stany, nie dwa — „unknown" (gmina nie wgrała
 * planów do rejestru krajowego) i „unchecked" (jeszcze nie pytaliśmy) znaczą co innego
 * i żaden z nich nie jest dowodem, że planu nie ma.
 */
export type MpzpCoverageStatus = "covered" | "none" | "unknown" | "unchecked";

/** Powód, dla którego działka MUSI mieć wypis i wyrys — zdanie przychodzi z backendu. */
export interface MpzpReason {
  key: "wody" | "odrolnienie" | "gdos" | (string & {});
  label: string;
  detail: string;
}

export interface MpzpCandidateParcel {
  id: string;
  teryt: string;
  short_number: string;
  obreb: string;
  karta_mapy: string;
  commune: string;
  county: string;
  voivodeship: string;
  locality: string;
  street_and_number: string;
  connection_id: string;
  project_id: string;
  project_name: string;
  operator: string;
  area_m2: number | null;
  mpzp_status: MpzpCoverageStatus;
  mpzp_status_label: string;
  mpzp_covered_pct: number | null;
  mpzp_checked_at: string | null;
  protected_area_checked_at: string | null;
  street_checked_at: string | null;
  reasons: MpzpReason[];
  /** Wynik reguł preselektu. Wody Polskie są zaznaczone także bez pokrycia planem. */
  preselected: boolean;
  missing_admin: boolean;
  missing_address: boolean;
}

export interface MpzpCandidateGroup {
  commune_teryt: string;
  commune: string;
  county: string;
  voivodeship: string;
  parcels: MpzpCandidateParcel[];
  parcel_count: number;
  preselected_count: number;
  missing_admin_count: number;
  missing_address_count: number;
  unchecked_mpzp_count: number;
  unchecked_gdos_count: number;
}

/** Opis metodologii pokazywany w panelu „Dlaczego te działki są zaznaczone?". */
export interface MpzpSelectionRule {
  key: string;
  title: string;
  detail: string;
  source: string;
}

export interface MpzpCoverageLegendItem {
  key: MpzpCoverageStatus;
  label: string;
  detail: string;
}

/**
 * Podsumowanie braków PER PROJEKT — przeliczenia idą per projekt (jedno zapytanie do
 * usługi mapowej obsługuje wszystkie jego działki), więc przycisk „Przelicz ponownie"
 * musi być per projekt, a nie per gmina.
 */
export interface MpzpProjectSummary {
  connection_id: string;
  project_id: string;
  project_name: string;
  parcel_count: number;
  missing_admin: number;
  missing_address: number;
  unchecked_mpzp: number;
  unchecked_gdos: number;
  /** NAJSTARSZE sprawdzenie w projekcie — mówi, jak stare są najstarsze dane. */
  last_checked: {
    address: string | null;
    mpzp: string | null;
    gdos: string | null;
  };
}

export interface MpzpCandidatesResponse {
  groups: MpzpCandidateGroup[];
  projects: MpzpProjectSummary[];
  selection_rules: MpzpSelectionRule[];
  coverage_legend: MpzpCoverageLegendItem[];
}

export interface MpzpOrderRow {
  id: string;
  number: string;
  status: MpzpOrderStatus;
  status_display: string;
  commune: string;
  commune_teryt: string;
  county: string;
  voivodeship: string;
  applicant: WypisApplicant;
  office: string | null;
  office_name: string;
  created_by: string | null;
  created_by_name: string;
  /** Następca nieaktywnego autora (osoba do kontaktów) — ma jego uprawnienia; `null`, gdy
   *  autor nadal pracuje. Liczone na backendzie (dlc_01 takeover.py). */
  taken_over_by: string | null;
  taken_over_by_name: string;
  /** Czy zalogowany może prowadzić zamówienie — lustro bramki `_can_manage` backendu. */
  can_manage: boolean;
  want_wypis: boolean;
  want_wyrys: boolean;
  scope_label: string;
  /** Wypis pozyskany ręcznie poza ELGW5: od razu „otrzymany", bez wniosku, opłat
   * i wysyłki — tylko pliki, zakończenie i usunięcie. */
  obtained_manually: boolean;
  parcel_count: number;
  /** Zamówienie idzie po gminie, więc jego działki bywają z kilku projektów. */
  project_names: string[];
  has_documents: boolean;
  generated_at: string | null;
  fee_id: string | null;
  fee_status: string | null;
  fee_amount: string | null;
  fee_rejection_comment: string;
  sent_at: string | null;
  received_at: string | null;
  created_at: string;
}

/** Zamówienie MPZP widziane z poziomu działki projektu. `match_type`: `parcel` —
 * zamówienie trzyma dokładnie ten wiersz działki; `number` — tę samą działkę
 * fizyczną (numer + obręb + gmina), ale jako wiersz z innego projektu ZE. */
export interface MpzpOrderForParcel extends MpzpOrderRow {
  match_type: "parcel" | "number";
}

export interface MpzpResultFile {
  id: string;
  file: ManagedFileMini;
  uploaded_by_name: string;
  created_at: string;
}

/** Działka zamówienia dokładnie w kształcie, w jakim wejdzie do tabeli wniosku. */
export interface MpzpOrderParcel {
  id: string;
  teryt: string;
  short_number: string;
  obreb: string;
  karta_mapy: string;
  commune: string;
  locality: string;
  street_and_number: string;
  mpzp_status: MpzpCoverageStatus;
  mpzp_status_label: string;
}

export interface MpzpOrder extends MpzpOrderRow {
  contact_person: string | null;
  contact_person_name: string;
  /** Kontakt zamawiającego — na żywo z profilu, więc jego zmiana zmienia i te pola. */
  created_by_phone: string | null;
  created_by_email: string | null;
  /** Kontakt następcy nieaktywnego zamawiającego (`null` bez przejęcia). */
  taken_over_by_phone: string | null;
  taken_over_by_email: string | null;
  notes: string;
  template: string | null;
  template_name: string;
  form_file: ManagedFileMini | null;
  /** Ten sam wniosek w PDF — z tego samego renderu co ODT. */
  form_file_pdf: ManagedFileMini | null;
  attachment_xlsx: ManagedFileMini | null;
  /** Pismo gminy o wysokości opłaty (odpowiednik DOO przy wypisach EGiB). */
  doo_file: ManagedFileMini | null;
  sent_by_name: string;
  received_by_name: string;
  result_files: MpzpResultFile[];
  suggested_account_number: string;
  poa_fees: WypisPoaFee[];
  fee_payment_files: ManagedFileMini[];
  poa_fee_not_required: boolean;
  poa_fee_not_required_reason: string;
  stamp_duty_office: StampDutyOfficeInfo & {
    /** Rozpoznany urząd wypadł tym samym, co adresat wniosku (gmina). */
    same_as_recipient: boolean;
    mixed_clients: boolean;
  };
  parcels_detail: MpzpOrderParcel[];
  data_gaps: { missing_obreb: number; missing_address: number };
  /** Projekty ZE działek zamówienia — przeliczenia danych idą per projekt. */
  connection_ids: string[];
  /** Historia zamówienia — od najnowszego wpisu (zmiany statusu, opłaty, pliki). */
  history: OrderHistoryEvent[];
  /** Status, do którego cofnie akcja „Cofnij…" ("" = nie ma czego cofać). */
  revert_to: MpzpOrderStatus | "";
  /** Etykieta przycisku cofania wyliczona przez backend (np. „Cofnij wysłanie"). */
  revert_label: string;
  created_fee_id?: string;
}

export interface MpzpOrderCreatePayload {
  parcel_ids: string[];
  commune?: string;
  commune_teryt?: string;
  county?: string;
  voivodeship?: string;
  office?: string;
  applicant?: WypisApplicant;
  contact_person?: string;
  want_wypis?: boolean;
  want_wyrys?: boolean;
  notes?: string;
}

// ── preanaliza dokumentów z urzędów („czego to dotyczy?") ─────────────────────
export type OfficeDocScanStatus = "pending" | "running" | "done" | "failed";

/** Jedno zamówienie, o które MOŻE chodzić dokument. `score` 0–100 — 100 = numer
 *  wniosku wprost w dokumencie; niżej: dopasowanie po geometrii albo po treści. */
export interface OfficeDocProposal {
  kind: "wypis" | "mz" | "mpzp";
  kind_label: string;
  order_id: string;
  number: string;
  /** Powiat / gmina / projekt ZE — po czym człowiek pozna zamówienie bez wchodzenia. */
  label: string;
  status: string;
  status_label: string;
  /** Link do strony zamówienia w ELG5. */
  href: string;
  score: number;
  reasons: string[];
}

/** Co udało się z dokumentu odczytać — pokazywane obok propozycji jako uzasadnienie. */
export interface OfficeDocFacts {
  filename?: string;
  doc_kind?: string;
  numbers?: string[];
  our_reference?: string;
  office_name?: string;
  case_sign?: string;
  voivodeship?: string;
  county?: string;
  commune?: string;
  parcels?: { teryt: string; number: string; obreb: string }[];
  summary?: string;
  /** Rozpoznany układ współrzędnych pliku DXF (2176–2179) i kolejność osi. */
  srid?: number;
  axes_swapped?: boolean;
  archive?: { filename: string; doc_kind: string; matches: number }[];
}

export interface OfficeDocScan {
  id: string;
  /** Paczka „to przyszło jednym mailem" (null = dokument sam dla siebie). */
  batch: string | null;
  file: ManagedFileMini;
  filename: string;
  status: OfficeDocScanStatus;
  doc_kind: string;
  doc_kind_label: string;
  facts: OfficeDocFacts;
  proposals: OfficeDocProposal[];
  error: string;
  cost_usd: string | null;
  analyzed_at: string | null;
  uploaded_by: string | null;
  uploaded_by_name: string;
  created_at: string;
}

/** Paczka dokumentów jednej sprawy — wspólny wynik ponad rozpoznaniami plików. */
export interface OfficeDocBatch {
  id: string;
  label: string;
  facts: OfficeDocFacts & { files?: string[] };
  proposals: OfficeDocProposal[];
  analyzed_at: string | null;
  /** running = któryś plik jeszcze się analizuje; wspólny wynik liczy się z kompletu. */
  status: "running" | "done" | "failed";
  created_by: string | null;
  created_by_name: string;
  created_at: string;
  scans: OfficeDocScan[];
}

// ── „Analiza zmian" (change-analysis/) ──────────────────────────────────────────
// Rozliczenie postępów jednej osoby w jej projektach ZE względem wybranej chwili.
// Widok dyrekcji technicznej / CEO — pozostałym backend zwraca 403.
export interface ChangeAnalysisPerson {
  id: string;
  name: string;
  /** Ile żywych projektów ZE ma na głowie (prowadzi lub koordynuje — bez podwójnego
   *  liczenia projektu, w którym pełni obie role). */
  projects: number;
}

export interface ChangeAnalysisRequest {
  person_id: string;
  since: string; // YYYY-MM-DD
  since_time?: string; // HH:MM
}

export interface ChangeAnalysisVerdict {
  directives: {
    done: number;
    overdue_open: number;
    dodged: number;
    /** Wymagalne w oknie: wykonane + po terminie + z przesuwanym terminem. */
    settled: number;
    pct: number | null;
  };
  projects_active: number;
  projects_total: number;
  proofs: number;
  attention: number;
}

export type ChangeAnalysisProof =
  | { type: "directive_done"; description: string; project: string; done_at: string; duration_days: number; late_days: number | null }
  | { type: "milestone_forward"; label: string; project: string; at: string; from: string; to: string; from_display: string; to_display: string }
  | { type: "own_task_done"; description: string; project: string; done_at: string; duration_days: number; late_days: number | null };

export type ChangeAnalysisAttention =
  | { type: "directive_overdue"; description: string; project: string; due_date: string; overdue_days: number; due_moves: number }
  | { type: "directive_dodged"; description: string; project: string; due_date: string; due_moves: number }
  | { type: "milestone_stalled"; label: string; project: string; stalled_days: number; overdue_days: number | null }
  | { type: "awaiting_nudge"; label: string; project: string; waiting_label: string; waiting_days: number; sla_days: number }
  | { type: "milestone_backward"; label: string; project: string; at: string; note: string }
  | { type: "project_silent"; project: string; contract_number: string; added_only_count: number };

export type ChangeAnalysisProjectEvent =
  | { at: string; kind: "milestone"; label: string; from: string; to: string; from_display: string; to_display: string; forward: boolean; by: string; note: string }
  | { at: string; kind: "task_done"; description: string; by: string; late_days: number | null; by_directorate: boolean }
  | { at: string; kind: "task_added"; description: string; by: string; by_directorate: boolean };

export interface ChangeAnalysisProject {
  connection_id: string;
  project_id: string;
  name: string;
  contract_number: string;
  branch: string;
  /** false = żaden kamień nie drgnął i żadne zadanie nie zostało wykonane w oknie. */
  active: boolean;
  added_only_count: number;
  events: ChangeAnalysisProjectEvent[];
  /** Kroki nieruszone z domkniętymi poprzednikami — „do wzięcia", piłka u nas.
   *  since/days = null, gdy chwili odblokowania nie da się w ogóle ustalić;
   *  since_exact = false, gdy to tylko dolne ograniczenie (część poprzedników
   *  domknięto bez śladu daty). */
  available: {
    key: string;
    label: string;
    since: string | null;
    days: number | null;
    since_exact: boolean;
  }[];
  /** Otwarte ustalenia dyrekcji tego projektu (własne zapiski pominięte). */
  open_directives: {
    description: string;
    created_at: string;
    due_date: string | null;
    overdue_days: number | null;
    due_moves: number;
  }[];
}

export interface ChangeAnalysisTransition {
  at: string;
  from: string;
  from_display: string;
  to: string;
  to_display: string;
  by: string;
  note: string;
  forward: boolean;
  /** Przejście „porządkowe" (na Dotyczy / Nie dotyczy) — poza osią, dowodami i wykresem. */
  tidy: boolean;
}

export interface ChangeAnalysisMilestone {
  connection_id: string;
  key: string;
  label: string;
  state_before: string | null;
  state_before_display: string | null;
  state_now: string;
  state_now_display: string;
  transitions: ChangeAnalysisTransition[];
  in_state_since: string;
  in_state_days: number;
  waiting_label: string | null;
  waiting_days: number | null;
  sla_days: number | null;
  due_date: string | null;
  overdue_days: number | null;
  stalled_days: number | null;
}

export interface ChangeAnalysisTask {
  id: string;
  connection_id: string;
  description: string;
  created_at: string;
  created_by_name: string;
  by_directorate: boolean;
  origin: "kpi" | "self";
  due_date: string | null;
  status: string;
  status_display: string;
  done_at: string | null;
  done_by_name: string;
  duration_days: number | null;
  late_days: number | null;
  overdue_days: number | null;
  due_moves: number;
}

export interface ChangeAnalysisWeek {
  monday: string; // YYYY-MM-DD
  transitions: number;
  tasks_done: number;
}

export interface ChangeAnalysisResult {
  person: ChangeAnalysisPerson;
  window: { since: string; until: string };
  verdict: ChangeAnalysisVerdict;
  proofs: ChangeAnalysisProof[];
  attention: ChangeAnalysisAttention[];
  projects: ChangeAnalysisProject[];
  milestones: ChangeAnalysisMilestone[];
  tasks: ChangeAnalysisTask[];
  weeks: ChangeAnalysisWeek[];
  meta: { excluded_shelved_or_closed: number };
}

// --- „Zdjęcia z terenu": geotagged result-file photos of the project's orders ---
// Mirrors apps/dlc_01_power_project/field_photos_views.py.
export interface FieldPhotoNearest {
  kind: "parcel" | "object";
  id: string;
  label: string;
  distance_m: number;
}

export interface FieldPhoto {
  id: string;
  order_id: string;
  order_number: string;
  line_item_id: string;
  line_item_name: string;
  original_name: string;
  file_url: string | null;
  status: "pending" | "accepted" | "rejected";
  photo_check: "" | "photo" | "suspect" | "screenshot";
  taken_at: string | null;
  camera: string;
  uploaded_by_name: string;
  geometry: { type: "Point"; coordinates: [number, number] };
  // Nearest ordered parcel/object (or project parcel when the order has no scope).
  nearest: FieldPhotoNearest | null;
  scoped: boolean;
}

export interface FieldPhotosState {
  photos: FieldPhoto[];
  orders: { id: string; order_number: string; contractor_name: string; has_scope: boolean }[];
}

// ── Kalendarz ponagleń (telefony kontrolne) ────────────────────────────────────

export type ControlCallSubjectKind =
  | "application"
  | "wpw"
  | "wypis"
  | "mpzp"
  | "mz"
  | "mdcp"
  /** Sprawa z Korespondencji dodana do ponagleń (termin = jej Termin KPA). */
  | "proceeding"
  /** Ponaglenie własne, dodane ręcznie dla siebie — bez sprawy i bez adresu. */
  | "manual";

export type ControlCallStatus = "pending" | "done" | "obsolete";

/** Jedno ponaglenie na kafelku dnia. `rolled_over` = nie odklikane w swoim dniu
 *  i przeniesione; `first_due_date` mówi, kiedy wypadło pierwszy raz. */
export interface ControlCallReminder {
  id: string;
  subject_kind: ControlCallSubjectKind;
  subject_kind_display: string;
  subject_id: string;
  title: string;
  url: string;
  due_date: string;
  first_due_date: string;
  rolled_over: boolean;
  sequence_no: number;
  reference_date: string;
  days_waiting: number;
  status: ControlCallStatus;
  status_display: string;
  done_at: string | null;
  done_by_name: string;
  note: string;
  assignee_name: string;
}

/** Co danego dnia wyszło („sent") albo wróciło („done") w rejestrach DLC_01. */
export interface ControlCallEvent {
  day: string;
  subject_kind: ControlCallSubjectKind;
  subject_kind_display: string;
  event: "sent" | "done";
  title: string;
  url: string;
  count: number;
}

export interface ControlCallCalendar {
  reminders: ControlCallReminder[];
  events: ControlCallEvent[];
}

export interface ControlCallCalendarMeta {
  from: string;
  to: string;
  scope: "mine" | "all" | "user";
  /** Wybrany pracownik (podgląd dyrekcji) albo null, gdy zestawienie jest własne. */
  user: { id: string; name: string } | null;
  /** Czy pytający może w ogóle wybrać inną osobę (dyrektor / CEO). */
  can_pick_user: boolean;
  show: ("reminders" | "events")[];
  kinds: ControlCallSubjectKind[];
  /** Ponaglenia na dziś (z przeniesionymi) — ta sama liczba, co plakietka w menu. */
  pending_due: number;
  pending_total: number;
  /** Stały okres rejestru w dniach — sprawy (Termin KPA) i własne go nie mają. */
  due_days: Partial<Record<ControlCallSubjectKind, number>>;
}
